import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  CrossTabSyncMessage,
  CrossTabSyncService,
  CrossTabSyncStreamKey,
} from './cross-tab-sync.service';
import { RuntimeConfigService } from '../common/runtime-config.service';
import { ConversionValidationCacheService } from './conversion-validation-cache.service';
import { ParityCheckService } from './parity-check.service';
import { PureValidatorRegistry } from './pure-validator-registry.service';
import {
  buildSheetValidationContext,
  pureStatusForAllowListedSheet,
  PURE_VERDICT_SHEETS,
} from './sheet-validation-context.util';
import { WARNING_CONTROLS_BY_STATE_KEY } from './warning-schema-registry';
import { diffChangedSheets } from '../../utils/state-slice-diff.util';
import {
  buildWarningBag,
  sheetHasTriggeredWarning,
} from './validators/warning-eval.helper';
import { CROSS_SHEET_VALIDATION_DEBOUNCE_MS } from '../../utils/debounce-times.util';
import { ConversionPhaseEnum } from '../../../types/commons';
import { FilingFormPersistenceService } from './filing-form-persistence.service';
// TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
import { recordLivenessDiagnostic } from './liveness-probe-diagnostics';
import { trimLeadingZeros } from '../../utils/input-filter';
import { INPUT_TRANSFORMS_BY_QNAME, TRIM_LEADING_ZERO_QNAMES } from '../../utils/schema-normalizers.util';
import { applyInputTransforms } from '../../utils/input-transform.util';
import { SHEET_LABELS_BY_STATE_KEY } from '../../models/sheet-types';

/**
 * DIAGNOSTIC (TEMPORARY). How long a `waitForMbrsHydration()` await may stay
 * pending before it is reported as a stall.
 *
 * A full MBRS hydration is one HTTP fetch plus per-sheet populate writes; it
 * completes in low single-digit seconds even on a cold, slow connection. 30s is
 * therefore comfortably past "slow" and firmly into "will never resolve", so the
 * warning cannot fire for a merely sluggish load and cry wolf.
 *
 * Deliberately NOT in `debounce-times.util.ts` — that file is for tuned RxJS
 * debounce timings that shape behaviour. This value shapes nothing; it only
 * decides when to print a warning, and it is deleted along with the tripwire.
 *
 * @see FilingFormStateService.armMbrsHydrationStallTripwire
 */
const MBRS_HYDRATION_STALL_WARN_MS = 30_000;

export interface FilingFormState {
  filingPrerequisites: Record<string, any>;
  filingInfo: Record<string, any>;
  scopeOfFiling: Record<string, any>;
  stockExchangeDisclosure: Record<string, any>;
  directorsReport: Record<string, any>;
  statementByDirectors: Record<string, any>;
  directorBusinessReview: Record<string, any>;
  auditorsReport: Record<string, any>;
  statementOfFinancialPosition: Record<string, any>;
  subClassificationOfAssetsLiabilitiesEquity: Record<string, any>;
  statementOfFinancialPositionLiquidity: Record<string, any>;
  subClassificationLiquidity: Record<string, any>;
  statementOfProfitOrLoss: Record<string, any>;
  analysisOfProfitOrLoss: Record<string, any>;
  statementOfProfitOrLossByNature: Record<string, any>;
  analysisOfProfitOrLossByNature: Record<string, any>;
  statementOfComprehensiveIncome: Record<string, any>;
  statementOfComprehensiveIncomeBeforeTax: Record<string, any>;
  statementOfCashFlowsDirectMethod: Record<string, any>;
  statementOfCashFlowsIndirectMethod: Record<string, any>;
  statementOfChangesInEquity: Record<string, any>;
  notesCorporateInformation: Record<string, any>;
  notesAccountingPolicies: Record<string, any>;
  notesListOfNotes: Record<string, any>;
  notesIssuedCapital: Record<string, any>;
  notesRelatedPartyTransactions: Record<string, any>;
  // Add more sheets here as needed
}

export interface SheetEditedStatus {
  filingPrerequisites: boolean;
  filingInfo: boolean;
  scopeOfFiling: boolean;
  stockExchangeDisclosure: boolean;
  directorsReport: boolean;
  statementByDirectors: boolean;
  directorBusinessReview: boolean;
  auditorsReport: boolean;
  statementOfFinancialPosition: boolean;
  subClassificationOfAssetsLiabilitiesEquity: boolean;
  statementOfFinancialPositionLiquidity: boolean;
  subClassificationLiquidity: boolean;
  statementOfProfitOrLoss: boolean;
  analysisOfProfitOrLoss: boolean;
  statementOfProfitOrLossByNature: boolean;
  analysisOfProfitOrLossByNature: boolean;
  statementOfComprehensiveIncome: boolean;
  statementOfComprehensiveIncomeBeforeTax: boolean;
  statementOfCashFlowsDirectMethod: boolean;
  statementOfCashFlowsIndirectMethod: boolean;
  statementOfChangesInEquity: boolean;
  notesCorporateInformation: boolean;
  notesAccountingPolicies: boolean;
  notesListOfNotes: boolean;
  notesIssuedCapital: boolean;
  notesRelatedPartyTransactions: boolean;
}

/**
 * Row-level status for statement sheets with Mapping Status column
 * Used to determine the status-indicator (Red Cross, Pen, Green Tick)
 */
export interface StatementSheetRowStatus {
  hasFailedRows: boolean;   // Any row has 'Failed' Mapping Status
  hasEditedRows: boolean;   // Any row has been edited/updated
}

/**
 * Track row-level status for all statement sheets
 */
export interface StatementSheetRowStatusMap {
  statementOfFinancialPosition: StatementSheetRowStatus;
  subClassificationOfAssetsLiabilitiesEquity: StatementSheetRowStatus;
  statementOfFinancialPositionLiquidity: StatementSheetRowStatus;
  subClassificationLiquidity: StatementSheetRowStatus;
  statementOfProfitOrLoss: StatementSheetRowStatus;
  analysisOfProfitOrLoss: StatementSheetRowStatus;
  statementOfProfitOrLossByNature: StatementSheetRowStatus;
  analysisOfProfitOrLossByNature: StatementSheetRowStatus;
  statementOfComprehensiveIncome: StatementSheetRowStatus;
  statementOfComprehensiveIncomeBeforeTax: StatementSheetRowStatus;
  statementOfCashFlowsDirectMethod: StatementSheetRowStatus;
  statementOfCashFlowsIndirectMethod: StatementSheetRowStatus;
  statementOfChangesInEquity: StatementSheetRowStatus;
  notesIssuedCapital: StatementSheetRowStatus;
  notesRelatedPartyTransactions: StatementSheetRowStatus;
}

/**
 * Sheet-Level Status Data for dropdown status indicator
 * Used to determine: Validated (tick), Failed (cross), Edited (pencil)
 * Priority: Edited > Failed > Validated
 */
export interface SheetLevelStatusData {
  hasErrors: boolean;           // Has Error category (blocks validation) - shows Failed
  hasSuggestedChanges: boolean; // Has "* Suggested Change" only - shows red asterisk when Validated
}

/**
 * Track sheet-level status for all sheets (for dropdown status indicator)
 */
export interface SheetLevelStatusMap {
  filingPrerequisites: SheetLevelStatusData;
  filingInfo: SheetLevelStatusData;
  scopeOfFiling: SheetLevelStatusData;
  stockExchangeDisclosure: SheetLevelStatusData;
  directorsReport: SheetLevelStatusData;
  statementByDirectors: SheetLevelStatusData;
  directorBusinessReview: SheetLevelStatusData;
  auditorsReport: SheetLevelStatusData;
  statementOfFinancialPosition: SheetLevelStatusData;
  subClassificationOfAssetsLiabilitiesEquity: SheetLevelStatusData;
  statementOfFinancialPositionLiquidity: SheetLevelStatusData;
  subClassificationLiquidity: SheetLevelStatusData;
  statementOfProfitOrLoss: SheetLevelStatusData;
  analysisOfProfitOrLoss: SheetLevelStatusData;
  statementOfProfitOrLossByNature: SheetLevelStatusData;
  analysisOfProfitOrLossByNature: SheetLevelStatusData;
  statementOfComprehensiveIncome: SheetLevelStatusData;
  statementOfComprehensiveIncomeBeforeTax: SheetLevelStatusData;
  statementOfCashFlowsDirectMethod: SheetLevelStatusData;
  statementOfCashFlowsIndirectMethod: SheetLevelStatusData;
  statementOfChangesInEquity: SheetLevelStatusData;
  notesCorporateInformation: SheetLevelStatusData;
  notesAccountingPolicies: SheetLevelStatusData;
  notesListOfNotes: SheetLevelStatusData;
  notesIssuedCapital: SheetLevelStatusData;
  notesRelatedPartyTransactions: SheetLevelStatusData;
}

@Injectable({
  providedIn: 'root'
})
export class FilingFormStateService {
  // Dual-state (original/modified/metadata) and the current-sheet pointer now
  // live in IndexedDB via FilingFormPersistenceService — no localStorage
  // prefix constants here. Prefix-scoped wipes delegate to that service.
  // Sheet-level status is now sourced from API, no longer persisted in localStorage.
  // Pre-validation-completed + per-conversion category-A state now live in
  // IndexedDB via FilingFormPersistenceService (no localStorage prefix here).

  // Current conversionId (scopes persistence to specific filing)
  private currentConversionId: string = 'new'; // Default to 'new' for first-time mode

  /** Resolves when the current conversion's state has been hydrated from IDB. */
  private hydrationPromise: Promise<void> = Promise.resolve();
  /**
   * Await before reading state or wiring valueChanges after a setConversionId().
   *
   * Resolves for the most-recently-set conversion; a subsequent setConversionId()
   * supersedes the prior promise. Callers MUST await this before reading OR writing
   * state (wiring valueChanges) for the conversion.
   */
  whenHydrated(): Promise<void> {
    return this.hydrationPromise;
  }

  // One-shot flag: true until the first setConversionId() call in this JS
  // context. A fresh page load (refresh, tab reopen, direct URL entry) is
  // treated as "Continue without saving" — the beforeunload prompt warned the
  // user, so unsaved edits are discarded by snapping modified === original.
  // In-app conversion switches (subsequent calls) keep modified-cache as-is
  // so navigation between conversions stays non-destructive.
  //
  // On its own this flag is NOT enough to gate the discard: it is per-JS-context,
  // so the FIRST setConversionId() in ANY tab trips it — including a brand-new
  // tab opened on a conversion a sibling tab is actively editing. Consumed only
  // together with the per-tab `UNLOAD_INTENT_KEY` marker below; see
  // `consumeRefreshDiscardFlag`.
  private static refreshDiscardArmed = true;

  /**
   * Per-TAB marker recording that this tab is unloading with unsaved edits on a
   * given conversion. `sessionStorage` is the right store precisely because it is
   * tab-scoped: it survives a reload of THIS tab while a newly opened tab gets an
   * empty one. That distinction is what stops a second tab from discarding a
   * sibling's unsubmitted work — merely opening a tab used to be enough to trip
   * `refreshDiscardArmed`, which is a per-JS-context static and so is armed for
   * every tab's first `setConversionId`.
   *
   * The `mbrs-` prefix is deliberate — `clearCustomerDataCache()` already wipes
   * that namespace, so the marker inherits the existing logout hygiene.
   */
  private static readonly UNLOAD_INTENT_KEY = 'mbrs-unload-intent';

  /**
   * True once this tab has finished hydrating a conversion AND settled the
   * discard question for it — i.e. this tab is an established holder whose
   * in-memory state is the real thing, not a half-built one.
   *
   * It gates the answer to a sibling's `livenessPing`, and the gate matters:
   * two tabs opened on the same conversion at the same moment would otherwise
   * vouch for each other while BOTH were still undecided, and the last-tab-out
   * discard could never fire again. Only a tab that has already committed to
   * keeping its state may vouch for it.
   *
   * A tab that navigated back to the listing still answers, on purpose — it
   * kept the edits in memory and would show them again on re-entry (the
   * documented non-destructive in-app switch), so it IS a live holder.
   */
  private hasSettledConversionOwnership = false;

  /**
   * Reports which sheet this tab currently has ON SCREEN, or `null` when it has
   * none. Registered by `FilingFormContainerComponent` while it is mounted; see
   * {@link registerMountedSheetProbe}.
   */
  private mountedSheetProbe: (() => keyof FilingFormState | null) | null = null;

  /**
   * Grace timers for sheets a sibling has released but which nobody has yet
   * vouched for, keyed by `pendingSheetDiscardKey(conversionId, sheetKey)`.
   * A `sheetHeldPong` cancels the matching entry; expiry discards the sheet.
   */
  private readonly pendingSheetDiscards = new Map<string, ReturnType<typeof setTimeout>>();

  // Tracks which conversion (if any) has had its backend data loaded during the
  // current JS context. Used by the container to gate fetchAllMbrsData() so that
  // SPA navigation away from and back to the same filing does NOT re-fetch and
  // clobber the user's in-memory unsubmitted edits. Cleared on successful submit
  // via clearSessionLoadedFlag() so the next mount re-fetches fresh backend data.
  private sessionLoadedConversionId: string | null = null;

  // Flag to track when API data is being loaded (skip edited marks during this phase)
  private isLoadingFromAPI = false;

  // Batch mode: defers localStorage writes during bulk populate operations.
  // Call startBatchMode() before populating sheets and endBatchMode() after.
  // BehaviorSubject .next() calls still fire for reactivity, but localStorage
  // writes are skipped until endBatchMode() flushes once.
  private isBatchMode = false;

  /**
   * Signal to track pre-validation mode
   * When true, components should skip reactive behaviors that could cause infinite loops
   * Components check this flag to skip:
   * - Continuous form processing/validation
   * - State persistence subscriptions
   * - Cross-sheet validation triggers
   */
  public isPreValidating = signal<boolean>(false);

  private initialState: FilingFormState = {
    filingPrerequisites: {},
    filingInfo: {},
    scopeOfFiling: {},
    stockExchangeDisclosure: {},
    directorsReport: {},
    statementByDirectors: {},
    directorBusinessReview: {},
    auditorsReport: {},
    statementOfFinancialPosition: {},
    subClassificationOfAssetsLiabilitiesEquity: {},
    statementOfFinancialPositionLiquidity: {},
    subClassificationLiquidity: {},
    statementOfProfitOrLoss: {},
    analysisOfProfitOrLoss: {},
    statementOfProfitOrLossByNature: {},
    analysisOfProfitOrLossByNature: {},
    statementOfComprehensiveIncome: {},
    statementOfComprehensiveIncomeBeforeTax: {},
    statementOfCashFlowsDirectMethod: {},
    statementOfCashFlowsIndirectMethod: {},
    statementOfChangesInEquity: {},
    notesCorporateInformation: {},
    notesAccountingPolicies: {},
    notesListOfNotes: {},
    notesIssuedCapital: {},
    notesRelatedPartyTransactions: {}
  };

  private initialEditedStatus: SheetEditedStatus = {
    filingPrerequisites: false,
    filingInfo: false,
    scopeOfFiling: false,
    stockExchangeDisclosure: false,
    directorsReport: false,
    statementByDirectors: false,
    directorBusinessReview: false,
    auditorsReport: false,
    statementOfFinancialPosition: false,
    subClassificationOfAssetsLiabilitiesEquity: false,
    statementOfFinancialPositionLiquidity: false,
    subClassificationLiquidity: false,
    statementOfProfitOrLoss: false,
    analysisOfProfitOrLoss: false,
    statementOfProfitOrLossByNature: false,
    analysisOfProfitOrLossByNature: false,
    statementOfComprehensiveIncome: false,
    statementOfComprehensiveIncomeBeforeTax: false,
    statementOfCashFlowsDirectMethod: false,
    statementOfCashFlowsIndirectMethod: false,
    statementOfChangesInEquity: false,
    notesCorporateInformation: false,
    notesAccountingPolicies: false,
    notesListOfNotes: false,
    notesIssuedCapital: false,
    notesRelatedPartyTransactions: false
  };

  // Track auto-calculated field keys per sheet (these are computed from other fields, not user-editable)
  // Keys registered here are skipped in isSheetModified() comparison
  private calculatedKeysMap = new Map<keyof FilingFormState, Set<string>>();

  // Track sheets that are currently initializing (loading data, calculating totals)
  // isSheetModified() returns false for these sheets to prevent false positives
  private initializingSheets = new Set<keyof FilingFormState>();

  /**
   * Sheets whose user is currently mid-edit (a row pencil is open, before Tick).
   * While a sheet is in this set, updateSheetLevelStatus publishes the component's
   * (same-sheet-only) verdict instead of substituting the pure validator (which
   * would read the not-yet-committed modified state). In-memory only; cleared on
   * conversion switch.
   */
  private editingSheets = new Set<keyof FilingFormState>();

  // Snapshot of `editedSubject[sheetKey]` at the moment markSheetInitializing()
  // was called. Used by markSheetInitialized() to tell apart a *real* prior
  // user edit (carried across re-mount of the same conversion) from a
  // *spurious* edited-flag flip that occurred during init — e.g. role1's
  // syncFormToState() calls populating modifiedState with schema-driven
  // defaults, followed by hydrateEditedStatusFromCaches() / a cross-tab
  // listener detecting a diff and calling markSheetAsEdited(). Without this
  // snapshot, snapshotBaseline()'s edited-guard short-circuits and the sheet
  // selector flips from validated/invalid to the pencil icon on first paint.
  private editedAtInitStart = new Map<keyof FilingFormState, boolean>();

  // Dual-state management: Original (API) and Modified (user changes)
  // Start EMPTY at construction — no synchronous persistence read. The real
  // per-conversion state is loaded asynchronously by hydrateCurrentConversion()
  // once setConversionId() runs; callers gate reads behind whenHydrated().
  private originalStateSubject = new BehaviorSubject<FilingFormState>(this.applyDefaultValuesToState({ ...this.initialState }));
  private modifiedStateSubject = new BehaviorSubject<FilingFormState>(this.applyDefaultValuesToState({ ...this.initialState }));
  private editedSubject = new BehaviorSubject<SheetEditedStatus>({ ...this.initialEditedStatus });

  // N8N immutable snapshot baseline, keyed like originalState (per-sheet, control-name-keyed).
  // Set once per retrieve-mbrs-data load; drives the Validated* extracted-sum reference.
  // In-memory only (no persistence) — re-fetched on every full load.
  private snapshotStateSubject = new BehaviorSubject<Partial<FilingFormState>>({});

  // Initial statement sheet row status (for sheets with Mapping Status column)
  private initialStatementSheetRowStatus: StatementSheetRowStatusMap = {
    statementOfFinancialPosition: { hasFailedRows: false, hasEditedRows: false },
    subClassificationOfAssetsLiabilitiesEquity: { hasFailedRows: false, hasEditedRows: false },
    statementOfFinancialPositionLiquidity: { hasFailedRows: false, hasEditedRows: false },
    subClassificationLiquidity: { hasFailedRows: false, hasEditedRows: false },
    statementOfProfitOrLoss: { hasFailedRows: false, hasEditedRows: false },
    analysisOfProfitOrLoss: { hasFailedRows: false, hasEditedRows: false },
    statementOfProfitOrLossByNature: { hasFailedRows: false, hasEditedRows: false },
    analysisOfProfitOrLossByNature: { hasFailedRows: false, hasEditedRows: false },
    statementOfComprehensiveIncome: { hasFailedRows: false, hasEditedRows: false },
    statementOfComprehensiveIncomeBeforeTax: { hasFailedRows: false, hasEditedRows: false },
    statementOfCashFlowsDirectMethod: { hasFailedRows: false, hasEditedRows: false },
    statementOfCashFlowsIndirectMethod: { hasFailedRows: false, hasEditedRows: false },
    statementOfChangesInEquity: { hasFailedRows: false, hasEditedRows: false },
    notesIssuedCapital: { hasFailedRows: false, hasEditedRows: false },
    notesRelatedPartyTransactions: { hasFailedRows: false, hasEditedRows: false },
  };

  private statementSheetRowStatusSubject = new BehaviorSubject<StatementSheetRowStatusMap>(
    { ...this.initialStatementSheetRowStatus }
  );

  // Initial sheet-level status (for dropdown status indicator)
  // Default to hasErrors: true so sheets show as invalid until explicitly validated
  // This prevents showing green tick before form validation completes
  private initialSheetLevelStatus: SheetLevelStatusMap = {
    filingPrerequisites: { hasErrors: true, hasSuggestedChanges: false },
    filingInfo: { hasErrors: true, hasSuggestedChanges: false },
    scopeOfFiling: { hasErrors: true, hasSuggestedChanges: false },
    stockExchangeDisclosure: { hasErrors: true, hasSuggestedChanges: false },
    directorsReport: { hasErrors: true, hasSuggestedChanges: false },
    statementByDirectors: { hasErrors: true, hasSuggestedChanges: false },
    directorBusinessReview: { hasErrors: true, hasSuggestedChanges: false },
    auditorsReport: { hasErrors: true, hasSuggestedChanges: false },
    statementOfFinancialPosition: { hasErrors: true, hasSuggestedChanges: false },
    subClassificationOfAssetsLiabilitiesEquity: { hasErrors: true, hasSuggestedChanges: false },
    statementOfFinancialPositionLiquidity: { hasErrors: true, hasSuggestedChanges: false },
    subClassificationLiquidity: { hasErrors: true, hasSuggestedChanges: false },
    statementOfProfitOrLoss: { hasErrors: true, hasSuggestedChanges: false },
    analysisOfProfitOrLoss: { hasErrors: true, hasSuggestedChanges: false },
    statementOfProfitOrLossByNature: { hasErrors: true, hasSuggestedChanges: false },
    analysisOfProfitOrLossByNature: { hasErrors: true, hasSuggestedChanges: false },
    statementOfComprehensiveIncome: { hasErrors: true, hasSuggestedChanges: false },
    statementOfComprehensiveIncomeBeforeTax: { hasErrors: true, hasSuggestedChanges: false },
    statementOfCashFlowsDirectMethod: { hasErrors: true, hasSuggestedChanges: false },
    statementOfCashFlowsIndirectMethod: { hasErrors: true, hasSuggestedChanges: false },
    statementOfChangesInEquity: { hasErrors: true, hasSuggestedChanges: false },
    notesCorporateInformation: { hasErrors: true, hasSuggestedChanges: false },
    notesAccountingPolicies: { hasErrors: true, hasSuggestedChanges: false },
    notesListOfNotes: { hasErrors: true, hasSuggestedChanges: false },
    notesIssuedCapital: { hasErrors: true, hasSuggestedChanges: false },
    notesRelatedPartyTransactions: { hasErrors: true, hasSuggestedChanges: false },
  };

  private sheetLevelStatusSubject = new BehaviorSubject<SheetLevelStatusMap>(
    { ...this.initialSheetLevelStatus }
  );

  // Track which sheets have NOT yet been validated by frontend batch validation
  // When pending, the sheet status icon shows a spinner
  private pendingValidationSubject = new BehaviorSubject<Set<string>>(new Set());
  public pendingValidation$ = this.pendingValidationSubject.asObservable();

  // Track which sheets have had their status reported via updateSheetLevelStatus()
  // Used by the validation cycle to detect when a component has finished validating
  // (separate from pending — status is reported but icon stays as spinner until cycle explicitly clears pending)
  private statusReportedSheets = new Set<string>();

  // Sheets whose status has been finalized by the background validation cycle.
  // Late writes from surviving setTimeout callbacks in destroyed components are blocked.
  private statusLockedSheets = new Set<string>();

  // Observable streams
  public originalState$: Observable<FilingFormState> = this.originalStateSubject.asObservable();
  public modifiedState$: Observable<FilingFormState> = this.modifiedStateSubject.asObservable();
  public state$: Observable<FilingFormState> = this.modifiedStateSubject.asObservable(); // Backward compatibility
  public edited$: Observable<SheetEditedStatus> = this.editedSubject.asObservable();
  public statementSheetRowStatus$: Observable<StatementSheetRowStatusMap> = this.statementSheetRowStatusSubject.asObservable();
  public sheetLevelStatus$: Observable<SheetLevelStatusMap> = this.sheetLevelStatusSubject.asObservable();

  // Cross-tab "remote update applied" signal.
  //
  // Fires AFTER applyRemoteChange() has rehydrated a BehaviorSubject from a
  // sibling tab's notification. Distinguishes remote-driven state mutations
  // from local form-edit-driven ones so cross-sheet-dependent components
  // (e.g., role8 SoFP <-> role19 SoCIE equity reconciliation) can re-run
  // their validation entry points without piggy-backing on form.valueChanges.
  //
  // Why a separate channel instead of relying on modifiedState$:
  //   - modifiedState$ emits for BOTH local edits and remote rehydrates.
  //     Existing role-component subscriptions filter local self-updates via
  //     isSheetInitializing + distinctUntilChanged on the watched slice. A
  //     dedicated remote channel makes the "remote happened" intent explicit
  //     and lets components react regardless of which slice changed.
  //   - Components that only care about local reactivity (the existing
  //     valueChanges/state$ paths) are unaffected — they never subscribe.
  //
  // Payload:
  //   - streamKey: which BehaviorSubject was rehydrated ('modifiedState',
  //     'originalState', 'edited'). Only 'modifiedState' is currently
  //     interesting for cross-sheet validation re-trigger.
  //   - sheetKey:  optional hint about WHICH sheet's slice the remote tab
  //     mutated. May be undefined for whole-state rehydrates. Receivers
  //     should NOT rely on this for correctness — always re-read state if
  //     they need the actual slice.
  //   - changedSheets: the slices that ACTUALLY moved, computed by diffing the
  //     rehydrated state against what this tab held. Prefer this over `sheetKey`
  //     for correctness: it stays right when the transport's 300 ms debounce
  //     coalesces edits to several sheets into one notify, and it needs nothing
  //     from the sender. `sheetKey` remains a hint and is undefined in practice.
  private remoteUpdateAppliedSubject = new Subject<{
    streamKey: CrossTabSyncStreamKey;
    sheetKey?: string;
    changedSheets: (keyof FilingFormState)[];
  }>();
  public remoteUpdateApplied$ = this.remoteUpdateAppliedSubject.asObservable();

  // Submission-committed signal.
  //
  // Fires after a successful `submit-for-validation` flow commits the modified
  // state as the new original state (see `submit-for-validation.component.ts`).
  // The post-commit, backend-accepted event is deliberately DECOUPLED from
  // `modifiedState$` / `remoteUpdateApplied$` so the cross-tab reload prompt
  // only reacts to committed-and-regenerating core settings, not to in-flight
  // keystrokes.
  //
  // Scope:
  //   - `sheetKey`       — the sheet whose submit button was clicked. Used by
  //                        subscribers to self-filter so the originating sheet
  //                        does not prompt itself.
  //   - `affectedSlices` — slices the emitter declares as layout-affecting for
  //                        downstream consumers (today: FI + scope). Receivers
  //                        still re-read state — this is a routing hint, not
  //                        the payload.
  //
  // Emits on both the local Subject AND the cross-tab bus so same-tab
  // subscribers (e.g., another sheet currently mounted) react identically to
  // other-tab subscribers.
  private submissionCommittedSubject = new Subject<{
    sheetKey?: string;
    affectedSlices: string[];
  }>();
  public submissionCommitted$ = this.submissionCommittedSubject.asObservable();

  // Conversion-reset request signal (cross-tab leave-the-form safeguard).
  //
  // Fires when one tab leaves the form and wants siblings on the same
  // conversion to leave too. Two triggers: cancel/revert (the shared persisted
  // original/modified state was reverted, so a sibling would otherwise re-read
  // an emptied original and flip every sheet to the pencil/edited icon) and
  // resubmit-for-validation (the committed conversion re-enters backend
  // validation, so a sibling must stop editing now-stale data). In both cases
  // the origin tab broadcasts a 'conversionReset' over the existing cross-tab
  // channel; remote tabs whose current conversion matches navigate to the
  // listing instead. See requestConversionResetAcrossTabs().
  private readonly conversionResetRequestedSubject = new Subject<string>();
  /** Emits a conversionId when a REMOTE tab requests that this conversion be reset
   *  (it cancelled/reverted, or resubmitted for validation). Consumers (the
   *  container) redirect to the listing if the id matches their current
   *  conversion. Only fires for remote tabs — the cross-tab self-echo filter
   *  excludes the tab that triggered it. */
  readonly conversionResetRequested$ = this.conversionResetRequestedSubject.asObservable();

  // Network status (for offline/online detection)
  private isOnline = navigator.onLine;

  // Cross-tab sync transport. Injected here (not lazily referenced) so the
  // service is actually instantiated — providedIn: 'root' services are lazy,
  // and without this constructor injection no tab ever builds the
  // BroadcastChannel/storage listener, which silently disables sync.
  private readonly crossTabSync = inject(CrossTabSyncService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly validationCache = inject(ConversionValidationCacheService);
  private readonly parityCheck = inject(ParityCheckService);
  private readonly pureValidatorRegistry = inject(PureValidatorRegistry);
  private readonly runtimeConfig = inject(RuntimeConfigService);
  private readonly persistence = inject(FilingFormPersistenceService);

  /**
   * Combined gate for `[CrossTabSync]…` debug logs. The build-time
   * `!environment.production` check lets the minifier strip the entire
   * branch from the production bundle; the runtime `crossTabSyncDebug`
   * flag controls visibility in non-prod and can be flipped per-deployment
   * via `/etc/mbrs/frontend-overrides.json`.
   */
  private isCrossTabDebugEnabled(): boolean {
    return !environment.production && this.runtimeConfig.crossTabSyncDebug();
  }

  /** Suppress notify() while applying a remote rehydrate to prevent echo storms. */
  private isApplyingRemote = false;

  /**
   * True from the moment this tab LEAVES the filing form until it re-enters one
   * (see {@link clearAllInMemoryFormState} / {@link setConversionId}).
   *
   * Why it exists: the listing-return boundary in `CurrentSheetCacheService`
   * wipes the persisted blobs AND resets both state subjects to `initialState`,
   * but deliberately leaves `currentConversionId` pointing at the conversion the
   * user just left. Trailing writes DO land after that point — a sheet's async
   * re-baseline / status persist finishing after its component was destroyed;
   * `abandonValidationCache()`'s docblock documents the same class of write
   * arriving "~1s later, AFTER the navigation-away wipe". Such a write merges
   * its one slice into the now-EMPTY `modifiedStateSubject`, and the save funnels
   * would then (a) re-create a GUTTED blob under the live conversion key that the
   * wipe had just removed, and (b) broadcast `modifiedState` to sibling tabs still
   * editing that conversion. Receivers see a blob that is present-but-empty, which
   * is not the ABSENT case their guard skips, so they collapse their own modified
   * state: the active filing-info slice empties (the sheet selector loses every
   * sheet -> "Sheet Not Available" toast) and every sheet lights the pencil.
   *
   * While set, {@link saveModifiedState} / {@link saveOriginalState} do nothing at
   * all — no persist, no notify. That matches the intent of the boundary: the wipe
   * already declared "this conversion has no persisted state"; a torn-down sheet's
   * trailing write must not resurrect a gutted version of it.
   */
  private hasLeftForm = false;

  /**
   * Set by {@link clearAllInMemoryFormState}, consumed by the next
   * {@link setConversionId}: "this tab's in-memory state was wiped, so the next
   * entry must re-read it even if the conversion id has not changed."
   *
   * It exists because those two facts had no way to meet. The listing-return
   * boundary empties both state subjects but deliberately leaves
   * `currentConversionId` set (see the {@link hasLeftForm} docblock), so
   * `setConversionId(sameId)` hit its reload guard and returned BEFORE
   * hydrating. A tab that walked out to the XBRL Files listing and back into
   * the same conversion therefore ran on the emptied subjects and never
   * re-read IndexedDB — which also made the last-tab-out discard branch
   * unreachable, so a sibling's abandoned edits could never be collected. That
   * was the measured cause of the 2026-09-01 cross-tab report (Tab A re-enters
   * and still shows a closed Tab B's values and pencils); the diagnostic
   * signature was a mounted-sheet probe registering with no hydration after it.
   *
   * A flag rather than clearing `currentConversionId` in the wipe: that field
   * is read by the `livenessPing` answer predicate and by
   * `releaseMountedSheetAcrossTabs`, and blanking it would silently change what
   * this tab vouches for.
   */
  private needsRehydrate = false;

  /**
   * True only while {@link hydrateCurrentConversion}'s synchronous mutating tail
   * runs (the `.next()` calls + migrate/revert/hydrate branch). Lets the dev-only
   * tripwire in {@link saveModifiedState}/{@link saveOriginalState} tell apart
   * hydration's OWN legitimate writes (which must persist) from an EXTERNAL caller
   * mutating state during the async read-suspension (which would persist stale data).
   */
  private isHydrationInternalWrite = false;

  /**
   * Serializes remote-change applications so two rapid cross-tab messages cannot
   * interleave and clear {@link isApplyingRemote} early (spurious echo). The
   * `messages$` subscriber chains each {@link applyRemoteChange} onto this promise
   * so they run strictly in arrival order.
   */
  private remoteApplyChain: Promise<void> = Promise.resolve();

  /**
   * Suppress notify() while running an initial hydration / page-load code path
   * (loadFromAPI bursts, conversionId-switch reloads, migration syncs, post-batch
   * baseline snapshots, etc.). These writes are not user-driven changes — the
   * receiver tab already has the same data in localStorage, so broadcasting
   * during hydration produces spurious cross-tab notifications.
   *
   * Always toggled via `withHydration()` (try/finally) so the flag is cleared
   * even if a hydration step throws.
   */
  private isHydrating = false;

  /**
   * True ONLY while persisted state is being hydrated from IndexedDB
   * (hydrateCurrentConversion / withHydrationAsync). Distinct from the
   * overloaded `isHydrating` flag, which is ALSO set during the MBRS API load
   * (beginHydration/withHydration) to suppress cross-tab notify. The
   * premature-write tripwire keys on THIS flag so it never false-fires on
   * legitimate loadFromAPI writes.
   */
  private isPersistedStateHydrating = false;

  /**
   * Fires once per *genuine user edit* to sheet-level status, signalling the
   * validation-cache auto-persist subscription (see constructor) to write the
   * latest snapshot. Deliberately separate from {@link sheetLevelStatusSubject}:
   * that subject is the render-state and emits for resets, hydration, and the
   * navigation-away wipe too — none of which should be persisted. By driving
   * persistence off this intent stream instead of the value subject, those
   * non-edit emissions are inert by construction (no flag to arm, nothing to
   * suppress). Only {@link updateSheetLevelStatus} and
   * {@link batchUpdateSheetLevelStatus} push here.
   */
  private validationPersistRequest$ = new Subject<void>();

  /**
   * Conversion whose sheet-level status map was last published as a FULL cover
   * by a completed validation cycle, plus the exact key set that cover spanned.
   *
   * This is what lets the debounced persist below write `complete: true`. It is
   * an in-memory latch rather than a read-modify-write of the stored entry on
   * purpose: `mbrs-validation-cache-<id>` is shared localStorage with no tab
   * ownership, so re-reading and re-writing it races sibling tabs.
   *
   * The COVER is tracked, not just a boolean, because the applicable-sheet set
   * can grow mid-session (change the accounting basis or a method field and
   * previously inapplicable sheets come into play). A verdict for a sheet
   * outside the cover proves the map is no longer a full cover, so the latch
   * drops - see {@link noteReportedForCompleteness}.
   */
  private completeValidationConversionId: string | null = null;
  private completeValidationCover: Set<string> = new Set();

  /**
   * Conversion id whose validation-cache writes are currently suppressed, or
   * null. Set by {@link abandonValidationCache} when the user cancels out of a
   * sheet (discarding changes and navigating to the listing); cleared on the
   * next {@link setConversionId} call (any form re-entry, including the same-id
   * revisit path).
   *
   * Why it exists: a sheet's post-cancel re-baseline
   * (`onCancelConfirmed` → `loadSavedState` / `finalizeInit`) fires
   * `updateSheetLevelStatus` ~1s AFTER the cancel navigation, which would
   * otherwise re-create `mbrs-validation-cache-<id>` through the debounced
   * persist subscription below — AFTER
   * `CurrentSheetCacheService.flushConversionCaches()` already wiped it. The
   * subscription skips its write while the pending target equals this id, so the
   * trailing write is dropped instead of resurrecting the cache. Scoped to the
   * cancel path only: Back/Submit never set it. See the Cancel button flow in
   * {@link ../../components/common/submit-for-validation/submit-for-validation.component}.
   */
  private suppressedValidationCacheConversionId: string | null = null;

  /**
   * Run a hydration code path with `isHydrating` set so that any `notifyCrossTab`
   * calls fired during the block are short-circuited. Use try/finally so the
   * flag clears on both success and error.
   *
   * For Observable-based hydration paths, do not use this wrapper directly —
   * set `isHydrating = true` at the start and clear it inside `finalize()` so
   * the flag spans the asynchronous lifetime of the stream.
   */
  private withHydration<T>(fn: () => T): T {
    const wasHydrating = this.isHydrating;
    this.isHydrating = true;
    try {
      return fn();
    } finally {
      this.isHydrating = wasHydrating;
    }
  }

  /**
   * Async sibling of {@link withHydration}: spans an `await`able hydration
   * block with the SAME `isHydrating` flag so that any `notifyCrossTab` /
   * persistence-write fired while the IDB reads settle is suppressed. Restores
   * the previous flag value in `finally` (nesting-safe) on both success and
   * error.
   */
  private async withHydrationAsync(fn: () => Promise<void>): Promise<void> {
    const wasHydrating = this.isHydrating;
    const wasPersistedStateHydrating = this.isPersistedStateHydrating;
    this.isHydrating = true;
    this.isPersistedStateHydrating = true;
    try {
      await fn();
    } finally {
      this.isHydrating = wasHydrating;
      this.isPersistedStateHydrating = wasPersistedStateHydrating;
    }
  }

  /**
   * Public hydration markers for use by callers (e.g., the filing-form-container)
   * that drive an asynchronous fetch → populate → snapshot pipeline. The container
   * should call `beginHydration()` before kicking off the API fetch and
   * `endHydration()` from BOTH the success path AND the error/finalize path so the
   * flag is always cleared.
   */
  beginHydration(): void {
    this.isHydrating = true;
  }

  endHydration(): void {
    this.isHydrating = false;
  }

  constructor() {
    // Listen to network status changes
    window.addEventListener('online', () => {
      this.isOnline = true;
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Subscribe to remote tab notifications. Payload is notify-only — we
    // rehydrate from IndexedDB via FilingFormPersistenceService (the persisted
    // source of truth) rather than trusting channel contents.
    //
    // Applications are serialized through `remoteApplyChain` so two rapid
    // messages cannot interleave and clear `isApplyingRemote` early (which would
    // let one application's tail re-broadcast as a spurious echo).
    this.crossTabSync.messages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(msg => {
        // Conversion-reset is a notify/intent-only stream — it carries no state
        // slice to rehydrate, so it must NOT go through applyRemoteChange()
        // (which only knows how to re-read modified/original/edited slices).
        // Emit the request directly and bail. The transport already re-enters
        // Angular's zone (CrossTabSyncService.dispatchRemote wraps the emit in
        // zone.run), so no extra zone.run is needed here. Only remote tabs reach
        // this point — the self-echo filter drops the cancelling tab's own
        // message, so it never redirects itself.
        if (msg.streamKey === 'conversionReset') {
          this.conversionResetRequestedSubject.next(msg.conversionId);
          return;
        }

        // Same shape as `conversionReset` above — an intent, not a slice to
        // re-read — but the outcome is the opposite: siblings STAY on their
        // sheet and return to the baseline. Handled from memory rather than by
        // re-reading persistence, because the discarding tab usually tears its
        // persisted state down and the absent-blob guard in applyRemoteChange
        // would (rightly) refuse to act on what is left. See the
        // `conversionDiscarded` stream-key docs in CrossTabSyncService.
        if (msg.streamKey === 'conversionDiscarded') {
          if (msg.conversionId === this.currentConversionId)
            this.adoptRemoteDiscard();
          return;
        }

        // Liveness probe. A tab deciding whether it is the LAST holder of a
        // conversion asks; this tab answers only if it genuinely holds that
        // conversion AND has settled its own hydration. Both halves are
        // intent-only and must never reach applyRemoteChange().
        if (msg.streamKey === 'livenessPing') {
          const holdsConversion = msg.conversionId === this.currentConversionId;
          const willAnswer = holdsConversion && this.hasSettledConversionOwnership;
          // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
          // Records not just the verdict but everything the verdict is NOT
          // currently conditioned on, so a vouch that turns out to be hollow
          // (a tab that left the form, or one that has just discarded) is
          // visible in the timeline rather than inferred from it.
          recordLivenessDiagnostic('ping:received', msg.conversionId, {
            answered: willAnswer,
            senderTransportTabId: msg.tabId,
            // Lets the asker's `probe:result.answeredBy` be mapped back to a
            // specific diag tab — the only way to tell whether the voucher was
            // an already-unloading tab. Feature-detected because many specs
            // hand-stub CrossTabSyncService without `getTabId`.
            myTransportTabId:
              typeof (this.crossTabSync as any).getTabId === 'function'
                ? (this.crossTabSync as any).getTabId()
                : null,
            holdsConversion,
            myConversionId: this.currentConversionId,
            hasSettledConversionOwnership: this.hasSettledConversionOwnership,
            hasLeftForm: this.hasLeftForm,
            hasAnySheetModified: holdsConversion ? this.hasAnySheetModified() : null,
          });
          if (willAnswer)
            this.crossTabSync.answerLivenessPing(msg.conversionId);
          return;
        }

        // The asker consumes its own answers through a dedicated subscription
        // inside `probeLiveSiblings`; every other tab ignores them.
        if (msg.streamKey === 'livenessPong')
          return;

        // Per-sheet abandonment. A sibling has left the form and given up the
        // sheet it had on screen. Intent-only — like the liveness pair, it must
        // never reach applyRemoteChange().
        if (msg.streamKey === 'sheetReleased') {
          this.onRemoteSheetReleased(msg);
          return;
        }

        // Someone else still has that sheet on screen, so it is not abandoned.
        // Cancel the pending discard this tab may have scheduled. Note that a
        // tab which never scheduled one (because it is itself the holder, or
        // is on another conversion) simply finds nothing to cancel.
        if (msg.streamKey === 'sheetHeldPong') {
          this.cancelPendingSheetDiscard(msg.conversionId, msg.sheetKey);
          return;
        }

        this.remoteApplyChain = this.remoteApplyChain
          .then(() => this.applyRemoteChange(msg))
          .catch(err => {
            console.error('[FilingFormState] applyRemoteChange failed', err);
          });
      });

    // Auto-persist sheet-level status updates to the per-conversion cache so
    // that listing/dashboard pages reading the cache between background
    // validation cycles see the latest results — not the stale snapshot from
    // the last full cycle.
    //
    // Why this is needed in addition to runBackgroundValidationCycle's
    // explicit persist (the cycle calls ConversionValidatorService
    // .validateLoadedConversion(), which writes the cache via
    // ConversionValidationCacheService.set): individual sheet components call
    // updateSheetLevelStatus() in ~20 places (role0..role24) as the user
    // edits, but those writes only mutate the in-memory BehaviorSubject.
    // Without this subscription, a user who edits a sheet then navigates back
    // to the listing would see the pre-edit cached status until the next full
    // cycle persists.
    //
    // We listen to `validationPersistRequest$` — fired ONLY by the genuine
    // edit funnels — rather than to `sheetLevelStatusSubject` directly. That
    // way resets, hydration, and the navigation-away wipe (which all emit on
    // the value subject) never reach here, so there is nothing to suppress:
    // CurrentSheetCacheService can clear `mbrs-validation-cache-<id>` without a
    // debounced trailing write re-creating it.
    //
    // debounceTime(x) — coalesce bursts from a sheet's own validation pipeline
    //                     (several updateSheetLevelStatus calls during a save).
    // 'new' guard      — first-time mode has no real conversion to key by.
    // isHydrating      — sheet components still call updateSheetLevelStatus()
    //                    while API data loads; those transient values are not
    //                    real validation results, so skip them.
    this.validationPersistRequest$
      .pipe(
        debounceTime(CROSS_SHEET_VALIDATION_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.currentConversionId === 'new' || this.isHydrating) return;
        // Drop the trailing write when the user has cancelled out of this
        // conversion. Without this, a sheet's post-cancel re-baseline
        // re-creates mbrs-validation-cache-<id> after the navigation-away wipe.
        // See abandonValidationCache().
        if (
          this.suppressedValidationCacheConversionId === this.currentConversionId
        )
          return;
        // Persist ONLY sheets that actually reported a verdict.
        //
        // `initialSheetLevelStatus` seeds EVERY sheet to `hasErrors: true` and
        // `scopeStatusesToActivePhase` returns the whole in-memory map (25 keys
        // in Phase 2), so an unqualified write publishes a pessimistic error
        // for sheets no validator ever ran — the inapplicable Liquidity /
        // ByNature / BeforeTax / CashFlowsDirect variants that
        // ConversionValidatorService.applyApplicabilityFilter correctly excludes
        // from the cycle. That widened the clean, applicability-filtered entry
        // validateLoadedConversion had just written into a superset carrying
        // phantom errors.
        //
        // Those phantoms are inert only because this write also sets
        // `complete: false` and validateConversion reads the cache solely when
        // `complete` is true. Do not lean on that: ConversionStatusResolver's
        // `deriveTerminal` is `some(hasErrors) -> Review` across every key and
        // `resolve()` POSTs the verdict back to the backend, so an honest
        // `complete` flag over a seeded map would resolve clean conversions to
        // Review and persist it server-side.
        const reported = this.scopeStatusesToActivePhase(
          this.sheetLevelStatusSubject.value
        );
        const validated = Object.fromEntries(
          Object.entries(reported).filter(([key]) =>
            this.statusReportedSheets.has(key)
          )
        ) as SheetLevelStatusMap;
        // An EMPTY payload is worse than no entry: `deriveTerminal`'s
        // `some(hasErrors)` is false over `{}`, so it would read as Success.
        if (Object.keys(validated).length === 0) return;
        // `complete` is carried by the in-memory latch (see
        // `completeValidationConversionId`). The second clause guards a cover
        // that has SHRUNK — `markAllSheetsValidationPending` clears the reported
        // set, so a persist landing mid-cycle would otherwise claim a full cover
        // for a partial map.
        const complete =
          this.completeValidationConversionId === this.currentConversionId &&
          [...this.completeValidationCover].every((key) => key in validated);
        this.validationCache.set(this.currentConversionId, validated, complete);
      });
  }

  /**
   * Fire-and-forget cross-tab notification. Called from every write funnel
   * after the BehaviorSubject.next() and the persistence write have been enqueued.
   *
   * Guarded by `isApplyingRemote` so that rehydrate-triggered next() calls
   * do not re-broadcast and create a feedback loop.
   */
  private notifyCrossTab(
    streamKey: CrossTabSyncStreamKey,
    sheetKey?: string
  ): void {
    if (this.isApplyingRemote) {
      // Debug Log — gated by `crossTabSyncDebug` runtime flag in non-prod;
      // dead-code-eliminated in prod via `isCrossTabDebugEnabled()`.
      if (this.isCrossTabDebugEnabled()) {
        // console.log('[CrossTabSync][skip-notify]', {
        //   reason: 'applying-remote',
        //   streamKey,
        //   sheetKey,
        //   conversionId: this.currentConversionId,
        // });
      }
      return;
    }
    if (this.isHydrating) {
      // Debug Log — gated by `crossTabSyncDebug` runtime flag in non-prod;
      // dead-code-eliminated in prod via `isCrossTabDebugEnabled()`.
      if (this.isCrossTabDebugEnabled()) {
        // console.log('[CrossTabSync][skip-notify]', {
        //   reason: 'hydrating',
        //   streamKey,
        //   sheetKey,
        //   conversionId: this.currentConversionId,
        // });
      }
      return;
    }
    if (this.isBatchMode) {
      // Debug Log — gated by `crossTabSyncDebug` runtime flag in non-prod;
      // dead-code-eliminated in prod via `isCrossTabDebugEnabled()`.
      if (this.isCrossTabDebugEnabled()) {
        // console.log('[CrossTabSync][skip-notify]', {
        //   reason: 'batch-mode',
        //   streamKey,
        //   sheetKey,
        //   conversionId: this.currentConversionId,
        // });
      }
      return; // Batch flushes fire notify once at endBatchMode.
    }
    try {
      this.crossTabSync.notify({
        streamKey,
        conversionId: this.currentConversionId,
        sheetKey,
      });
    } catch (error) {
      console.error('[CrossTabSync] notify() threw:', error);
    }
  }

  /**
   * Tell other tabs on this conversion to leave the form and return to the
   * listing. Two callers, same receiver contract:
   *   - cancel/revert (`cancelChanges` / `cancelSheetChanges`) — the shared
   *     persisted original/modified state was reverted, so a sibling tab would
   *     otherwise re-read an emptied original and flip every sheet to the pencil
   *     icon.
   *   - resubmit-for-validation (`SubmitForValidationComponent.onSubmit`) — the
   *     conversion has been committed and re-enters backend validation, so a
   *     sibling tab sitting on the now-stale sheet data must not keep editing it.
   * In both cases the right outcome for passive tabs is identical: navigate to
   * `/mbrs/xbrl-files` (handled in the container via `conversionResetRequested$`).
   *
   * Broadcasts DIRECTLY via `crossTabSync.notifyImmediate` rather than through
   * `notifyCrossTab` so it bypasses the `isHydrating` / `isApplyingRemote` /
   * `isBatchMode` notify-suppression guards: the leave intent is an explicit
   * user action that must always reach sibling tabs. The cross-tab self-echo
   * filter ensures the origin tab does NOT receive its own message, so it
   * never redirects itself. Receivers compare `conversionId` and redirect only
   * if it matches their current conversion.
   */
  requestConversionResetAcrossTabs(): void {
    try {
      // Immediate (non-debounced) so the other tabs navigate away BEFORE the
      // ~300ms-debounced originalState/modifiedState notify can land and flip
      // their sheet statuses to the pencil icon.
      this.crossTabSync.notifyImmediate({
        streamKey: 'conversionReset',
        conversionId: this.currentConversionId,
      });
    } catch (error) {
      console.error('[CrossTabSync] conversionReset notifyImmediate() threw:', error);
    }
  }

  /**
   * Broadcast a submission-committed event after a successful
   * `submit-for-validation` flow. Unlike `notifyCrossTab`, this one also emits
   * LOCALLY (same-tab) so that other mounted sheets in the same tab react
   * identically to other-tab subscribers.
   *
   * Self-filter responsibility lies with subscribers: they should compare
   * `sheetKey` against their own `sheetKey` and skip if equal.
   */
  notifySubmissionCommitted(
    affectedSlices: string[],
    sheetKey?: string
  ): void {
    // Local fan-out — same-tab subscribers. Local state has already been
    // updated synchronously by commitModifiedStateAsOriginal(), so
    // `detectChanged` predicates read fresh values immediately.
    this.submissionCommittedSubject.next({
      sheetKey,
      affectedSlices,
    });

    // Cross-tab fan-out. Deferred by one debounce window so the sibling
    // debounced `originalState` / `modifiedState` broadcasts that
    // commitModifiedStateAsOriginal just enqueued are guaranteed to flush
    // first on this sender AND arrive first on the receiver. Without this
    // delay a receiving tab could process `submissionCommitted` before its
    // state caches are rehydrated, causing its `detectChanged` predicate to
    // read stale values and skip the prompt. 400ms > CrossTabSyncService's
    // 300ms NOTIFY_DEBOUNCE_MS by a safety margin.
    const capturedConversionId = this.currentConversionId;
    setTimeout(() => {
      try {
        this.crossTabSync.notify({
          streamKey: 'submissionCommitted',
          conversionId: capturedConversionId,
          sheetKey,
          affectedSlices,
        });
      } catch (error) {
        console.error('[CrossTabSync] submissionCommitted notify() threw:', error);
      }
    }, 400);
  }

  /**
   * Re-read from persistence (IDB) and push the fresh slice into the relevant
   * BehaviorSubject. Suppresses re-broadcast via `isApplyingRemote`.
   *
   * Async because the per-conversion reads now resolve from IndexedDB; the
   * `messages$` subscriber invokes this fire-and-forget (the cross-tab
   * transport already re-enters Angular's zone before emitting), so the
   * returned promise is intentionally unawaited at the call site.
   */
  private async applyRemoteChange(msg: CrossTabSyncMessage): Promise<void> {
    // Ignore notifications scoped to a different conversion.
    if (msg.conversionId !== this.currentConversionId) {
      // Debug Log — gated by `crossTabSyncDebug` runtime flag in non-prod;
      // dead-code-eliminated in prod via `isCrossTabDebugEnabled()`.
      if (this.isCrossTabDebugEnabled()) {
        // console.log('[CrossTabSync][apply:skipped-conversion-mismatch]', {
        //   incoming: msg.conversionId,
        //   current: this.currentConversionId,
        //   streamKey: msg.streamKey,
        // });
      }
      return;
    }

    // Debug Log — gated by `crossTabSyncDebug` runtime flag in non-prod;
    // dead-code-eliminated in prod via `isCrossTabDebugEnabled()`.
    if (this.isCrossTabDebugEnabled()) {
      // console.log('[CrossTabSync][apply]', {
      //   streamKey: msg.streamKey,
      //   sheetKey: msg.sheetKey,
      //   conversionId: msg.conversionId,
      // });
    }

    this.isApplyingRemote = true;
    let didRehydrate = false;
    let changedSheets: (keyof FilingFormState)[] = [];

    try {
      // The sibling's write may still be draining its own queue; flush ours so
      // the read below cannot observe a half-settled picture. Inside the try so
      // a rejection still hits the `finally` below — otherwise `isApplyingRemote`
      // would stay `true` for the tab's lifetime and every outgoing cross-tab
      // notify would be silently suppressed forever.
      await this.persistence.flush();

      switch (msg.streamKey) {
        case 'originalState': {
          const previous = this.originalStateSubject.value;
          const fresh = await this.persistence.getOriginal(this.currentConversionId, { bypassPending: true });
          // `fresh === null` means the read found nothing persisted for this
          // conversion — the state was torn down (e.g. a sibling's
          // discardModifiedChanges()/teardown left the blob absent), NOT that
          // the user emptied every sheet. Merging `fresh ?? {}` into
          // `initialState` would collapse every sheet's baseline to empty and,
          // via isSheetModified()'s asymmetric-emptiness branch, make every
          // sheet with real modified data look like a pending edit. Leave the
          // subjects alone and report no adoption instead (R2 regression).
          if (fresh === null) {
            if (this.isCrossTabDebugEnabled()) {
              // console.log('[CrossTabSync][rehydrate:originalState:skipped-absent]');
            }
            break;
          }
          const withDefaults = this.applyDefaultValuesToState({ ...this.initialState, ...fresh });
          changedSheets = diffChangedSheets(previous as any, withDefaults as any) as (keyof FilingFormState)[];
          this.originalStateSubject.next(withDefaults);
          // The pencil ("edited") flag is derived from (modified, original);
          // a remote baseline change can flip it either way, so reconcile it
          // now that the fresh original cache is in place. See
          // recomputeEditedStatusFromCaches().
          await this.refreshCounterpartCache('modified');
          this.recomputeEditedStatusFromCaches();
          didRehydrate = true;

          // Debug Log — gated by `crossTabSyncDebug` runtime flag in non-prod;
          // dead-code-eliminated in prod via `isCrossTabDebugEnabled()`.
          if (this.isCrossTabDebugEnabled()) {
            // console.log('[CrossTabSync][rehydrate:originalState]');
          }
          break;
        }
        case 'modifiedState': {
          const previous = this.modifiedStateSubject.value;
          const fresh = await this.persistence.getModified(this.currentConversionId, { bypassPending: true });
          // Same reasoning as the originalState branch above (R2 regression):
          // an ABSENT persisted blob means "nothing to rehydrate from", not
          // "every sheet's modified data is now empty". Without this guard,
          // `{ ...this.initialState, ...(fresh ?? {}) }` would collapse every
          // modified slice to empty while originalStateSubject still holds
          // real data, and isSheetModified()'s asymmetric-emptiness branch
          // would then read every non-default original field as a pending
          // edit — lighting the pencil on every sheet in the sibling tab,
          // including ones nobody touched. Leave the subjects alone and
          // report no adoption (changedSheets stays []) instead.
          if (fresh === null) {
            if (this.isCrossTabDebugEnabled()) {
              // console.log('[CrossTabSync][rehydrate:modifiedState:skipped-absent]');
            }
            break;
          }
          const withDefaults = this.applyDefaultValuesToState({ ...this.initialState, ...fresh });
          changedSheets = diffChangedSheets(previous as any, withDefaults as any) as (keyof FilingFormState)[];
          this.modifiedStateSubject.next(withDefaults);
          // Reconcile the pencil from the freshly-rehydrated caches so a remote
          // edit shows the pencil here, AND a remote revert-to-original clears
          // it. Without this the receiving tab's pencil stayed frozen because
          // `editedSubject` was never touched on a modifiedState rehydrate.
          await this.refreshCounterpartCache('original');
          this.recomputeEditedStatusFromCaches();
          didRehydrate = true;

          // Debug Log — gated by `crossTabSyncDebug` runtime flag in non-prod;
          // dead-code-eliminated in prod via `isCrossTabDebugEnabled()`.
          if (this.isCrossTabDebugEnabled()) {
            // console.log('[CrossTabSync][rehydrate:modifiedState]');
          }
          break;
        }
        case 'edited': {
          // Derive edited status from the freshly-loaded caches. Two-way so a
          // remote clear propagates (the legacy hydrate* ratchet only marked).
          this.recomputeEditedStatusFromCaches();
          didRehydrate = true;
          if (this.isCrossTabDebugEnabled()) {
            // console.log('[CrossTabSync][rehydrate:edited]');
          }
          break;
        }
        case 'rowStatus':
        case 'sheetStatus': {
          // These streams are API-derived in this codebase; no localStorage
          // cache exists to rehydrate from here. Intentionally a no-op until
          // a persistence source is wired.
          
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // if (!environment.production) {
          //   console.log('[CrossTabSync][rehydrate:noop]', {
          //     streamKey: msg.streamKey,
          //   });
          // }

          break;
        }
        case 'submissionCommitted': {
          // No localStorage rehydrate — the originalState/modifiedState
          // broadcasts emitted by commitModifiedStateAsOriginal() handle the
          // data refresh. This stream carries only the notification intent,
          // and fan-out happens below via submissionCommittedSubject.
          
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // if (!environment.production) {
          //   console.log('[CrossTabSync][rehydrate:submissionCommitted]', {
          //     sheetKey: msg.sheetKey,
          //     affectedSlices: msg.affectedSlices,
          //   });
          // }
          
          break;
        }
      }
    } finally {
      this.isApplyingRemote = false;
    }

    // Fan-out submissionCommitted AFTER clearing isApplyingRemote — keeps
    // this consistent with remoteUpdateAppliedSubject's ordering rule and
    // ensures any secondary writes subscribers make in response can still
    // notify other tabs.
    if (msg.streamKey === 'submissionCommitted') {
      this.submissionCommittedSubject.next({
        sheetKey: msg.sheetKey,
        affectedSlices: msg.affectedSlices ?? [],
      });
    }

    // Emit AFTER clearing isApplyingRemote so any downstream subscribers that
    // call updateSheetData() (e.g., a re-validation pass that writes its
    // sheet-level status) can correctly notify other tabs in turn. If we
    // emitted inside the try block, those follow-on writes would be silently
    // dropped by the notify guard.
    if (didRehydrate) {
      this.remoteUpdateAppliedSubject.next({
        streamKey: msg.streamKey,
        sheetKey: msg.sheetKey,
        changedSheets,
      });
    }
  }

  /**
   * Set the conversionId for persistence scoping
   * MUST be called before using the service for a specific filing
   * @param conversionId - The conversion ID from URL parameter (or 'new' for first-time mode)
   */
  setConversionId(conversionId: string | null): void {
    const newId = conversionId || 'new';
    // Re-entering the form re-enables validation-cache persistence, even on the
    // same-id revisit path that early-returns below (that path bypasses the
    // reload guard, so this clear must sit ABOVE it). Pairs with the cancel-path
    // suppression set in abandonValidationCache().
    this.suppressedValidationCacheConversionId = null;
    // Re-entering the form re-enables persistence + cross-tab notify. Like the
    // line above it this must sit ABOVE the reload guard, because the same-id
    // revisit path (listing -> back into the conversion just left) early-returns
    // and would otherwise stay detached for the rest of the session.
    this.hasLeftForm = false;
    // Consumed one-shot, and BEFORE the guard, so a wipe forces exactly one
    // rehydrate. Back-to-back `setConversionId(sameId)` calls with no wipe
    // between them (the container re-emitting route params) still no-op.
    const isColdReentry = this.needsRehydrate;
    this.needsRehydrate = false;
    if (this.currentConversionId === newId && !isColdReentry)
      return;

    // (a) id set SYNCHRONOUSLY, before any await — persistence scoping
    // (keyed off currentConversionId) is correct the instant this returns.
    // Anything that does not depend on the (async) persisted state runs here
    // so callers see consistent scoping even before whenHydrated() resolves.
    //
    // Clear status locks from the previous conversion's validation cycle.
    // Without this, updateSheetLevelStatus() calls for the new conversion
    // would be silently blocked by stale locks from the old conversion.
    this.unlockAllSheetStatuses();
    // A cover latched for the PREVIOUS conversion says nothing about this one.
    this.clearFullValidationLatch();
    this.currentConversionId = newId;
    // This tab does not hold the NEW conversion yet, so it must not vouch for
    // it while hydration runs. Re-set in hydrateCurrentConversion's `finally`.
    this.hasSettledConversionOwnership = false;
    // Reset sheet-level status — will be populated from API response.
    this.sheetLevelStatusSubject.next({ ...this.initialSheetLevelStatus });
    // Clear all edited status to start fresh.
    this.clearAllEdited();

    // (b) async hydration, gated by whenHydrated(); withHydrationAsync
    // suppresses persistence + cross-tab notify for the window.
    this.hydrationPromise = this.hydrateCurrentConversion(newId, isColdReentry);
  }

  /**
   * Async tail of {@link setConversionId}: read the two per-conversion slices
   * from IDB, push them into the dual-state subjects, run the empty-original
   * migration, then apply the refresh-discard policy. Wrapped in
   * {@link withHydrationAsync} so the notify-eligible writes it triggers
   * (revertToOriginalState's saveModifiedState, the migration's
   * saveOriginalState) do not broadcast to other tabs — the receiver already
   * has identical persisted state.
   *
   * DISABLED (Phase 1, XBRL-File-Loader-Field-Mapping-Optimizations):
   * Previously wiped the outgoing conversion's validation cache as defensive
   * house-cleaning for an editor-only world. That assumption no longer holds —
   * the listing/dashboard pages now read the same cache for every conversion
   * they paginate over, and wiping on switch would silently destroy work the
   * user has not explicitly invalidated. Cache freshness is now governed by the
   * explicit triggers in ConversionValidationCacheService (submit success,
   * SignalR status flip, logout). Preserved as a comment so it can be
   * re-enabled cleanly if validation reverts to single-conversion ownership.
   * // this.clearValidationCache();
   */
  private async hydrateCurrentConversion(
    id: string,
    isColdReentry = false
  ): Promise<void> {
    await this.withHydrationAsync(async () => {
      const [original, modified] = await Promise.all([
        this.persistence.getOriginal(id),
        this.persistence.getModified(id),
      ]);
      const origState = this.applyDefaultValuesToState({ ...this.initialState, ...(original ?? {}) });
      const modState = this.applyDefaultValuesToState({ ...this.initialState, ...(modified ?? original ?? {}) });

      // The mutating tail below is hydration's OWN legitimate write. Flag it so
      // the dev-only premature-write tripwire in saveModifiedState/saveOriginalState
      // fires only for EXTERNAL writes that race the async read-suspension above.
      this.isHydrationInternalWrite = true;
      try {
        this.originalStateSubject.next(origState);
        this.modifiedStateSubject.next(modState);

        // MIGRATION: If modified state has data but original state is empty, sync
        // them. Handles backwards compatibility for users who submitted before
        // the fix.
        this.migrateOriginalStateIfNeeded();

        // Refresh-discard policy: a fresh page load (refresh, tab reopen, direct
        // URL entry) is treated as "Continue without saving" — the beforeunload
        // prompt already warned the user. Snap modified === original so the
        // pencil icon never lies about persisted state. The submission baseline
        // (originalState) is untouched. In-app conversion switches keep the
        // existing "modified survives" semantics.
        // Consumed post-IDB-read; check-and-clear is synchronous so exactly one hydration wins the one-shot flag.
        // Scoped to THIS tab via the sessionStorage unload-intent marker — see
        // consumeRefreshDiscardFlag's docblock.
        //
        // Captured BEFORE consuming, because consuming clears it: the
        // last-tab-out probe below needs to know whether this was the first
        // setConversionId of the JS context (a genuine fresh page load) or a
        // later in-app conversion switch, which must stay non-destructive.
        const isFirstLoadOfContext = FilingFormStateService.refreshDiscardArmed;
        // The last-tab-out probe's own gate. A fresh page load qualifies, and
        // so does a return from the XBRL Files listing: `isColdReentry` says
        // this tab's in-memory state was wiped on the way out, so it holds no
        // unsaved work of its own that a discard could take. Before this term
        // existed the probe could fire at most ONCE per tab — the static above
        // is burned by the first `setConversionId` of the JS context — so a
        // long-lived tab could never collect a sibling's abandoned edits.
        const isColdEntry = isFirstLoadOfContext || isColdReentry;
        // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
        // Captured BEFORE the branch runs: `hasAnySheetModified()` is the exact
        // term `shouldDiscardAsLastTabOut` gates on, and the discard branch
        // mutates it, so reading it afterwards would report the wrong thing.
        recordLivenessDiagnostic('hydrate:start', id, {
          isFirstLoadOfContext,
          isColdReentry,
          isColdEntry,
          hasAnySheetModified: this.hasAnySheetModified(),
          originalBlobPresent: !!original,
          modifiedBlobPresent: !!modified,
          // TEMPORARY DIAGNOSTIC — names the sheets the arriving tab inherited
          // as dirty, so a multi-sheet residue is visible at the moment of
          // entry rather than deduced from the pencils on screen.
          dirtySheets: this.listDirtySheetsForDiagnostic(),
        });
        if (this.consumeRefreshDiscardFlag(id)) {
          this.revertToOriginalState();
          // revertToOriginalState()'s saveModifiedState() only ENQUEUES a
          // fire-and-forget persistence write and returns immediately — it does
          // not wait for the write to land. A sibling tab's applyRemoteChange()
          // awaits its OWN persistence.flush() (a no-op for this tab's queue),
          // then reads IndexedDB with `bypassPending: true`. Without the flush
          // below, that read can race this tab's still-queued write and observe
          // the PRE-discard blob, so diffChangedSheets() returns [], nothing
          // adopts, and the sibling later republishes the discarded edits —
          // undoing the discard in the tab that just confirmed it. Flushing
          // first guarantees the notify only fires once the write is durable.
          await this.persistence.flush();
          // revertToOriginalState's own notify is suppressed by the hydration
          // guard, so announce it explicitly — siblings adopt the reverted
          // values and the user is not asked to confirm the same discard
          // twice. Same escape hatch `conversionReset` uses.
          this.crossTabSync.notifyImmediate({
            streamKey: 'modifiedState',
            conversionId: id,
          });
          // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
          recordLivenessDiagnostic('hydrate:branch', id, { branch: 'refresh-discard' });
        } else if (await this.shouldDiscardAsLastTabOut(id, isColdEntry)) {
          // Last-tab-out discard. Every tab that held this conversion is gone,
          // and each was warned on the way out, so the unsaved edits are spent.
          this.revertToOriginalState();
          await this.persistence.flush();
          // NO notify here, and that asymmetry is the safety net. The branch
          // above acts on an EXPLICIT confirmation from this tab's own user, so
          // announcing it is right. This branch acts on an INFERENCE — "nobody
          // answered" — and an inference can be wrong (a sibling wedged past
          // the probe timeout). Staying quiet makes that mistake self-healing:
          // the sibling still holds its values in memory and re-persists them
          // on its next write, which we then adopt. Announcing would propagate
          // the mistake to the one tab that could still undo it.
          // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
          recordLivenessDiagnostic('hydrate:branch', id, { branch: 'last-tab-out-discard' });
        } else {
          // Rehydrate edited status from persisted caches so the sheet selector
          // shows the pencil icon for any sheet whose modified cache differs from
          // the original cache.
          this.hydrateEditedStatusFromCaches();
          // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
          // The defect's signature: this branch running in a freshly opened tab
          // that still shows the pencil. `edited` names the sheets it lit.
          recordLivenessDiagnostic('hydrate:branch', id, {
            branch: 'keep',
            edited: Object.entries(this.editedSubject.value)
              .filter(([, v]) => v)
              .map(([k]) => k),
          });
        }
      } finally {
        this.isHydrationInternalWrite = false;
        // Whatever the outcome, this tab has now committed to the state it
        // holds — so it may vouch for the conversion when a sibling probes.
        // In the `finally` so a failed hydration cannot leave the tab mute
        // forever, which would make it invisible to a sibling's probe and let
        // that sibling discard work this tab is still showing.
        this.hasSettledConversionOwnership = true;
        // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
        // The moment this tab becomes able to vouch. Its position relative to a
        // sibling's `probe:sent` in the merged timeline is the whole question.
        recordLivenessDiagnostic('ownership:settled', id, {
          hasAnySheetModified: this.hasAnySheetModified(),
        });
      }
    });
  }

  /**
   * Should this load discard the conversion's unsaved edits because every tab
   * that held them has been closed?
   *
   * The refresh-discard policy above answers "did THIS tab just reload itself?"
   * from a `sessionStorage` marker. That marker is destroyed with the tab that
   * writes it, so it can only ever complete within one tab's lifetime — closing
   * a tab and opening a fresh one loses it, and the edits survived a close the
   * user was explicitly warned about. This closes that hole by asking the
   * question the marker cannot: is anyone else still holding this conversion?
   *
   * Three conditions, each load-bearing:
   *
   *   - `isColdEntry` — this tab holds nothing of its own for the conversion,
   *     either because it is a fresh page load or because the listing-return
   *     wipe emptied its state on the way out (`needsRehydrate`). An in-app
   *     conversion switch that kept its state is NOT cold and must stay
   *     non-destructive; without this term the probe would destroy edits.
   *
   *     It deliberately reads wider than the `refreshDiscardArmed` static it
   *     replaced. That static is burned by the first `setConversionId` of the
   *     JS context, so the probe could fire at most once per tab — and a tab
   *     that had already been into any conversion could never collect a
   *     sibling's abandoned edits, which is the defect this widening fixes.
   *   - `hasAnySheetModified()` — there is something to discard. This is also
   *     what keeps the probe's latency off the ordinary path: a clean
   *     conversion never waits.
   *   - nobody answers the probe — see `CrossTabSyncService.probeLiveSiblings`
   *     for why liveness is proven by a reply rather than read from a registry
   *     of live tabs, and why the timeout is sized for a busy sibling.
   *
   * Note what is NOT a condition: whether any tab actually armed an unload
   * intent. Arming is per-tab `sessionStorage` and is exactly the signal that
   * cannot cross a tab boundary, so requiring it here would reintroduce the
   * bug. The warning the user answered on the way out is what licenses this.
   */
  private async shouldDiscardAsLastTabOut(
    conversionId: string,
    isColdEntry: boolean
  ): Promise<boolean> {
    if (!isColdEntry) return false;
    if (!this.hasAnySheetModified()) return false;

    return !(await this.crossTabSync.probeLiveSiblings(conversionId));
  }

  /**
   * Migration helper: Sync original and modified states if original is empty
   * This handles backwards compatibility for existing users
   */
  private migrateOriginalStateIfNeeded(): void {
    const original = this.originalStateSubject.value;
    const modified = this.modifiedStateSubject.value;

    // Check if original state is empty (never set) but modified has data
    const originalIsEmpty = Object.values(original).every(
      sheet => !sheet || Object.keys(sheet).length === 0
    );
    const modifiedHasData = Object.values(modified).some(
      sheet => sheet && Object.keys(sheet).length > 0
    );

    if (originalIsEmpty && modifiedHasData) {
      const clonedModified = this.deepCloneState(modified);
      this.originalStateSubject.next(clonedModified);
      this.saveOriginalState(clonedModified);
    }
  }

  /**
   * Get current conversionId
   */
  getConversionId(): string {
    return this.currentConversionId;
  }

  /**
   * Mark the current conversion's backend data as loaded within this JS context.
   * Called by FilingFormContainerComponent after fetchAllMbrsData() completes
   * successfully. Once set, the container skips re-fetching on subsequent navigation
   * back to the same conversion so in-memory unsubmitted edits are preserved.
   */
  markDataLoadedForCurrentConversion(): void {
    this.sessionLoadedConversionId = this.currentConversionId;
  }

  /**
   * Check whether the given conversion has already had its backend data loaded
   * within this JS context. Used by the filing form container to decide whether
   * to skip fetchAllMbrsData() on navigate-back. Returns false after a page
   * refresh (the flag is reset with the JS context) and after a successful
   * submit (the flag is explicitly cleared via clearSessionLoadedFlag()).
   */
  isDataLoadedInCurrentSession(conversionId: string | null | undefined): boolean {
    if (!conversionId) return false;
    return this.sessionLoadedConversionId === conversionId;
  }

  /**
   * Clear the "data loaded in session" flag so the next container mount will
   * re-run fetchAllMbrsData() against the backend. Called from
   * commitModifiedStateAsOriginal() after a successful submit, so the next
   * navigation into the same conversion pulls fresh server-committed data.
   */
  clearSessionLoadedFlag(): void {
    this.sessionLoadedConversionId = null;
  }

  /**
   * Get current state snapshot (modified state by default)
   */
  getState(): FilingFormState {
    return this.modifiedStateSubject.value;
  }

  /**
   * Get original state snapshot (API data)
   */
  getOriginalState(): FilingFormState {
    return this.originalStateSubject.value;
  }

  /**
   * Get specific sheet data (from modified state)
   */
  getSheetData(sheetKey: keyof FilingFormState): Record<string, any> {
    return this.modifiedStateSubject.value[sheetKey] || {};
  }

  /**
   * Get original sheet data (from API)
   */
  getOriginalSheetData(sheetKey: keyof FilingFormState): Record<string, any> {
    return this.originalStateSubject.value[sheetKey] || {};
  }

  /** Store one sheet's transformed (control-name-keyed) snapshot data. */
  setSnapshotSheetData(sheetKey: keyof FilingFormState, data: Record<string, any>): void {
    const current = { ...this.snapshotStateSubject.value };
    current[sheetKey] = data as any;
    this.snapshotStateSubject.next(current);
  }

  /** Read one sheet's snapshot data (empty object if not yet loaded). */
  getSnapshotSheetData(sheetKey: keyof FilingFormState): Record<string, any> {
    return (this.snapshotStateSubject.value[sheetKey] as Record<string, any>) ?? {};
  }

  /**
   * Mark a sheet as initializing. isSheetModified() returns false during initialization.
   */
  markSheetInitializing(sheetKey: keyof FilingFormState): void {
    this.initializingSheets.add(sheetKey);
    // Capture the edited flag at init start so markSheetInitialized() can
    // distinguish a real carry-over user edit (was true before init) from a
    // spurious flip that happened during init (was false before init, true
    // after). See the field comment on `editedAtInitStart` for context.
    this.editedAtInitStart.set(sheetKey, this.editedSubject.value[sheetKey] === true);
  }

  /**
   * Check whether a sheet is currently mid-initialization.
   * Cross-sheet listeners use this to suppress spurious emissions that occur
   * during their own init sequence (e.g. SOCIE's loadSavedState → updateSheetData
   * chain fires modifiedState$ before the sheet has finished initializing).
   */
  isSheetInitializing(sheetKey: keyof FilingFormState): boolean {
    return this.initializingSheets.has(sheetKey);
  }

  /** Mark a sheet as mid-edit (a row's pencil is open). See `editingSheets`. */
  markSheetEditing(sheetKey: keyof FilingFormState): void {
    this.editingSheets.add(sheetKey);
  }

  /** Clear a sheet's mid-edit flag (called on Tick and Cancel). */
  clearSheetEditing(sheetKey: keyof FilingFormState): void {
    this.editingSheets.delete(sheetKey);
  }

  /** Whether a sheet is currently mid-edit. */
  isSheetEditing(sheetKey: keyof FilingFormState): boolean {
    return this.editingSheets.has(sheetKey);
  }

  /**
   * Mark a sheet as done initializing and snapshot the baseline.
   * isSheetModified() will resume normal comparison after this.
   */
  markSheetInitialized(sheetKey: keyof FilingFormState): void {
    // If editedSubject[sheetKey] flipped from false → true *during* this init
    // cycle, the flip is spurious (driven by setup-method writes to
    // modifiedState that have no matching entry in originalState — the
    // canonical case is role1's syncFormToState() chain). Clear it before
    // snapshotting so snapshotBaseline()'s edited-guard does not short-circuit
    // and leave the sheet showing the pencil icon on first paint. A real
    // carry-over edit (was already true at init start) is preserved.
    const wasEditedAtStart = this.editedAtInitStart.get(sheetKey) === true;
    if (!wasEditedAtStart && this.editedSubject.value[sheetKey]) {
      this.clearSheetEdited(sheetKey);
    }
    this.editedAtInitStart.delete(sheetKey);

    this.snapshotBaseline(sheetKey);
    this.initializingSheets.delete(sheetKey);

    // Also clear pending validation — the component has finished initializing
    // and reported its status (or will report via valueChanges shortly after)
    this.markSheetValidationComplete(sheetKey as string);
  }

  /**
   * Mark a sheet as done initializing AND report its validation status.
   * This ensures correct sheet-level status is set at the end of the component's
   * init cycle, regardless of whether valueChanges fired.
   *
   * Publishes a COMPLETE status object (both flags) rather than a partial
   * `{ hasErrors }` patch. The mount-based path owns both flags at the
   * initialization boundary; carrying over `hasSuggestedChanges` from a
   * previous session — or from a stale entry in the sheet-level status map
   * after a same-id revisit — would leak unrelated state into the dual-path
   * parity check (pure validators always emit `hasSuggestedChanges: false`,
   * so a leaked `true` would throw a phantom mismatch).
   *
   * @param sheetKey - The sheet to mark as initialized
   * @param hasErrors - Whether the sheet has validation errors
   */
  markSheetInitializedWithStatus(
    sheetKey: keyof FilingFormState,
    hasErrors: boolean
  ): void {
    // `finally`, NOT `catch`: markSheetInitialized() (bracket close + baseline
    // snapshot) must always run so a throw inside updateSheetLevelStatus can
    // never leave the sheet stuck in `initializingSheets` for the rest of the
    // session (frozen pencil flag, no baseline, every
    // `filter(() => !isSheetInitializing(...))` guard across the app dead).
    // The error must still propagate — do not swallow it here. Every
    // `finalizeSheetInit` adopter's `ngOnInit` awaits this inside its own
    // try/catch and shows a sticky "Initialization Error" toast on rejection;
    // a `catch` here would silently swallow that signal instead.
    try {
      this.updateSheetLevelStatus(sheetKey, { hasErrors, hasSuggestedChanges: false });
    } finally {
      this.markSheetInitialized(sheetKey);
    }
  }

  /**
   * Snapshot the current modified state as the new baseline for a sheet.
   * Call this AFTER component initialization completes (form loaded, calculations done).
   * This ensures the "original" state matches exactly what the user sees,
   * eliminating false positives from type coercion, calculated totals, etc.
   *
   * GUARD: Skips the snapshot when the sheet already carries a user edit
   * (editedSubject[sheetKey] === true). Without this guard, re-mounting a sheet
   * that has unsubmitted edits (e.g. after SPA navigate-back) would copy the
   * user's edited values into the "original" cache, erasing the diff that the
   * pencil icon depends on. Matches the existing contract of snapshotAllBaselines().
   */
  snapshotBaseline(sheetKey: keyof FilingFormState): void {
    if (this.editedSubject.value[sheetKey]) return;

    const currentModified = this.modifiedStateSubject.value[sheetKey];
    if (!currentModified || Object.keys(currentModified).length === 0) return;

    // Deep clone to prevent shared references
    const clonedData = structuredClone(currentModified);

    const currentOriginal = { ...this.originalStateSubject.value };
    currentOriginal[sheetKey] = clonedData;

    this.originalStateSubject.next(currentOriginal);
    this.saveOriginalState(currentOriginal);
  }

  /**
   * Snapshot ALL sheets' baselines at once.
   * Call this after all API data has been loaded and transformed.
   * Copies the entire modified state to the original state so both match exactly.
   * Only snapshots sheets that haven't been user-edited (edited status is false).
   */
  snapshotAllBaselines(): void {
    const currentModified = this.modifiedStateSubject.value;
    const currentOriginal = { ...this.originalStateSubject.value };
    const editedStatus = this.editedSubject.value;

    // For each sheet, only snapshot if it hasn't been marked as edited
    const allSheetKeys = Object.keys(this.initialState) as Array<keyof FilingFormState>;
    for (const sheetKey of allSheetKeys) {
      const isEdited = editedStatus[sheetKey as keyof SheetEditedStatus];
      if (!isEdited) {
        const modifiedData = currentModified[sheetKey];
        if (modifiedData && Object.keys(modifiedData).length > 0) {
          currentOriginal[sheetKey] = structuredClone(modifiedData);
        }
      }
    }

    this.originalStateSubject.next(currentOriginal);
    this.saveOriginalState(currentOriginal);
  }

  /**
   * Check if sheet has been modified (different from original)
   * Handles date comparison properly by normalizing dates before comparison
   */
  isSheetModified(sheetKey: keyof FilingFormState): boolean {
    // Sheet is currently initializing — treat as not modified to prevent false positives
    if (this.initializingSheets.has(sheetKey)) return false;

    const original = this.originalStateSubject.value[sheetKey];
    const modified = this.modifiedStateSubject.value[sheetKey];

    // Both empty/null = not modified
    if (!original && !modified) return false;
    if (!original || !modified) {
      // One is empty, other is not - check if the non-empty one has only default values
      const nonEmpty = original || modified;
      if (typeof nonEmpty === 'object') {
        return Object.values(nonEmpty).some(value => !this.isDefaultValue(value));
      }
      return true;
    }

    // Get calculated keys for this sheet (these are auto-computed, not user edits)
    const calculatedKeys = this.calculatedKeysMap.get(sheetKey);

    // Only compare keys that exist in the original state (from API)
    // Extra keys in modified state (from form controls not in API) are ignored
    const originalKeys = Object.keys(original);
    for (const key of originalKeys) {
      // Skip auto-calculated keys (totals computed by CalculationService)
      if (calculatedKeys && calculatedKeys.has(key)) {
        continue;
      }

      const origVal = (original as any)[key];
      const modVal = (modified as any)[key];

      // Skip if modified value is null/undefined AND the original is also a default value.
      // This means the form never populated this key (e.g., bracket-notation keys that
      // the form doesn't directly manage).
      // BUT if the original had a real value and modified is now null/undefined,
      // the user cleared the field — that IS a real change and should NOT be skipped.
      if ((modVal === null || modVal === undefined) && this.isDefaultValue(origVal)) {
        continue;
      }

      if (!this.deepEqual(origVal, modVal)) {
        return true;
      }
    }

    // Also check keys in modified that don't exist in original
    // These are form controls that weren't in the API data (e.g., fields the user typed into for the first time)
    const originalKeySet = new Set(originalKeys);
    const modifiedKeys = Object.keys(modified);
    for (const key of modifiedKeys) {
      if (originalKeySet.has(key)) continue; // already compared above
      if (calculatedKeys && calculatedKeys.has(key)) continue;
      const modVal = (modified as any)[key];
      if (!this.isDefaultValue(modVal)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Register auto-calculated field keys for a sheet.
   * These keys are skipped in isSheetModified() comparison because their values
   * are computed from other fields (by CalculationService), not edited by users.
   */
  registerCalculatedKeys(sheetKey: keyof FilingFormState, keys: string[]): void {
    const existing = this.calculatedKeysMap.get(sheetKey) || new Set<string>();
    keys.forEach(key => existing.add(key));
    this.calculatedKeysMap.set(sheetKey, existing);
  }

  /**
   * Check if a value is a "default" value (null, undefined, empty string, 0)
   * Used by isSheetModified to ignore form controls with default values
   * that don't exist in the original API data
   */
  private isDefaultValue(value: any): boolean {
    if (value === null || value === undefined || value === '' || value === 0 || value === false) {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    // Empty objects or objects where all values are default
    // e.g. { sepCur: null, conCur: null } from form initialization
    if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
      const keys = Object.keys(value);
      return keys.length === 0 || keys.every(key => this.isDefaultValue(value[key]));
    }
    return false;
  }

  /**
   * Check if any sheet has been modified (different from original)
   * Returns true if at least one sheet has unsaved changes
   */
  /**
   * TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
   *
   * Every sheet this tab currently considers dirty, by the SAME two measures
   * `releaseMountedSheetAcrossTabs` uses (value diff OR the explicit edited
   * flag). Exists so the timeline can state the residue directly: the release
   * protocol names one sheet, and this names all of them.
   *
   * Swallows its own errors — a diagnostic must never break the path it is
   * observing.
   */
  private listDirtySheetsForDiagnostic(): string[] {
    try {
      return (Object.keys(this.initialState) as Array<keyof FilingFormState>)
        .filter(stateKey => this.isSheetModified(stateKey) || this.getSheetEdited(stateKey))
        .map(stateKey => stateKey as string);
    } catch {
      return [];
    }
  }

  hasAnySheetModified(): boolean {
    const allSheetKeys = Object.keys(this.initialState) as Array<keyof FilingFormState>;
    for (const sheetKey of allSheetKeys) {
      if (this.isSheetModified(sheetKey)) {
        // Log which specific key differs — using SAME logic as isSheetModified
        const original = this.originalStateSubject.value[sheetKey];
        const modified = this.modifiedStateSubject.value[sheetKey];
        const calculatedKeys = this.calculatedKeysMap.get(sheetKey);
        if (original && modified) {
          for (const key of Object.keys(original)) {
            if (calculatedKeys && calculatedKeys.has(key)) continue;
            const origVal = (original as any)[key];
            const modVal = (modified as any)[key];
            // ----------------------------
            // Developer Note (Merge Resolvement TODO): Commented out due to code differences by developer
            // are roughly around the same date. Revert to this if below is not the correct flow or when developer
            // replies to inquiry.

            // Same skip logic as isSheetModified
            // if ((modVal === null || modVal === undefined)) continue;
            if (modVal === null || modVal === undefined && this.isDefaultValue(origVal)) continue;

            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // if (!this.deepEqual(origVal, modVal)) {
            //   console.log(`   ❌ Diff key: "${key}"`);
            //   console.log(`      Original (${typeof origVal}):`, origVal);
            //   console.log(`      Modified (${typeof modVal}):`, modVal);
            //   console.log(`      isDate original:`, this.isDateObject(origVal));
            //   console.log(`      isDate modified:`, this.isDateObject(modVal));
            // }
            // ----------------------------
          }
        }
        return true;
      }
    }
    return false;
  }

  /**
   * Deep equality check with date normalization
   * Dates are compared by their ISO date string representation to avoid timezone/serialization issues
   */
  private deepEqual(obj1: any, obj2: any): boolean {
    // Handle null/undefined
    if (obj1 === obj2) return true;

    // Treat all "empty/default" values as equivalent
    // This prevents false positives when form initialization sets null→0 or null→''
    if (this.isDefaultValue(obj1) && this.isDefaultValue(obj2)) return true;

    if (obj1 === null || obj2 === null) return obj1 === obj2;
    if (obj1 === undefined || obj2 === undefined) return obj1 === obj2;

    // Handle Date objects - compare as ISO date strings
    if (this.isDateObject(obj1) && this.isDateObject(obj2)) {
      const date1 = this.toLocalDateString(new Date(obj1));
      const date2 = this.toLocalDateString(new Date(obj2));
      return date1 === date2;
    }

    // If one is Date and other is string, normalize and compare
    // Check both instanceof Date and duck-typing (obj.getTime) for cross-context safety
    if (this.isDateObject(obj1) && typeof obj2 === 'string') {
      return this.compareDateWithString(new Date(obj1), obj2);
    }

    if (typeof obj1 === 'string' && this.isDateObject(obj2)) {
      return this.compareDateWithString(new Date(obj2), obj1);
    }

    // Handle strings (potentially date strings)
    if (typeof obj1 === 'string' && typeof obj2 === 'string') {
      // Try to normalize both as dates if either looks like a date
      const date1Normalized = this.tryNormalizeDateString(obj1);
      const date2Normalized = this.tryNormalizeDateString(obj2);

      // If both successfully parsed as dates, compare normalized forms
      if (date1Normalized && date2Normalized) {
        return date1Normalized === date2Normalized;
      }

      return obj1 === obj2;
    }

    // Handle arrays
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) return false;
      return obj1.every((item, index) => this.deepEqual(item, obj2[index]));
    }

    // Handle objects
    if (typeof obj1 === 'object' && typeof obj2 === 'object') {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      if (keys1.length !== keys2.length) return false;

      return keys1.every(key => this.deepEqual(obj1[key], obj2[key]));
    }

    // Try to normalize both values as dates before giving up
    // Handles: string "01/01/2025" vs Date object, or any mixed date representations
    const date1 = this.tryParseAsDate(obj1);
    const date2 = this.tryParseAsDate(obj2);
    if (date1 && date2) {
      return date1 === date2;
    }

    // Standardize type comparison by converting to string
    // Handles cases like API returning number 150 vs form returning string "150"
    return String(obj1) === String(obj2);
  }

  /**
   * Extract local date as YYYY-MM-DD string (timezone-safe, no UTC conversion)
   * Fixes: Jan 01 2025 00:00:00 GMT+0800 → toISOString() gives "2024-12-31" (wrong!)
   *        This method correctly returns "2025-01-01"
   */
  private toLocalDateString(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  /**
   * Check if a value is a Date object (supports cross-context and duck-typing)
   */
  private isDateObject(value: any): boolean {
    if (value instanceof Date) return true;
    // Duck-type check for Date-like objects (cross-context safety)
    return value !== null && typeof value === 'object' && typeof value.getTime === 'function' && !isNaN(value.getTime());
  }

  /**
   * Compare a Date object with a date string in any common format
   * Supports: DD/MM/YYYY, YYYY-MM-DD, ISO strings, and Date.toString() output
   */
  private compareDateWithString(dateObj: Date, dateStr: string): boolean {
    // Normalize the Date object to YYYY-MM-DD
    const dateObjNormalized = this.toLocalDateString(dateObj); // e.g. "2025-01-01"

    // Try parsing the string as DD/MM/YYYY format
    const ddmmyyyyMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyyMatch) {
      const [, dd, mm, yyyy] = ddmmyyyyMatch;
      const dateStrNormalized = `${yyyy}-${mm}-${dd}`; // Convert to YYYY-MM-DD
      return dateObjNormalized === dateStrNormalized;
    }

    // Try parsing as ISO or YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      const parsedDate = new Date(dateStr);
      if (!isNaN(parsedDate.getTime())) {
        return dateObjNormalized === this.toLocalDateString(parsedDate);
      }
      return dateObjNormalized === dateStr.split('T')[0];
    }

    // Try parsing the string as a Date (handles "Wed Jan 01 2025 ..." format)
    const parsedDate = new Date(dateStr);
    if (!isNaN(parsedDate.getTime())) {
      return dateObjNormalized === this.toLocalDateString(parsedDate);
    }

    return false;
  }

  /**
   * Try to parse any value (string, Date object, or date-like object) to normalized YYYY-MM-DD
   * Returns null if the value is not a recognizable date
   */
  private tryParseAsDate(value: any): string | null {
    // Date object
    if (value instanceof Date && !isNaN(value.getTime())) {
      return this.toLocalDateString(value);
    }

    // Duck-typed Date object
    if (value !== null && typeof value === 'object' && typeof value.getTime === 'function' && !isNaN(value.getTime())) {
      return this.toLocalDateString(new Date(value.getTime()));
    }

    // String date formats
    if (typeof value === 'string') {
      return this.tryNormalizeDateString(value);
    }

    return null;
  }

  /**
   * Try to normalize a string to YYYY-MM-DD if it looks like a date
   * Returns the normalized date string, or null if not a date
   * Supports: DD/MM/YYYY, YYYY-MM-DD, ISO strings (YYYY-MM-DDTHH:mm:ss)
   */
  private tryNormalizeDateString(str: string): string | null {
    // DD/MM/YYYY format (e.g. "01/01/2025")
    const ddmmyyyyMatch = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyyMatch) {
      const [, dd, mm, yyyy] = ddmmyyyyMatch;
      return `${yyyy}-${mm}-${dd}`;
    }

    // YYYY-MM-DD or ISO format (e.g. "2025-01-01" or "2025-01-01T00:00:00.000Z")
    if (str.match(/^\d{4}-\d{2}-\d{2}/)) {
      const parsedDate = new Date(str);
      if (!isNaN(parsedDate.getTime())) {
        return this.toLocalDateString(parsedDate);
      }
      return str.split('T')[0];
    }

    return null;
  }

  /**
   * Update specific sheet data (partial update to modified state)
   * Always saves to localStorage to preserve user changes
   * IMPORTANT: Applies default values for filingPrerequisites to ensure consistency
   * @param sheetKey - The sheet to update
   * @param data - The data to update
   * @param isValid - Whether the form is valid (used for validation status tracking)
   * @param skipEditedMark - If true, skip marking the sheet as edited (use for initial data loading)
   */
  updateSheetData(sheetKey: keyof FilingFormState, data: Record<string, any>, isValid: boolean = false, skipEditedMark: boolean = false): void {
    const currentState = this.modifiedStateSubject.value;
    const updatedState = {
      ...currentState,
      [sheetKey]: {
        ...currentState[sheetKey],
        ...data
      }
    };

    // Apply defaults before emitting (important for BehaviorSubject subscribers).
    // `currentState` is passed so the TypeOfSubmission derivation can recognise a
    // user CLEARING the Basis dropdown (a transition) as opposed to the ordinary
    // empty Basis of a brand-new filing — see deriveTypeOfSubmission.
    const stateWithDefaults = this.applyDefaultValuesToState(updatedState, currentState);
    this.modifiedStateSubject.next(stateWithDefaults);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Update Sheet Data] Sheet Key: ", sheetKey);
    // console.log("[On Update Sheet Data] Current: ", currentState);
    // console.log("[On Update Sheet Data] Updated: ", updatedState);
    // console.log("[On Update Sheet Data] Flag 1: ", skipEditedMark);
    // console.log("[On Update Sheet Data] Flag 2: ", this.isLoadingFromAPI);
    // console.log("[On Update Sheet Data] Flag 3: ", this.isPreValidating());
    
    // Reconcile the sheet's "edited" pencil flag against ORIGINAL whenever data is
    // updated (unless skipped for initial load, API loading, or pre-validation).
    //
    // Why reconcile (two-way) and not markSheetAsEdited (one-way): the sheets
    // WITHOUT an inline pencil/tick edit mode (role1/2/4, …) drive this flag only
    // through their auto-save pipeline → updateSheetData. markSheetAsEdited only
    // ever SETS the flag — it compares against the previous MODIFIED slot, never
    // the original — so once a user edited a field the pencil stayed frozen even
    // after every field was reverted to its original value. reconcileEditedFlag
    // delegates to isSheetModified (modified-vs-original, via the date-normalising
    // deepEqual), so a genuine edit raises the pencil and a full manual revert
    // clears it. The date-normalising compare also subsumes the old
    // diffIsDateFormatOnly guard: the first auto-save's API-ISO→Date round-trip
    // reduces to "not modified", so it no longer spuriously flips the icon.
    //
    // The inline edit-mode sheets are unaffected: their programmatic writes pass
    // skipEditedMark=true (bypassing this block), and their user-driven Tick writes
    // already call reconcileEditedFlag immediately after, so this is idempotent.
    if (!skipEditedMark && !this.isLoadingFromAPI && !this.isPreValidating()) {
      this.reconcileEditedFlag(sheetKey);
    }

    // Always save to preserve user changes (even when form is invalid).
    // Per-sheet write: only the slices this call actually moved are persisted,
    // so a sibling tab's newer save on ANOTHER sheet survives. See
    // saveModifiedState's `changedKeys` parameter.
    this.saveModifiedState(stateWithDefaults, diffChangedSheets(currentState as any, stateWithDefaults as any) as (keyof FilingFormState)[]);
  }

  /**
   * Apply a SYSTEM-DRIVEN correction to a sheet's values — a write the user did
   * not make, but WILL see — and reconcile the sheet-selector pencil against it.
   *
   * The case this exists for: a sheet rewrites its own values when the user
   * lands on it (the historical example was a rounding normalisation, since
   * fixed at source). Each affected row's Mapping Status badge flips to
   * `Edited` on its own, because {@link computeRowStatus} is DERIVED — it
   * recompares the row against `originalState` on every render. The selector
   * icon does not, because it reads the STORED `editedSubject` flag, which only
   * a `markSheetAsEdited` / `reconcileEditedFlag` call moves. A plain
   * `updateSheetData(..., skipEditedMark = true)` therefore left the two
   * disagreeing — row Edited, sheet still tick or cross — for the rest of the
   * session, since the only two-way reconciler
   * ({@link recomputeEditedStatusFromCaches}) runs on an API load and a
   * cross-tab adopt, and a same-tab correction triggers neither.
   *
   * Why this is a separate method and not a change to `skipEditedMark`: that
   * flag also carries the role0 / role1 driver-clear cascade
   * (`syncFormToState`), a schema-rule side effect that fires POST-init and
   * must NOT raise a pencil of its own — the user's edit of the DRIVER already
   * owns that, through the auto-save pipeline. Reconciling every suppressed
   * write would invent pencils there. So the seam is opt-in: call this only for
   * a write that changes a value the user is looking at.
   *
   * Two-way, like every other reconcile path: a correction that restores the
   * baseline value lowers the pencil rather than latching it.
   *
   * @param sheetKey - The sheet being corrected
   * @param data - The corrected values (partial, merged as in updateSheetData)
   * @param isValid - Whether the form is valid, forwarded to updateSheetData
   */
  applySystemCorrection(
    sheetKey: keyof FilingFormState,
    data: Record<string, any>,
    isValid: boolean = false
  ): void {
    this.updateSheetData(sheetKey, data, isValid, true);

    // The same three windows `updateSheetData` suppresses marking in. The
    // initializing guard is load-bearing in BOTH directions: a correction
    // inside the bracket is part of landing rather than a change to what
    // landed, and `isSheetModified()` answers false for an initializing sheet —
    // so reconciling here would read "no diff" and lower a carry-over pencil
    // that is entirely real.
    if (this.initializingSheets.has(sheetKey)) return;
    if (this.isLoadingFromAPI || this.isPreValidating()) return;

    this.reconcileEditedFlag(sheetKey);
  }

  /**
   * Replace entire sheet data (full replacement)
   * IMPORTANT: Applies default values for filingPrerequisites to ensure consistency
   */
  setSheetData(sheetKey: keyof FilingFormState, data: Record<string, any>): void {
    const currentState = this.modifiedStateSubject.value;
    const updatedState = {
      ...currentState,
      [sheetKey]: data
    };

    // Apply defaults before emitting and saving (previous state passed for the
    // cleared-Basis transition check — see updateSheetData / deriveTypeOfSubmission)
    const stateWithDefaults = this.applyDefaultValuesToState(updatedState, currentState);
    this.modifiedStateSubject.next(stateWithDefaults);
    // Per-sheet write — see the note in updateSheetData.
    this.saveModifiedState(stateWithDefaults, diffChangedSheets(currentState as any, stateWithDefaults as any) as (keyof FilingFormState)[]);
  }

  /**
   * Load data from API and store as original state
   * This is the pristine data that will be used for cancel/revert
   * IMPORTANT: Applies default values to ensure consistency
   */
  loadFromAPI(apiData: Partial<FilingFormState>, sheetKey?: keyof FilingFormState): void {
    // Defensive hydration guard: even when the container forgets to call
    // beginHydration() around the fetch, each per-sheet loadFromAPI() invocation
    // is intrinsically a hydration write (not user-driven). Wrap so notifyCrossTab
    // is suppressed for the duration of this call.
    return this.withHydration(() => this.loadFromAPIImpl(apiData, sheetKey));
  }

  /**
   * The sheets that currently hold UNSUBMITTED edits, and so must survive an
   * inbound backend payload (see {@link loadFromAPIImpl}).
   *
   * Two predicates, ORed, because neither alone is sufficient:
   *   - `editedSubject` — the pencil flag. Immune to the `initializingSheets`
   *     bracket, which is what makes it load-bearing here: a fresh tab mounts
   *     its visible sheet while the backend fetch is still in flight, and
   *     `isSheetModified()` returns `false` for a sheet inside that bracket, so
   *     relying on it alone would leave exactly the sheet the user is looking at
   *     unprotected.
   *   - `isSheetModified()` — the value diff. Covers a genuine difference whose
   *     flag has not been raised yet (the flag is set by hydration's ratchet and
   *     by the edit pipeline, both of which can lag a write).
   *
   * Deliberately mirrors `snapshotAllBaselines()`, the other seam that rewrites
   * baselines and has always skipped sheets flagged edited.
   */
  private collectSheetsWithUnsubmittedEdits(
    keys: Array<keyof FilingFormState>
  ): Set<keyof FilingFormState> {
    const dirty = new Set<keyof FilingFormState>();
    for (const key of keys) {
      if (this.editedSubject.value[key as keyof SheetEditedStatus] || this.isSheetModified(key)) {
        dirty.add(key);
      }
    }
    return dirty;
  }

  private loadFromAPIImpl(
    apiData: Partial<FilingFormState>,
    sheetKey?: keyof FilingFormState
  ): void {
    // Which sheets are carrying UNSUBMITTED edits right now. Computed BEFORE the
    // baseline write below, because `isSheetModified()` compares modified against
    // original — once the new baseline has landed it would be answering a
    // different question and every protected sheet would read as clean.
    const protectedSheets = this.collectSheetsWithUnsubmittedEdits(
      sheetKey && sheetKey.trim() !== ''
        ? [sheetKey]
        : (Object.keys(this.initialState) as Array<keyof FilingFormState>)
    );

    let mergedState;

    if (sheetKey && sheetKey.trim() !== '') {
      // Merge apiData under the given key with CURRENT original state
      // This preserves previously loaded sheets
      const currentOriginal = this.originalStateSubject.value;

      mergedState = {
        ...currentOriginal,
        [sheetKey]: {
          ...(currentOriginal[sheetKey] || {}),
          ...apiData
        }
      };
    } else {
      // Merge at root level with CURRENT original state
      const currentOriginal = this.originalStateSubject.value;
      mergedState = {
        ...currentOriginal,
        ...apiData
      };
    }

    // Apply default values before saving to original state
    const mergedStateWithDefaults = this.applyDefaultValuesToState(mergedState);

    // Save as original state (saveOriginalState will apply defaults again, but that's ok - idempotent)
    this.originalStateSubject.next(mergedStateWithDefaults);
    this.saveOriginalState(mergedStateWithDefaults);

    // Also update modified state to match original
    // IMPORTANT: Deep clone to prevent shared references between original and modified states
    const currentModified = this.modifiedStateSubject.value;
    let updatedModified;

    if (sheetKey && sheetKey.trim() !== '') {
      // Update only the specific sheet, preserve other sheets' modifications
      // Deep clone the sheet data to prevent shared references
      if (protectedSheets.has(sheetKey)) {
        // Unsubmitted edits win — leave the slice exactly as it is.
        updatedModified = currentModified;
      } else {
        const clonedSheetData = structuredClone(mergedStateWithDefaults[sheetKey]);
        updatedModified = {
          ...currentModified,
          [sheetKey]: clonedSheetData
        };
      }
    } else {
      // Merge all data at root level - deep clone to prevent shared references
      const clonedState = this.deepCloneState(mergedStateWithDefaults);
      // Same rule, applied per sheet: drop the protected slices from the payload
      // so the merge below cannot overwrite them.
      for (const key of protectedSheets) delete (clonedState as any)[key];
      updatedModified = {
        ...currentModified,
        ...clonedState
      };
    }

    // Apply defaults and save modified state
    const updatedModifiedWithDefaults = this.applyDefaultValuesToState(updatedModified);
    this.modifiedStateSubject.next(updatedModifiedWithDefaults);
    this.saveModifiedState(updatedModifiedWithDefaults);

    this.saveMetadata({
      lastApiLoadTime: new Date().toISOString(),
      hasApiData: true
    });
  }

  /**
   * Cancel changes and revert to original state
   * Behavior depends on network status:
   * - Online: Fetch fresh data from API (future implementation)
   * - Offline: Revert to original state from localStorage
   */
  async cancelChanges(): Promise<void> {
    if (this.isOnline) {
      // TODO: Implement API call to fetch fresh data
      // For now, revert to original state from localStorage
      this.revertToOriginalState();
    } else {
      this.revertToOriginalState();
    }

    // Cross-tab cancel/revert safeguard: tell other tabs on this conversion to
    // leave the form before the debounced originalState/modifiedState notify
    // makes them re-read an emptied original and flip to the pencil icon.
    this.requestConversionResetAcrossTabs();
  }

  /**
   * Discard all in-memory + localStorage modifications for the current conversion,
   * reverting the modified state back to the pristine original (as loaded from API).
   * Used by the can-deactivate guard when the user chooses "Continue without saving".
   */
  discardModifiedChanges(): void {
    this.revertToOriginalState();
    this.clearAllEdited();

    // Tell sibling tabs on this conversion to return to the baseline too.
    //
    // P.O. ruling (2026-08-21): siblings do NOT leave the form — they stay on
    // their sheet, their values revert to the baseline, and any cross-tab
    // Filing Information block clears because nothing reads as modified any
    // more. That is deliberately different from cancel/revert, which evicts
    // siblings to the listing via `requestConversionResetAcrossTabs()`.
    //
    // The discard scope itself is unchanged and remains WHOLE-FORM (P.O.,
    // same date): every sheet returns to baseline, including sheets a sibling
    // tab was editing. Broadcasting is what makes that visible rather than
    // silent — before this, siblings kept rendering values that no longer
    // existed anywhere.
    //
    // Sent via `notifyImmediate` (undebounced) for the same reason
    // `conversionReset` is: this is a one-shot user intent, and the debounced
    // state notifies that follow must not overtake it.
    try {
      this.crossTabSync.notifyImmediate({
        streamKey: 'conversionDiscarded',
        conversionId: this.currentConversionId,
      });
    } catch (error) {
      console.error('[CrossTabSync] conversionDiscarded notifyImmediate() threw:', error);
    }
  }

  /**
   * Receiver half of `conversionDiscarded`: return this tab to the baseline
   * because another tab discarded every unsaved change on this conversion.
   *
   * Deliberately reverts from the IN-MEMORY original rather than re-reading
   * persistence: the discarding tab typically tears its persisted state down
   * immediately afterwards, so a read here races that teardown and would often
   * come back absent. This tab's own `originalStateSubject` is the baseline the
   * discard reverted to, so it is both correct and always available.
   *
   * Publishes on `remoteUpdateApplied$` with every sheet listed as changed so
   * mounted sheets repaint through their normal adoption path — the same route
   * a sibling's edit already travels. No redirect, no navigation.
   */
  private adoptRemoteDiscard(): void {
    let changedSheets: (keyof FilingFormState)[] = [];

    this.isApplyingRemote = true;
    try {
      const previous = this.modifiedStateSubject.value;
      const baseline = this.deepCloneState(this.originalStateSubject.value);
      changedSheets = diffChangedSheets(previous as any, baseline as any) as (keyof FilingFormState)[];
      this.modifiedStateSubject.next(baseline);
      this.clearAllEdited();
    } finally {
      this.isApplyingRemote = false;
    }

    // Publish as `modifiedState`, NOT as `conversionDiscarded`.
    //
    // The transport stream key and this stream key are different things: the
    // transport key names the INTENT that crossed the tab boundary, while
    // `remoteUpdateApplied$.streamKey` names WHICH CACHE was rehydrated — and a
    // discard rehydrates the modified slice, so `modifiedState` is the honest
    // answer, not a convenient lie.
    //
    // It is also load-bearing. All 26 sheets' adoption subscriptions filter
    // `streamKey === 'modifiedState'` before repainting their form. Publishing
    // the transport key here filtered every one of them out, and the result was
    // a half-applied discard that is worth recognising if it ever recurs: the
    // sheet status reverted and the Submit/Cancel buttons reset (both read from
    // state), while the edited field values stayed on screen (they are repainted
    // only by adoption). State right, form wrong.
    this.remoteUpdateAppliedSubject.next({
      streamKey: 'modifiedState',
      sheetKey: undefined,
      changedSheets,
    });
  }

  // ---------------------------------------------------------------------------
  // Per-sheet abandonment ("last tab out OF A SHEET")
  //
  // Developer Note (revisit after launch — P.O. acknowledged 2026-08-27):
  // ---------------------------------------------------------------------------
  // The rule implemented below is: **a sheet's unsaved changes survive a tab
  // leaving the filing form only if some remaining tab is currently mounted on
  // that same conversion AND that same sheet.** If none is, the sheet reverts to
  // its baseline in every tab on the conversion and its selector icon goes back
  // to tick/cross.
  //
  // A leaving tab releases ONLY the sheet it had on screen — not every sheet it
  // had dirtied along the way. The P.O. has reviewed and accepted the oddity
  // this produces:
  //
  //     [Tab A] edits Scope of Filing
  //     [Tab B] idles on Sub-classification
  //     [Tab A] moves to Directors Report, edits it
  //     [Tab A] moves to Statement of Profit or Loss, edits it, closes the tab
  //             and confirms
  //     [Tab B] loses ONLY Statement of Profit or Loss
  //
  // Scope of Filing and Directors Report survive in Tab B even though no tab is
  // showing them. That is a consequence of releasing the mounted sheet rather
  // than the dirty set, and it is deliberate for launch: releasing everything a
  // leaving tab had touched is the strictly more destructive reading, and this
  // protocol's failure direction is already data loss. The residue is not
  // permanent — it is collected by the conversion-wide
  // `shouldDiscardAsLastTabOut` probe the next time the conversion is opened
  // with no tab holding it.
  //
  // This whole handshake is the launch-scoped answer, not the durable one. The
  // durable shape is a proper per-sheet LEASE — a tab acquires a sheet on mount,
  // renews it, and releases it on unmount, with expiry as the fallback — which
  // replaces every ad-hoc probe here (and the conversion-level `livenessPing`
  // pair) with one ownership question asked once. **We will revisit this after
  // the project officially launches.** Do not extend the current design with a
  // third probe pair; that is the signal to build the lease instead.
  //
  // Why the timer lives on the RECEIVER and not the sender: the sender is inside
  // `pagehide`, which grants no async budget whatsoever. See
  // `CrossTabSyncService.releaseSheet`.

  /**
   * Register the callback that reports which sheet this tab has ON SCREEN.
   *
   * Called by `FilingFormContainerComponent` on init and cleared on destroy and
   * on `pagehide`. That the answer comes from the CONTAINER rather than from a
   * field on this service is the load-bearing part, and it is what makes this
   * protocol immune to the two over-vouching holes the conversion-level liveness
   * probe still carries:
   *
   *   - A tab idling on the XBRL Files listing after once entering the
   *     conversion has no container, so it cannot vouch. (The conversion-level
   *     probe answers forever in that state, from `currentConversionId`, which
   *     `clearAllInMemoryFormState()` deliberately leaves behind.)
   *   - A tab past `pagehide` clears its probe BEFORE broadcasting its own
   *     release, so a tab that is going away cannot vouch for a sheet it is
   *     about to abandon.
   *
   * Both are structural here rather than a predicate that has to be kept in
   * sync, which is why the judgement was moved out of this service.
   */
  registerMountedSheetProbe(probe: () => keyof FilingFormState | null): void {
    this.mountedSheetProbe = probe;
    // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
    recordLivenessDiagnostic('sheet:probe', this.currentConversionId, { action: 'registered' });
  }

  /**
   * Withdraw the mounted-sheet probe. Idempotent, and safe to call from a
   * teardown path that may run twice (`pagehide` followed by `ngOnDestroy`).
   *
   * After this returns, this tab answers no `sheetReleased` message — which is
   * the correct reading of "nothing of mine is on screen".
   */
  clearMountedSheetProbe(): void {
    // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
    // Read the mounted sheet BEFORE dropping the probe — afterwards this tab
    // can no longer say what it was showing, which is the whole point of the
    // ordering `onPageHide` depends on.
    recordLivenessDiagnostic('sheet:probe', this.currentConversionId, {
      action: 'withdrawn',
      wasShowing: (this.getMountedSheet() as string) ?? null,
    });
    this.mountedSheetProbe = null;
  }

  /**
   * Which sheet this tab has on screen, or `null`.
   *
   * Feature-tolerant by design: the probe is registered by a component, and a
   * spec or a teardown ordering can leave it absent or throw. A probe that
   * fails must read as "I hold nothing" rather than take the tab down — the
   * cost of that reading is a sheet discarded slightly too eagerly, which
   * self-heals, whereas a throw here would break the message pump for every
   * other cross-tab stream.
   */
  private getMountedSheet(): keyof FilingFormState | null {
    if (!this.mountedSheetProbe) return null;
    try {
      return this.mountedSheetProbe();
    } catch (error) {
      console.error('[FilingFormState] mountedSheetProbe threw:', error);
      return null;
    }
  }

  /**
   * Announce that this tab is leaving the filing form and giving up the sheet
   * it had on screen, so sibling tabs can decide whether that sheet's unsaved
   * changes are now abandoned.
   *
   * Called from the container's `pagehide` (tab close / refresh / browser
   * navigation) and from `canDeactivateFilingGuard` once the user confirms
   * "Continue without saving". Both paths are the same event as far as sibling
   * tabs are concerned: the tab that was showing this sheet is gone.
   *
   * No-ops when there is nothing to release — no sheet on screen, no conversion
   * yet, or a sheet that carries no unsaved change. The dirty check is what
   * keeps the grace timer off the ordinary path: closing a clean tab costs
   * siblings nothing at all.
   *
   * Both dirtiness measures are consulted, because the sheet selector's pencil
   * is driven by both: `isSheetModified` is the value diff, `getSheetEdited` is
   * the explicit flag that survives the `initializingSheets` bracket in which
   * the value diff is suppressed. Releasing on either keeps this protocol's
   * verdict and the icon the user is looking at in agreement.
   *
   * @param explicitSheetKey the sheet being given up, when the caller has
   *   already withdrawn its probe and this service can no longer ask. The
   *   `pagehide` path MUST pass it: it deliberately clears the probe before
   *   broadcasting so that a tab past `pagehide` cannot vouch for the very
   *   sheet it is abandoning. The guard path passes nothing — its component is
   *   still mounted, so the probe is authoritative.
   */
  releaseMountedSheetAcrossTabs(explicitSheetKey?: keyof FilingFormState | null): void {
    if (this.currentConversionId === 'new') return;

    const sheetKey = explicitSheetKey ?? this.getMountedSheet();
    // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
    // `dirtySheets` is the whole point of this line: the protocol names ONE
    // sheet, so anything in this list that is not `sheetKey` is residue no tab
    // will ever release. That gap is the multi-sheet defect, stated rather
    // than inferred.
    recordLivenessDiagnostic('sheet:release-sent', this.currentConversionId, {
      sheetKey: (sheetKey as string) ?? null,
      viaExplicitKey: !!explicitSheetKey,
      sheetIsDirty: sheetKey
        ? this.isSheetModified(sheetKey) || this.getSheetEdited(sheetKey)
        : null,
      dirtySheets: this.listDirtySheetsForDiagnostic(),
    });
    if (!sheetKey) return;
    if (!this.isSheetModified(sheetKey) && !this.getSheetEdited(sheetKey)) return;

    try {
      this.crossTabSync.releaseSheet(this.currentConversionId, sheetKey as string);
    } catch (error) {
      console.error('[CrossTabSync] sheetReleased releaseSheet() threw:', error);
    }
  }

  /**
   * Release the mounted sheet AND drop this tab's own copy of it.
   *
   * The `canDeactivateFilingGuard` path only. It exists because that path is
   * the one where the releasing tab SURVIVES: it navigates to the listing
   * instead of dying, and the cross-tab self-echo filter means it never
   * receives its own `sheetReleased`. Broadcasting alone would therefore leave
   * the tab that just confirmed "Continue without saving" still holding the
   * changes it discarded — and still holding them in IndexedDB, so they would
   * come back on its next entry into the conversion.
   *
   * The tab close path must NOT do this. There the discard cannot be conditioned
   * on the vouch (nothing runs after `pagehide`), and the tab is being destroyed
   * anyway, so writing a revert that a live holder is about to contradict buys
   * nothing and risks losing that holder's work.
   *
   * Residual, and deliberate: the local discard is unconditional — it does not
   * wait to hear whether a sibling still has the sheet on screen, because the
   * guard cannot hold the navigation open for a grace window. If a sibling IS
   * holding it, that sibling keeps its values in memory and re-persists them on
   * its next write, which is the same self-healing route the conversion-level
   * probe relies on. Covered by the developer note above.
   */
  discardMountedSheetAndRelease(): void {
    const sheetKey = this.getMountedSheet();
    if (!sheetKey) return;
    if (!this.isSheetModified(sheetKey) && !this.getSheetEdited(sheetKey)) return;

    // Broadcast BEFORE the local revert. `discardSheetAsUnheld` runs under
    // `isApplyingRemote`, so it cannot emit a `modifiedState` notify of its own;
    // releasing first keeps the sibling-facing message the first thing to leave
    // this tab, in the same spirit as `requestConversionResetAcrossTabs`.
    this.releaseMountedSheetAcrossTabs(sheetKey);
    this.discardSheetAsUnheld(sheetKey);
  }

  /**
   * Receiver half of `sheetReleased`. Either vouch for the sheet or start the
   * clock on discarding it.
   *
   * Every tab on the conversion runs this independently and reaches the same
   * verdict from the same evidence, so there is no coordinator and no election.
   * A tab that vouches never schedules a discard for itself, which is why the
   * holder keeps its own in-progress edit.
   */
  private onRemoteSheetReleased(msg: CrossTabSyncMessage): void {
    // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
    // Recorded BEFORE every early return, so a release this tab ignored is
    // distinguishable from one it never received.
    recordLivenessDiagnostic('sheet:release-received', msg.conversionId, {
      sheetKey: (msg.sheetKey as string) ?? null,
      myConversionId: this.currentConversionId,
      conversionMatches: msg.conversionId === this.currentConversionId,
      hasLeftForm: this.hasLeftForm,
      mountedSheet: (this.getMountedSheet() as string) ?? null,
      dirtySheets: this.listDirtySheetsForDiagnostic(),
    });
    if (msg.conversionId !== this.currentConversionId) return;

    const sheetKey = msg.sheetKey as keyof FilingFormState | undefined;
    if (!sheetKey) return;

    // This tab has left the form and its in-memory state was gutted by
    // `clearAllInMemoryFormState()`. It holds nothing to vouch for and nothing
    // to discard — see the `hasLeftForm` docblock.
    if (this.hasLeftForm) return;

    if (this.getMountedSheet() === sheetKey) {
      try {
        this.crossTabSync.answerSheetHeld(msg.conversionId, sheetKey as string);
      } catch (error) {
        console.error('[CrossTabSync] sheetHeldPong answerSheetHeld() threw:', error);
      }
      return;
    }

    // Nothing unsaved on that sheet here, so there is nothing a discard could
    // take. Staying out of the protocol entirely also keeps a clean tab from
    // holding a pointless timer.
    if (!this.isSheetModified(sheetKey) && !this.getSheetEdited(sheetKey)) return;

    const key = FilingFormStateService.pendingSheetDiscardKey(msg.conversionId, sheetKey as string);
    const existing = this.pendingSheetDiscards.get(key);
    if (existing) clearTimeout(existing);

    // Deliberately scheduled INSIDE Angular's zone. The transport re-enters the
    // zone before emitting, so the timer is zone-tracked and the discard's
    // repaint runs change detection on its own. This differs from
    // `probeLiveSiblings`, which runs outside the zone precisely because it is
    // awaited inside the hydration suspension.
    const timer = setTimeout(() => {
      this.pendingSheetDiscards.delete(key);
      // Re-check on expiry rather than trusting the snapshot taken when the
      // timer was armed: during the grace window this tab may have navigated to
      // the released sheet, switched conversions, or left the form entirely.
      if (msg.conversionId !== this.currentConversionId) return;
      if (this.hasLeftForm) return;
      if (this.getMountedSheet() === sheetKey) return;
      this.discardSheetAsUnheld(sheetKey);
    }, CrossTabSyncService.SHEET_RELEASE_GRACE_MS);

    this.pendingSheetDiscards.set(key, timer);
  }

  /**
   * A sibling vouched for the sheet — stand down.
   *
   * With no `sheetKey` (a malformed or future message), cancels every pending
   * discard for that conversion. Erring toward NOT discarding is the safe
   * direction here: this protocol's mistake budget is unsaved user work.
   */
  private cancelPendingSheetDiscard(conversionId: string, sheetKey?: string): void {
    if (sheetKey) {
      const key = FilingFormStateService.pendingSheetDiscardKey(conversionId, sheetKey);
      const timer = this.pendingSheetDiscards.get(key);
      if (timer) {
        clearTimeout(timer);
        this.pendingSheetDiscards.delete(key);
      }
      return;
    }

    const prefix = conversionId + '|';
    for (const [key, timer] of Array.from(this.pendingSheetDiscards.entries())) {
      if (!key.startsWith(prefix)) continue;
      clearTimeout(timer);
      this.pendingSheetDiscards.delete(key);
    }
  }

  /**
   * Revert ONE sheet to its baseline because no tab is showing it any more.
   *
   * Modelled on {@link adoptRemoteDiscard} and sharing its two rules — revert
   * from the IN-MEMORY original (the releasing tab is typically gone, so a
   * persistence read races its teardown), and publish under
   * `streamKey: 'modifiedState'` because that names WHICH CACHE was rehydrated,
   * not which intent crossed the tab boundary. All 26 sheets filter their
   * adoption subscription on `'modifiedState'`; publishing the transport key
   * instead is what once produced a half-applied discard where the sheet status
   * reverted while the edited values stayed on screen.
   *
   * It differs from `adoptRemoteDiscard` in one way that matters:
   * **it persists.** That method can skip persistence because the tab that
   * discarded is about to tear the stored blob down anyway. Here the releasing
   * tab died inside `pagehide` and tore nothing down, so if this tab does not
   * write the reverted slice, IndexedDB keeps the abandoned values and the
   * pencil returns on the next open. The write is a slice write
   * (`changedKeys: [sheetKey]`), never a whole-document replace, so it cannot
   * clobber a sibling's newer save on a sheet this tab never opened.
   *
   * The whole body runs under `isApplyingRemote`, which suppresses
   * `notifyCrossTab` inside `saveModifiedState`. That is intentional and is the
   * adoption invariant: adoption never re-broadcasts. Every tab already reached
   * this verdict independently, so a rebroadcast would be pure echo.
   */
  private discardSheetAsUnheld(sheetKey: keyof FilingFormState): void {
    // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
    recordLivenessDiagnostic('sheet:discarded', this.currentConversionId, {
      sheetKey: sheetKey as string,
      dirtySheetsBefore: this.listDirtySheetsForDiagnostic(),
    });
    this.isApplyingRemote = true;
    try {
      const current = this.modifiedStateSubject.value;
      const baseline = this.deepCloneState(this.originalStateSubject.value)[sheetKey];
      const reverted: FilingFormState = {
        ...current,
        [sheetKey]: baseline ?? {},
      };
      this.modifiedStateSubject.next(reverted);
      this.saveModifiedState(reverted, [sheetKey]);
      this.clearSheetEdited(sheetKey);
    } finally {
      this.isApplyingRemote = false;
    }

    this.remoteUpdateAppliedSubject.next({
      streamKey: 'modifiedState',
      sheetKey: sheetKey as string,
      changedSheets: [sheetKey],
    });
  }

  /** Map key for {@link pendingSheetDiscards}. */
  private static pendingSheetDiscardKey(conversionId: string, sheetKey: string): string {
    return conversionId + '|' + sheetKey;
  }

  /**
   * Cancel specific sheet changes and revert to original
   */
  cancelSheetChanges(sheetKey: keyof FilingFormState): void {
    const originalData = this.originalStateSubject.value[sheetKey];

    // Deep clone the original data to prevent shared references
    let clonedOriginalData: Record<string, any> = {};
    try {
      clonedOriginalData = originalData ? structuredClone(originalData) : {};
    } catch (error) {
      console.error('Error deep cloning sheet data:', error);
      clonedOriginalData = { ...originalData };
    }

    const currentState = this.modifiedStateSubject.value;
    const revertedState = {
      ...currentState,
      [sheetKey]: clonedOriginalData
    };

    this.modifiedStateSubject.next(revertedState);
    this.saveModifiedState(revertedState);

    // Cross-tab cancel/revert safeguard: tell other tabs on this conversion to
    // leave the form before the debounced originalState/modifiedState notify
    // makes them re-read an emptied original and flip to the pencil icon.
    this.requestConversionResetAcrossTabs();
  }

  /**
   * One-shot check: returns true exactly once per JS context, on the first
   * setConversionId() call. Used to gate the refresh-discard policy so that
   * a fresh page load wipes unsaved edits while in-app conversion switches
   * preserve them. See `refreshDiscardArmed` declaration for the rationale.
   */
  /**
   * True only when BOTH hold: this is the first `setConversionId` of the JS
   * context (the existing static), AND this tab armed an unload intent for THIS
   * conversion via `armUnloadDiscardIntent()`. The second condition is what
   * scopes the discard to the tab that actually reloaded — without it, merely
   * opening a tab on a conversion a sibling is editing destroys that sibling's
   * unsubmitted work.
   *
   * Both signals are consumed one-shot.
   *
   * Consuming `refreshDiscardArmed` here even when the `sessionStorage` marker
   * does NOT match `conversionId` (the early-armed-static, non-matching-intent
   * branch below) is correct ONLY because `setConversionId()` has a single call
   * site today (`filing-form-container.component.ts`), reached exactly once per
   * page load with the real conversion id already resolved from route params.
   * If a future change adds an earlier call in the same JS context — e.g. a
   * placeholder `setConversionId('new')` fired before route params resolve —
   * that earlier call would burn `refreshDiscardArmed` against a marker that
   * cannot match, silently disabling the refresh-discard policy for the rest
   * of the tab's life with no test to catch it (nothing here asserts the
   * call-site count). Guard any such change by re-arming or re-checking here.
   */
  private consumeRefreshDiscardFlag(conversionId: string): boolean {
    if (!FilingFormStateService.refreshDiscardArmed)
      return false;

    FilingFormStateService.refreshDiscardArmed = false;

    let intent: string | null = null;
    try {
      intent = sessionStorage.getItem(FilingFormStateService.UNLOAD_INTENT_KEY);
      sessionStorage.removeItem(FilingFormStateService.UNLOAD_INTENT_KEY);
    } catch {
      return false;
    }

    return intent === conversionId;
  }

  /**
   * Record that this tab is unloading with unsaved edits, so the refresh-discard
   * policy (see `consumeRefreshDiscardFlag`) fires on the way back in for THIS
   * tab only. Called ONLY from the filing container's `onPageHide`.
   *
   * **Arming is unconditional and permanent — this method does NOT un-arm
   * itself, and must not be taught to.** It used to queue a `setTimeout(…, 0)`
   * removal on the premise that "a real unload kills the timer, so if it runs
   * the page stayed" — the compensation for `beforeunload` firing while the
   * page is still alive, before the user has answered "Reload site?".
   * Instrumented browser runs disproved the premise outright: the queued clear
   * fired identically whether the user clicked Reload or Cancel, so the marker
   * was always gone by the time the reloaded page read it and the whole
   * refresh-discard policy silently never fired. A macrotask's ordering against
   * the unload commit is a browser implementation detail, so no delay is safe.
   *
   * The stay-versus-leave question is answered at the CALL SITE instead, by
   * choosing an event that only fires when the document is genuinely going
   * away (`pagehide`). Cancelling the prompt never reaches this method at all.
   * See the container's `onPageHide` and
   * `filing-form-container-unload-arming.spec.ts`.
   */
  armUnloadDiscardIntent(): void {
    // TEMPORARY DIAGNOSTIC — remove with liveness-probe-diagnostics.ts.
    recordLivenessDiagnostic('unload:armed', this.currentConversionId, {
      hasAnySheetModified: this.hasAnySheetModified(),
      myTransportTabId:
        typeof (this.crossTabSync as any).getTabId === 'function'
          ? (this.crossTabSync as any).getTabId()
          : null,
    });
    try {
      sessionStorage.setItem(FilingFormStateService.UNLOAD_INTENT_KEY, this.currentConversionId);
    } catch {
      return; // storage unavailable (private mode) — degrade to no discard
    }
  }

  /**
   * Revert entire modified state to original state
   * IMPORTANT: Uses deep clone to prevent shared references between original and modified states
   */
  private revertToOriginalState(): void {
    const originalState = this.deepCloneState(this.originalStateSubject.value);
    this.modifiedStateSubject.next(originalState);
    this.saveModifiedState(originalState);
  }

  /**
   * Deep clone a FilingFormState to prevent shared references
   * Uses JSON parse/stringify for a complete deep copy
   */
  private deepCloneState(state: FilingFormState): FilingFormState {
    try {
      return structuredClone(state);
    } catch (error) {
      console.error('Error deep cloning state:', error);
      // Fallback to shallow copy if JSON fails
      return { ...state };
    }
  }

  /**
   * Clear specific sheet data
   */
  clearSheetData(sheetKey: keyof FilingFormState): void {
    this.setSheetData(sheetKey, {});
  }

  /**
   * Clear all form data (reset to initial state)
   * Clears both original and modified states
   */
  clearAllData(): void {
    this.originalStateSubject.next({ ...this.initialState });
    this.modifiedStateSubject.next({ ...this.initialState });
    // Reset the N8N snapshot too, so switching conversions within a session
    // can't briefly surface the previous conversion's snapshot (which would
    // produce spurious "Validated*" mismatches) before the next
    // retrieve-mbrs-data load repopulates it.
    this.snapshotStateSubject.next({});
    this.clearAllStorage();
    this.clearValidationCache();
  }

  /**
   * Export all data for submission (use modified state)
   */
  exportAllData(): FilingFormState {
    return { ...this.modifiedStateSubject.value };
  }

  /**
   * Export ALL sheets' immutable N8N-extracted baselines (the snapshot store),
   * keyed by sheet. The baseline counterpart to {@link exportAllData}; the
   * mounted components pass it to `recomputeReferencedSheets` so a cross-sheet
   * blocking equality anchors referenced operands to their extraction (global
   * rule — a sub-sheet recompute divergence is a non-blocking warning). Empty
   * for sheets whose snapshot has not been populated; callers degrade to
   * recompute-with-inheritance there.
   */
  exportAllSnapshotData(): Partial<FilingFormState> {
    return { ...this.snapshotStateSubject.value };
  }

  /**
   * Get form completion status (from modified state)
   */
  getCompletionStatus(): { [key: string]: boolean } {
    const state = this.modifiedStateSubject.value;
    return {
      filingPrerequisites: state.filingPrerequisites ? Object.keys(state.filingPrerequisites).length > 0 : false,
      filingInfo: state.filingInfo ? Object.keys(state.filingInfo).length > 0 : false,
      scopeOfFiling: state.scopeOfFiling ? Object.keys(state.scopeOfFiling).length > 0 : false,
      stockExchangeDisclosure: state.stockExchangeDisclosure ? Object.keys(state.stockExchangeDisclosure).length > 0 : false,
      directorsReport: state.directorsReport ? Object.keys(state.directorsReport).length > 0 : false,
      statementByDirectors: state.statementByDirectors ? Object.keys(state.statementByDirectors).length > 0 : false,
      directorBusinessReview: state.directorBusinessReview ? Object.keys(state.directorBusinessReview).length > 0 : false,
      auditorsReport: state.auditorsReport ? Object.keys(state.auditorsReport).length > 0 : false,
      statementOfFinancialPosition: state.statementOfFinancialPosition ? Object.keys(state.statementOfFinancialPosition).length > 0 : false,
      subClassificationOfAssetsLiabilitiesEquity: state.subClassificationOfAssetsLiabilitiesEquity ? Object.keys(state.subClassificationOfAssetsLiabilitiesEquity).length > 0 : false,
      statementOfFinancialPositionLiquidity: state.statementOfFinancialPositionLiquidity ? Object.keys(state.statementOfFinancialPositionLiquidity).length > 0 : false,
      subClassificationLiquidity: state.subClassificationLiquidity ? Object.keys(state.subClassificationLiquidity).length > 0 : false,
      statementOfProfitOrLoss: state.statementOfProfitOrLoss ? Object.keys(state.statementOfProfitOrLoss).length > 0 : false,
      analysisOfProfitOrLoss: state.analysisOfProfitOrLoss ? Object.keys(state.analysisOfProfitOrLoss).length > 0 : false,
      statementOfProfitOrLossByNature: state.statementOfProfitOrLossByNature ? Object.keys(state.statementOfProfitOrLossByNature).length > 0 : false,
      analysisOfProfitOrLossByNature: state.analysisOfProfitOrLossByNature ? Object.keys(state.analysisOfProfitOrLossByNature).length > 0 : false,
      statementOfComprehensiveIncome: state.statementOfComprehensiveIncome ? Object.keys(state.statementOfComprehensiveIncome).length > 0 : false,
      statementOfComprehensiveIncomeBeforeTax: state.statementOfComprehensiveIncomeBeforeTax ? Object.keys(state.statementOfComprehensiveIncomeBeforeTax).length > 0 : false,
      statementOfCashFlowsDirectMethod: state.statementOfCashFlowsDirectMethod ? Object.keys(state.statementOfCashFlowsDirectMethod).length > 0 : false,
      statementOfCashFlowsIndirectMethod: state.statementOfCashFlowsIndirectMethod ? Object.keys(state.statementOfCashFlowsIndirectMethod).length > 0 : false,
      statementOfChangesInEquity: state.statementOfChangesInEquity ? Object.keys(state.statementOfChangesInEquity).length > 0 : false,
      notesCorporateInformation: state.notesCorporateInformation ? Object.keys(state.notesCorporateInformation).length > 0 : false,
      notesAccountingPolicies: state.notesAccountingPolicies ? Object.keys(state.notesAccountingPolicies).length > 0 : false,
      notesListOfNotes: state.notesListOfNotes ? Object.keys(state.notesListOfNotes).length > 0 : false,
      notesIssuedCapital: state.notesIssuedCapital ? Object.keys(state.notesIssuedCapital).length > 0 : false,
      notesRelatedPartyTransactions: state.notesRelatedPartyTransactions ? Object.keys(state.notesRelatedPartyTransactions).length > 0 : false
    };
  }


  /**
   * Mark a sheet as edited (used when user modifies data)
   */
  markSheetAsEdited(sheetKey: keyof FilingFormState): void {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[Marking Sheet as Edited] Sheet Key: ", sheetKey);

    const currentEdited = this.editedSubject.value;
    if (!currentEdited[sheetKey]) {
      const updatedEdited = {
        ...currentEdited,
        [sheetKey]: true
      };
      this.editedSubject.next(updatedEdited);
    }
  }

  /**
   * Clear edited status for a specific sheet (used after validation passes)
   */
  clearSheetEdited(sheetKey: keyof FilingFormState): void {
    const currentEdited = this.editedSubject.value;
    if (currentEdited[sheetKey]) {
      const updatedEdited = {
        ...currentEdited,
        [sheetKey]: false
      };
      this.editedSubject.next(updatedEdited);
    }
  }

  /**
   * Set a sheet's "edited" pencil flag to exactly its modified-vs-original state.
   * Called by each edit-mode sheet's Tick AFTER persisting, so that committing a
   * value back to its original clears the pencil while a genuine change shows it.
   * `markSheetAsEdited` alone is insufficient — it compares against the previous
   * MODIFIED slot, not the original, so it never clears on revert. Mirrors
   * `recomputeEditedStatusFromCaches()` for one sheet.
   */
  reconcileEditedFlag(sheetKey: keyof FilingFormState): void {
    if (this.isSheetModified(sheetKey)) {
      this.markSheetAsEdited(sheetKey);
    } else {
      this.clearSheetEdited(sheetKey);
    }
  }

  /**
   * Get edited status for a specific sheet
   */
  getSheetEdited(sheetKey: keyof FilingFormState): boolean {
    return this.editedSubject.value[sheetKey];
  }

  /**
   * Get all edited statuses
   */
  getAllEditedStatus(): SheetEditedStatus {
    return { ...this.editedSubject.value };
  }

  /**
   * Start API data loading phase - skip edited marks during this phase
   * Call this before populating data from API
   */
  startAPIDataLoading(): void {
    this.isLoadingFromAPI = true;
  }

  /**
   * End API data loading phase - resume normal edited marking
   * Call this after all API data has been populated
   */
  endAPIDataLoading(): void {
    this.isLoadingFromAPI = false;
  }

  /**
   * Initial MBRS-data hydration in progress. Set synchronously by the
   * filing-form container at the start of `fetchAllMbrsData()` — BEFORE
   * the async backend call — and cleared after every sheet's `populate*`
   * write finishes. Role components await this to gate their form
   * visibility so the user never sees an empty form that subsequently
   * flashes populated (role0 / role1 / role2 specifically).
   *
   * Distinct from `isLoadingFromAPI` (which is set later, inside the
   * subscribe callback after fetch resolves) because role components
   * mount synchronously alongside the container's fetch kick-off; by the
   * time `startAPIDataLoading()` fires, role0's ngOnInit is already past
   * the point it would have flipped `isLoading = false`.
   */
  private isHydratingMbrsDataSubject = new BehaviorSubject<boolean>(false);

  /**
   * Called by the filing-form container at the start of `fetchAllMbrsData()`
   * to signal that a hydration cycle has begun. Role components waiting
   * via `waitForMbrsHydration()` will block until the matching
   * `endMbrsHydration()` fires.
   */
  beginMbrsHydration(): void {
    this.isHydratingMbrsDataSubject.next(true);
  }

  /**
   * Called by the filing-form container after all per-sheet `populate*`
   * writes have landed (success path OR error-finalize paths — must be
   * symmetric with `beginMbrsHydration()` so awaiters never deadlock).
   */
  endMbrsHydration(): void {
    this.isHydratingMbrsDataSubject.next(false);
  }

  /**
   * Await the end of an in-progress MBRS-data hydration. Resolves
   * immediately when no hydration is in flight — safe to call from a role
   * component's ngOnInit regardless of whether the container has started
   * fetching yet.
   *
   * Intended usage: a role component finishes its local setup
   * (loadSchema → buildForm → loadSavedState → wire-up), then awaits
   * this before flipping its own `isLoading` flag so the form reveals
   * only once populated data is in the state slots.
   */
  async waitForMbrsHydration(caller?: string): Promise<void> {
    if (!this.isHydratingMbrsDataSubject.value) return;

    const stall = this.armMbrsHydrationStallTripwire(caller);
    try {
      await firstValueFrom(
        this.isHydratingMbrsDataSubject.pipe(filter((loading) => !loading))
      );
    } finally {
      clearTimeout(stall);
    }
  }

  /**
   * DIAGNOSTIC (TEMPORARY — see the investigation doc before removing).
   *
   * Warns when a `waitForMbrsHydration()` await has been pending far longer
   * than a hydration cycle should ever take.
   *
   * ## Why this exists
   *
   * Every sheet's `loadSchema()` awaits `waitForMbrsHydration()` before it
   * clears its own `isLoading`. That await only settles when `endMbrsHydration()`
   * fires. If the flag is ever left set, the await never settles — and because a
   * never-settling promise is not an error, the sheet's own `try/catch` cannot
   * see it: `ngOnInit` simply never reaches `isLoading = false`. The sheet is
   * then stuck loading forever with no console error, no failed request, and
   * (before the loader was hoisted out of the `!isLoading`-gated card) no
   * spinner either — a blank page with no diagnostic signal at all.
   *
   * That is the reported intermittent stall on AOPL (role 13) and Notes –
   * Issued Capital (role 23). Static review of the begin/end pairing found it
   * symmetric on every path, so the leak is NOT a missing `endMbrsHydration()`
   * call site that can be spotted by reading the container. This tripwire
   * captures the runtime state at the moment of the stall instead of asking the
   * next person to reproduce it.
   *
   * Non-production only and side-effect free — it never resolves the await, and
   * deliberately does NOT auto-recover. Auto-recovering would paper over the
   * defect and reveal the sheet with half-hydrated data, which is worse than a
   * visible spinner. Remove this, the `caller` parameter, and the sheet-side
   * labels once the root cause is fixed.
   *
   * @see docs/development/filing-load-diagnostics.md (`STALL`)
   */
  private armMbrsHydrationStallTripwire(caller?: string): ReturnType<typeof setTimeout> {
    const startedAt = Date.now();
    return setTimeout(() => {
      if (environment.production) return;
      console.warn(
        `[mbrs-hydration-stall] waitForMbrsHydration(${caller ?? 'unlabelled caller'}) ` +
          `still pending after ${Math.round((Date.now() - startedAt) / 1000)}s. ` +
          `The sheet awaiting it cannot clear its own isLoading flag, so it is stuck ` +
          `on the loading spinner. isHydratingMbrsData=${this.isHydratingMbrsDataSubject.value}, ` +
          `conversionId=${this.currentConversionId}. ` +
          `This means beginMbrsHydration() was called without a matching endMbrsHydration(), ` +
          `or the container's whenHydrated() promise never settled so neither ` +
          `fetchAllMbrsData() nor replayPostLoadUiWiring() ever ran.`
      );
    }, MBRS_HYDRATION_STALL_WARN_MS);
  }

  /**
   * Synchronous snapshot of the MBRS-data hydration flag. Consumed by
   * container-local logic that needs to gate recomputes on the fly
   * (getEnabledSheets, validateCurrentSheet, state-subscription handlers).
   */
  isHydratingMbrsData(): boolean {
    return this.isHydratingMbrsDataSubject.value;
  }

  /**
   * Start batch mode: defer all localStorage writes until endBatchMode().
   * BehaviorSubject emissions still fire for in-memory reactivity.
   */
  startBatchMode(): void {
    this.isBatchMode = true;
  }

  /**
   * End batch mode: flush current original/modified state + metadata to localStorage.
   */
  endBatchMode(): void {
    this.isBatchMode = false;
    // Single flush of both states. This flush is part of the hydration
    // pipeline (driven by the container after fetchAllMbrsData() resolves) —
    // wrap so the two saves do not broadcast cross-tab notifications. The
    // container's beginHydration()/endHydration() bracket ALSO covers this,
    // but the local guard here makes the intent explicit and is robust to
    // callers that flush the batch outside the container's pipeline.
    this.withHydration(() => {
      this.saveOriginalState(this.originalStateSubject.value);
      this.saveModifiedState(this.modifiedStateSubject.value);
      this.saveMetadata({
        lastApiLoadTime: new Date().toISOString(),
        hasApiData: true,
      });
    });
  }

  /**
   * Check if currently loading from API
   */
  isAPIDataLoading(): boolean {
    return this.isLoadingFromAPI;
  }

  /**
   * Clear all edited statuses
   */
  clearAllEdited(): void {
    this.editedSubject.next({ ...this.initialEditedStatus });
  }

  /**
   * Rehydrate `editedSubject` from the persisted original/modified localStorage caches.
   *
   * Problem this solves:
   *   `editedSubject` is in-memory only — on page refresh it resets to all-false, so the
   *   sheet selector dropdown in `filing-form-container` loses its pencil icon even though
   *   `mbrs-filing-form-original-<conversionId>` and `mbrs-filing-form-modified-<conversionId>`
   *   still contain a diff.
   *
   * Mechanism:
   *   For every sheet key, compare the two caches via the existing `isSheetModified()`
   *   (which handles dates, default values, and auto-calculated keys). If they differ,
   *   mark the sheet as edited so `getSheetEdited()` / `edited$` reflect reality.
   *
   * Call site: `setConversionId()` after both states are loaded and migration has run.
   */
  hydrateEditedStatusFromCaches(): void {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Hydrate Edited Status from Caches] START.");

    const allSheetKeys = Object.keys(this.initialState) as Array<keyof FilingFormState>;
    for (const sheetKey of allSheetKeys) {
      if (this.isSheetModified(sheetKey)) {
        this.markSheetAsEdited(sheetKey);
      }
    }
  }

  /**
   * Two-way reconciliation of `editedSubject` against the current
   * original/modified caches — like {@link hydrateEditedStatusFromCaches} but
   * it also CLEARS the pencil for sheets that are no longer modified.
   *
   * Why a separate method instead of fixing the ratchet in place:
   *   `hydrateEditedStatusFromCaches()` is a deliberate one-way ratchet used on
   *   the initial-load path (it starts from an all-false baseline, so it only
   *   ever needs to mark). Reconciling cross-tab updates needs the opposite
   *   guarantee: when another tab REVERTS a sheet back to its original value,
   *   the pencil must disappear on this tab too — which only a clear can do.
   *
   * The "edited" flag is a pure function of (modified, original). Both of those
   * caches are rehydrated from localStorage in {@link applyRemoteChange} before
   * this runs, so recomputing the derived flag here keeps every tab's pencil in
   * lock-step with the shared caches without needing a dedicated `edited`
   * broadcast. `markSheetAsEdited` / `clearSheetEdited` each no-op when the flag
   * is already in the target state, so `edited$` emits only on a real change.
   *
   * PUBLIC because the LOCAL load path needs it too, not just `applyRemoteChange`.
   * `loadFromAPI` rewrites BOTH caches from the backend payload, which can erase
   * a diff that hydration's one-way ratchet ({@link hydrateEditedStatusFromCaches})
   * had already turned into a pencil — leaving the flag lit against caches that
   * agree. The filing container calls this once its populate bracket has settled;
   * see `filing-form-state.stale-pencil-after-reload.spec.ts`. Before that, only a
   * cross-tab broadcast could correct the reloading tab's own pencil, which is why
   * the icon cleared only when a SIBLING tab was refreshed.
   */
  /**
   * Re-read the OTHER state cache from persistence before the pencil is
   * recomputed, so the recompute never runs on a half-refreshed pair.
   *
   * Why this exists: the pencil is derived from (modified vs original), but
   * `originalState` and `modifiedState` arrive as two INDEPENDENT debounced
   * cross-tab streams. `applyRemoteChange` refreshes only the cache its own
   * message names, then calls `recomputeEditedStatusFromCaches()` across every
   * sheet — so a single logical operation that moved BOTH blobs (a commit, a
   * discard) is seen half-applied, and every sheet whose counterpart slice
   * moved reads as a pending user edit until the second notify lands ~300ms
   * later and silently corrects it. That is the intermittent phantom pencil on
   * sheets nobody touched, reproduced in the reproduction spec's
   * "DEFECT 2(a)" case.
   *
   * Scoping the recompute to `changedSheets` does NOT fix this and was
   * rejected: in the failing scenario the changed sheets ARE the affected
   * ones — their own slice genuinely moved; it is the counterpart that is
   * stale.
   *
   * Safe to call unconditionally: when the counterpart has not actually
   * changed, the value pushed is deep-equal to what is already there and every
   * downstream consumer of `originalState$` / `state$` filters with
   * `distinctUntilChanged`, so a no-op refresh reaches nobody. The absent-blob
   * (`null`) case is skipped for the same reason the two branches above skip
   * it — see the R2 regression note there.
   */
  private async refreshCounterpartCache(which: 'original' | 'modified'): Promise<void> {
    try {
      const fresh = which === 'original'
        ? await this.persistence.getOriginal(this.currentConversionId, { bypassPending: true })
        : await this.persistence.getModified(this.currentConversionId, { bypassPending: true });

      if (fresh === null) return;

      const withDefaults = this.applyDefaultValuesToState({ ...this.initialState, ...fresh });
      if (which === 'original')
        this.originalStateSubject.next(withDefaults);
      else
        this.modifiedStateSubject.next(withDefaults);
    } catch (error) {
      // A failed counterpart read must not abort the rehydrate that is already
      // in flight; the pencil simply stays as stale as it was before this fix.
      console.error('[FilingFormState] refreshCounterpartCache failed', error);
    }
  }

  recomputeEditedStatusFromCaches(): void {
    const allSheetKeys = Object.keys(this.initialState) as Array<keyof FilingFormState>;
    for (const sheetKey of allSheetKeys) {
      if (this.isSheetModified(sheetKey)) {
        this.markSheetAsEdited(sheetKey);
      } else {
        this.clearSheetEdited(sheetKey);
      }
    }
  }

  // ============================================================================
  // STATEMENT SHEET ROW STATUS MANAGEMENT
  // For sheets with Mapping Status column (Row-level validation status)
  // ============================================================================

  /**
   * Check if a sheet key is a statement sheet (has Mapping Status column)
   */
  isStatementSheet(sheetKey: keyof FilingFormState): sheetKey is keyof StatementSheetRowStatusMap {
    return sheetKey in this.initialStatementSheetRowStatus;
  }

  /**
   * Update statement sheet row status
   * @param sheetKey - The sheet key (must be a statement sheet)
   * @param status - The status containing hasFailedRows and hasEditedRows
   */
  updateStatementSheetRowStatus(
    sheetKey: keyof StatementSheetRowStatusMap,
    status: Partial<StatementSheetRowStatus>
  ): void {
    const currentStatus = this.statementSheetRowStatusSubject.value;
    const currentSheetStatus = currentStatus[sheetKey];

    const updatedStatus = {
      ...currentStatus,
      [sheetKey]: {
        ...currentSheetStatus,
        ...status,
      },
    };

    this.statementSheetRowStatusSubject.next(updatedStatus);
  }

  /**
   * Get statement sheet row status for a specific sheet
   * @param sheetKey - The sheet key (must be a statement sheet)
   * @returns The row status { hasFailedRows, hasEditedRows }
   */
  getStatementSheetRowStatus(sheetKey: keyof StatementSheetRowStatusMap): StatementSheetRowStatus {
    return { ...this.statementSheetRowStatusSubject.value[sheetKey] };
  }

  /**
   * Get all statement sheet row statuses
   */
  getAllStatementSheetRowStatus(): StatementSheetRowStatusMap {
    return { ...this.statementSheetRowStatusSubject.value };
  }

  /**
   * Clear statement sheet row status for a specific sheet
   */
  clearStatementSheetRowStatus(sheetKey: keyof StatementSheetRowStatusMap): void {
    this.updateStatementSheetRowStatus(sheetKey, { hasFailedRows: false, hasEditedRows: false });
  }

  /**
   * Clear all statement sheet row statuses
   */
  clearAllStatementSheetRowStatus(): void {
    this.statementSheetRowStatusSubject.next({ ...this.initialStatementSheetRowStatus });
  }

  // ============================================================================
  // SHEET-LEVEL STATUS MANAGEMENT
  // For dropdown status indicator (Validated/Failed/Edited + red asterisk)
  // ============================================================================

  /**
   * Update sheet-level status for a specific sheet
   * Called by each sheet component after validation to report its status
   * @param sheetKey - The sheet key
   * @param status - The status containing hasErrors and hasSuggestedChanges
   */
  updateSheetLevelStatus(
    sheetKey: keyof FilingFormState,
    status: Partial<SheetLevelStatusData>
  ): void {
    // Block late writes from surviving setTimeout callbacks in destroyed components.
    // After the background validation cycle finalizes a sheet, its status is locked
    // to prevent orphaned callbacks from flipping the icon (e.g., X → Tick).
    if (this.statusLockedSheets.has(sheetKey as string)) {
      return;
    }

    const currentStatus = this.sheetLevelStatusSubject.value;
    const currentSheetStatus = currentStatus[sheetKey];

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Update Sheet Level Status] Sheet Key: ", sheetKey);
    // console.log("[On Update Sheet Level Status] Current Sheet Status: ", currentStatus[sheetKey]);

    const mergedSheetStatus: SheetLevelStatusData = {
      ...currentSheetStatus,
      ...status,
    };

    // Schema-warning rollup. For sheets that declare `warnings` in their form
    // schema (filing prereq/info, scope of filing, stock exchange, directors /
    // statement-by-directors / auditors reports), drive `hasSuggestedChanges`
    // from the live FieldWarningService surface so the red asterisk tracks the
    // user's edits — and so the editor agrees with the headless pure validators,
    // which OR the SAME schema warnings into the submit-for-validation payload.
    //
    // We SET (not merge) the flag here because these sheets' `evaluateStatus`
    // closures only send `{ hasErrors }`; the partial merge alone could never
    // clear a previously-flagged `hasSuggestedChanges` once a warning is
    // resolved. Calc-diff monetary sheets are absent from the registry and keep
    // their extracted-sum `hasSuggestedChanges` untouched. The bag mirrors the
    // editor's `{ ...buildFilingInfoBagEntry(), ...currentFormValues }`.
    const warningControls = WARNING_CONTROLS_BY_STATE_KEY[sheetKey as string];
    if (warningControls) {
      const sheetData = this.modifiedStateSubject.value[sheetKey] ?? {};
      mergedSheetStatus.hasSuggestedChanges = sheetHasTriggeredWarning(
        warningControls,
        sheetData,
        // Pass the full modified-state map + this sheet's key so date
        // comparisons whose `compareWith.sheet` is the sheet ITSELF (scope's
        // circulation-vs-approval) or another non-filing-info sheet (scope's
        // AGM-vs-auditor, directors-vs-auditor, …) resolve — mirroring the bag
        // each editor component hand-wires. Without these, those warnings fire
        // inline but never flip the red asterisk / `hasSuggestedChanges`.
        buildWarningBag(this.getActiveFilingInfoData(), sheetData, {
          sheets: new Map(Object.entries(this.modifiedStateSubject.value)),
          sheetKey: sheetKey as string,
        })
      );
    }

    // Build the shared validation context ONCE — the single source of truth that
    // ConversionValidatorService (headless listing) also feeds the pure
    // validators. The active filing-info source is resolved via the phase signal
    // (getActiveFilingInfoData) and passed as the override so it stays
    // authoritative; the util handles the BOTH-keys aliasing the visibility-aware
    // validators depend on. Reused by the substitution below and the parity check.
    const sheetData = this.modifiedStateSubject.value[sheetKey] ?? {};
    // Per-sheet N8N baseline, threaded in so calc-diff validators surface the
    // "Validated*" (hasSuggestedChanges) asterisk by diffing recomputed totals
    // against the immutable extraction rather than the persisted slice (which
    // already equals the recompute). Omitted when empty so the validator's
    // persisted-slice fallback stays intact. Mirrors the headless runRegistry.
    const snapshot = this.getSnapshotSheetData(sheetKey);
    const extractedBaseline =
      snapshot && Object.keys(snapshot).length > 0 ? snapshot : undefined;
    // Cross-sheet N8N baselines (all sheets) so cross-sheet recompute can anchor
    // referenced operands to their extraction — the mounted counterpart to the
    // headless runRegistry's `crossSheetBaseline` (global rule: a sub-sheet
    // recompute divergence is a non-blocking warning, not a blocking cross-sheet
    // error). Mirrors `extractedBaseline` above, widened to every sheet.
    const crossSheetBaseline = new Map(
      Object.entries(this.snapshotStateSubject.value) as [
        keyof FilingFormState,
        Record<string, any>
      ][]
    );
    const validationContext = buildSheetValidationContext(
      new Map(
        Object.entries(this.modifiedStateSubject.value) as [
          keyof FilingFormState,
          Record<string, any>
        ][]
      ),
      { activeFilingInfo: this.getActiveFilingInfoData(), extractedBaseline, crossSheetBaseline }
    );
    const pureValidator = this.pureValidatorRegistry.get(sheetKey);

    // Snapshot the component's display-derived verdict BEFORE substitution so the
    // dev parity check below can still compare it against the pure verdict — even
    // for allow-listed sheets, where it now flags display-vs-published divergence
    // (red cells disagreeing with the tick) rather than a wrong published status.
    const componentResult: SheetLevelStatusData = { ...mergedSheetStatus };

    // Pure-status substitution (allow-listed sheets, ALL environments). For a
    // sheet on PURE_VERDICT_SHEETS we publish its registered pure validator's
    // BOTH flags as the single source of truth, so the editor's sheet-selector
    // tick (hasErrors) and its red "Success *" asterisk (hasSuggestedChanges)
    // can never disagree with the headless listing for the same data. The
    // component still owns its display surface (red borders / per-row badges);
    // only these two rolled-up booleans are delegated. Sheets not yet on the
    // allow-list keep the component's display-derived flags.
    //
    // Publishing `hasSuggestedChanges` here (not just `hasErrors`) is what closes
    // the asterisk-LAG bug: the calc-diff monetary sheets emit only `hasErrors`
    // from their component and are absent from the schema-warning registry, so
    // their `hasSuggestedChanges` was otherwise frozen to the one-shot load-time
    // validation cycle (`runBackgroundValidationCycle`) — an in-session edit that
    // diverged from the immutable N8N baseline only surfaced its "Success *" on
    // the NEXT reload/submit. The `validationContext` built above already threads
    // the live modified `sheetData` + per-sheet `extractedBaseline`, so the
    // recompute now reflects the current edit and matches the headless verdict.
    //
    // Pure substitution runs in ALL states, INCLUDING mid-edit. Modified state is
    // not written until Tick (the edit-mode "no commit until Tick" seam), so during
    // an edit the substituted verdict equals the LAST-SAVED verdict — freezing the
    // sheet-selector tick/✗ and "Success*" chip until the user confirms/saves
    // (P.O. requirement). The component still owns its inline display (live red
    // borders / cross-sheet messages); only these two rolled-up booleans defer.
    //
    // Fail-safe: if the pure validator throws, retain the component verdict
    // rather than break the editor.
    try {
      const pureStatus = pureStatusForAllowListedSheet(
        pureValidator,
        sheetData,
        validationContext
      );
      if (pureStatus !== undefined) {
        mergedSheetStatus.hasErrors = pureStatus.hasErrors;
        mergedSheetStatus.hasSuggestedChanges = pureStatus.hasSuggestedChanges;
      }
    } catch (err) {
      console.error(
        '[PureVerdict] pure validator threw during substitution; keeping component verdict',
        { sheetKey, error: err }
      );
    }

    const updatedStatus = {
      ...currentStatus,
      [sheetKey]: mergedSheetStatus,
    };
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Update Sheet Level Status] Updated Sheet Status: ", updatedStatus[sheetKey]);

    this.sheetLevelStatusSubject.next(updatedStatus);
    // Signal a genuine edit so the auto-persist subscription writes the cache.
    this.validationPersistRequest$.next();

    // Track that this sheet has reported its status (used by validation cycle polling)
    // Note: We intentionally do NOT auto-clear pending here. The validation cycle
    // explicitly clears pending after the sheet is fully initialized, preventing
    // intermediate statuses from flashing (e.g., cross → tick).
    this.statusReportedSheets.add(sheetKey as string);
    this.noteReportedForCompleteness(sheetKey as string);

    // Phase 2 dual-path parity check (dev/staging only). Compares the component's
    // display-derived verdict (`componentResult`, captured pre-substitution)
    // against the registered pure validator over the same inputs. For sheets not
    // yet allow-listed this is the original safety net (their published boolean IS
    // the component's). For allow-listed sheets it now surfaces the one residual
    // risk of substitution: the editor's red cells / per-row badges disagreeing
    // with the pure verdict that drives the published tick. Reuses the single
    // `validationContext` built above. No-op in production / when no validator is
    // registered.
    // Skip the dev parity check while editing: the component verdict (same-sheet,
    // live) is EXPECTED to diverge from the pure verdict (committed) during an edit,
    // so the warning would be noise.
    if (!this.isSheetEditing(sheetKey)) {
      this.parityCheck.runIfPureValidatorRegistered(
        sheetKey,
        sheetData,
        validationContext,
        componentResult,
        this.conversionPhaseSignal()
      );
    }
  }

  /**
   * Batch update sheet-level statuses for multiple sheets in a single emission.
   * Avoids N re-renders when updating N sheets individually.
   */
  batchUpdateSheetLevelStatus(
    updates: Partial<Record<keyof FilingFormState, Partial<SheetLevelStatusData>>>
  ): void {
    const currentStatus = this.sheetLevelStatusSubject.value;
    const updatedStatus = { ...currentStatus };

    for (const [sheetKey, status] of Object.entries(updates)) {
      const key = sheetKey as keyof FilingFormState;
      updatedStatus[key] = {
        ...updatedStatus[key],
        ...status,
      };
      // Register every key this batch carries as REPORTED. Without it the
      // debounced persist below (which now writes only reported sheets) would
      // drop everything the validation cycle publishes — the cycle goes
      // through here, not through the single-sheet updateSheetLevelStatus that
      // used to be the only writer of this set.
      this.statusReportedSheets.add(key as string);
      this.noteReportedForCompleteness(key as string);
    }

    this.sheetLevelStatusSubject.next(updatedStatus);
    // Signal a genuine edit so the auto-persist subscription writes the cache.
    this.validationPersistRequest$.next();
  }

  /**
   * Recompute and republish the pure-validator verdict for every
   * {@link PURE_VERDICT_SHEETS} entry from the CURRENT modified state —
   * deliberately BYPASSING the per-sheet status lock.
   *
   * Why this exists: a pure-verdict sheet's verdict can change because an
   * UPSTREAM sheet it cross-reads changed (e.g. Role 8 SFP's Assets ==
   * Equity & Liabilities balance shifts when the user edits its Role 9
   * sub-classification). The dependent sheet's own component only re-publishes
   * its status while it is MOUNTED and UNLOCKED — but the container locks every
   * sheet after `runBackgroundValidationCycle` and only unlocks the one the user
   * navigates to (`onSheetChange` → `unlockSheetStatus`). So while the user
   * edits Role 9, Role 8 is both unmounted AND locked, and its sheet-selector
   * dropdown icon froze at its load-time value (reported: stayed a green tick
   * while the mismatch was real). The headless pure validator re-inherits the
   * upstream sheet itself (`applyLinkedInheritance`), so it produces the correct
   * verdict without the dependent sheet being mounted.
   *
   * Why bypassing the lock is safe: the lock guards against STALE writes from
   * orphaned `setTimeout` callbacks in torn-down components. This recompute is
   * the opposite — a fresh, synchronous read of authoritative current state — so
   * replacing a frozen (stale) verdict with the recomputed one is strictly more
   * correct. The component-owned merge path ({@link updateSheetLevelStatus})
   * still honours the lock.
   *
   * Only the rolled-up `{ hasErrors, hasSuggestedChanges }` are republished, and
   * only for allow-listed sheets whose verdict is already delegated to the pure
   * validator; the component keeps owning its display surface. Publishes at most
   * once (batched) and only when something actually changed, so a settled edit
   * that touches no pure-verdict sheet is a no-op.
   */
  recomputePureVerdictStatuses(): void {
    const modifiedState = this.modifiedStateSubject.value;
    const currentStatus = this.sheetLevelStatusSubject.value;
    const updatedStatus: SheetLevelStatusMap = { ...currentStatus };

    // Built once and shared across sheets (each sheet only overrides
    // extractedBaseline). Mirrors updateSheetLevelStatus's context assembly so
    // the republished verdict is identical to the per-sheet path's.
    const stateMap = new Map(
      Object.entries(modifiedState) as [
        keyof FilingFormState,
        Record<string, any>
      ][]
    );
    const activeFilingInfo = this.getActiveFilingInfoData();
    // Cross-sheet N8N baselines (all sheets), built once and shared — anchors
    // cross-sheet recompute to the extraction (global rule). Mirrors
    // updateSheetLevelStatus.
    const crossSheetBaseline = new Map(
      Object.entries(this.snapshotStateSubject.value) as [
        keyof FilingFormState,
        Record<string, any>
      ][]
    );

    let changed = false;
    for (const sheetKey of PURE_VERDICT_SHEETS) {
      const pureValidator = this.pureValidatorRegistry.get(sheetKey);
      if (!pureValidator) continue;

      const sheetData = modifiedState[sheetKey] ?? {};
      const snapshot = this.getSnapshotSheetData(sheetKey);
      const extractedBaseline =
        snapshot && Object.keys(snapshot).length > 0 ? snapshot : undefined;
      const validationContext = buildSheetValidationContext(stateMap, {
        activeFilingInfo,
        extractedBaseline,
        crossSheetBaseline,
      });

      let pureStatus:
        | { hasErrors: boolean; hasSuggestedChanges: boolean }
        | undefined;
      try {
        pureStatus = pureStatusForAllowListedSheet(
          pureValidator,
          sheetData,
          validationContext
        );
      } catch (err) {
        console.error(
          '[PureVerdict] cross-sheet recompute threw; keeping prior status',
          { sheetKey, error: err }
        );
        continue;
      }
      if (pureStatus === undefined) continue;

      const prev = currentStatus[sheetKey];
      if (
        !prev ||
        prev.hasErrors !== pureStatus.hasErrors ||
        prev.hasSuggestedChanges !== pureStatus.hasSuggestedChanges
      ) {
        updatedStatus[sheetKey] = {
          ...prev,
          hasErrors: pureStatus.hasErrors,
          hasSuggestedChanges: pureStatus.hasSuggestedChanges,
        };
        changed = true;
      }
    }

    if (changed) {
      this.sheetLevelStatusSubject.next(updatedStatus);
      // Persist the refreshed statuses so a reload/submit sees the same verdict.
      this.validationPersistRequest$.next();
    }
  }

  /**
   * Get sheet-level status for a specific sheet
   * @param sheetKey - The sheet key
   * @returns The status { hasErrors, hasSuggestedChanges }
   */
  getSheetLevelStatus(sheetKey: keyof FilingFormState): SheetLevelStatusData {
    const status = this.sheetLevelStatusSubject.value[sheetKey];
    // If no status is set, default to hasErrors: true (assume invalid until validated)
    // This prevents showing green checkmark before the form component has validated
    if (!status) {
      return { hasErrors: true, hasSuggestedChanges: false };
    }
    return { ...status };
  }

  /**
   * Get all sheet-level statuses
   */
  getAllSheetLevelStatus(): SheetLevelStatusMap {
    return { ...this.sheetLevelStatusSubject.value };
  }

  /**
   * Clear sheet-level status for a specific sheet
   */
  clearSheetLevelStatus(sheetKey: keyof FilingFormState): void {
    this.updateSheetLevelStatus(sheetKey, { hasErrors: false, hasSuggestedChanges: false });
  }

  /**
   * Clear all sheet-level statuses
   * Called on initial load to ensure fresh validation state
   */
  clearAllSheetLevelStatus(): void {
    const initialStatus = { ...this.initialSheetLevelStatus };
    this.sheetLevelStatusSubject.next(initialStatus);
  }

  // ==================== Pending Validation State ====================

  /**
   * Mark a single sheet as pending validation (icon will be hidden)
   */
  markSheetValidationPending(sheetKey: string): void {
    const current = this.pendingValidationSubject.value;
    const updated = new Set(current);
    updated.add(sheetKey);
    this.pendingValidationSubject.next(updated);
  }

  /**
   * Mark sheets as pending validation (shows spinner until validation completes).
   * @param sheetKeys - Optional list of sheet keys to mark pending. If omitted, marks all sheets.
   */
  markAllSheetsValidationPending(sheetKeys?: string[]): void {
    const keys = sheetKeys ?? Object.keys(this.initialSheetLevelStatus);
    this.pendingValidationSubject.next(new Set(keys));
    // Reset status-reported tracking so we can detect fresh reports
    this.statusReportedSheets.clear();
  }

  /**
   * Mark a sheet's validation as complete (icon will now show tick/cross)
   */
  markSheetValidationComplete(sheetKey: string): void {
    const current = this.pendingValidationSubject.value;
    const updated = new Set(current);
    updated.delete(sheetKey);
    this.pendingValidationSubject.next(updated);
  }

  /**
   * Check if a sheet's validation is still pending (icon should be hidden)
   */
  isSheetValidationPending(sheetKey: string): boolean {
    return this.pendingValidationSubject.value.has(sheetKey);
  }

  /**
   * Check if a sheet has reported its validation status via updateSheetLevelStatus().
   * Used by the validation cycle to detect when a component has finished validating,
   * even for form-based components that don't call markSheetInitialized().
   */
  isSheetStatusReported(sheetKey: string): boolean {
    return this.statusReportedSheets.has(sheetKey);
  }

  /**
   * Clear all pending validation state (all sheets show their status)
   */
  clearAllPendingValidation(): void {
    this.pendingValidationSubject.next(new Set());
  }

  // ==================== Sheet Status Locking ====================
  // Prevents late writes from surviving setTimeout callbacks in destroyed
  // components after the background validation cycle has finalized a sheet.

  /**
   * Lock a sheet's status so that updateSheetLevelStatus() calls are ignored.
   * Called by the validation cycle after a sheet has been fully processed.
   */
  lockSheetStatus(sheetKey: string): void {
    this.statusLockedSheets.add(sheetKey);
  }

  /**
   * Unlock a single sheet's status. Called when the user navigates to a sheet
   * so the active component can report validation updates from user edits.
   */
  unlockSheetStatus(sheetKey: string): void {
    this.statusLockedSheets.delete(sheetKey);
  }

  /**
   * Unlock all sheet statuses. Called when starting a new validation cycle
   * or when a user action requires re-validation (e.g., form submit).
   */
  unlockAllSheetStatuses(): void {
    this.statusLockedSheets.clear();
  }

  /**
   * Check if a sheet's status is locked.
   */
  isSheetStatusLocked(sheetKey: string): boolean {
    return this.statusLockedSheets.has(sheetKey);
  }

  // ==================== Validation Status Cache ====================
  // Reads sheet-level validation statuses from localStorage on conversion
  // load so a page refresh within the same conversion can skip the full
  // background validation cycle and show tick/cross immediately. The WRITE
  // side is owned by ConversionValidationCacheService (see the auto-persist
  // subscription in the constructor and runBackgroundValidationCycle's
  // validateLoadedConversion call) — this service only reads and invalidates.

  /**
   * Prune the sheet-level status map to the sheets that are authoritative for
   * the current phase before it is persisted to the validation cache. This
   * mirrors {@link ConversionValidatorService}'s phase-aware key selection so
   * the auto-persist writer never re-introduces statuses the validator would
   * not have computed for this phase.
   *
   * The two filing-info slots are mutually exclusive across phases: Phase 1
   * writes the editable filing-info to role 0 (`filingPrerequisites`) and
   * Phase 2 writes it to role 1 (`filingInfo`). The inactive slot sits at its
   * initial `hasErrors: true` default and would otherwise be persisted as a
   * phantom error the listing/dashboard reads.
   *
   *  - Phase 1: the editor reveals only Filing Prerequisites, so persist just
   *    that key (the other 25 sheets have no data yet — see getEnabledSheets()).
   *  - Phase 2: drop `filingPrerequisites` (its role-0 slot is an empty stub
   *    in Phase 2); persist `filingInfo` + every downstream sheet.
   *  - Unknown phase: persist the full map unchanged — an unknown phase must
   *    not silently hide real errors.
   *
   * The casts mirror runRegistry()'s posture in ConversionValidatorService:
   * consumers (deriveTerminal, batchUpdateSheetLevelStatus) tolerate a partial
   * status map.
   */
  private scopeStatusesToActivePhase(
    statuses: SheetLevelStatusMap
  ): SheetLevelStatusMap {
    const phase = this.conversionPhaseSignal();
    if (phase === ConversionPhaseEnum.Phase1) {
      return {
        filingPrerequisites: statuses.filingPrerequisites,
      } as SheetLevelStatusMap;
    }
    if (phase === ConversionPhaseEnum.Phase2) {
      const { filingPrerequisites: _omitRole0, ...rest } = statuses;
      return rest as SheetLevelStatusMap;
    }
    return statuses;
  }

  /**
   * Load cached sheet-level validation statuses for the current conversion.
   *
   * Returns the cached map paired with a `complete` flag so callers can
   * distinguish a fully-validated run from a partial (interrupted) one.
   *
   * Delegates to {@link ConversionValidationCacheService}, the single owner of
   * the `mbrs-validation-cache-<id>` key format and its `CACHE_VERSION`. This
   * service used to parse the entry itself against a separate
   * `VALIDATION_CACHE_VERSION` constant; the two drifted (1 vs 3) and this
   * reader silently rejected (and deleted) every entry the cache service
   * wrote. Delegating keeps a single version constant so the drift cannot
   * recur. The owner already handles version-mismatch eviction and the
   * missing-`complete` backward-compat default.
   *
   * @returns `{ statuses, complete } | null` — null on cache miss / stale version.
   */
  loadValidationCache(): { statuses: SheetLevelStatusMap; complete: boolean } | null {
    const entry = this.validationCache.get(this.currentConversionId);
    if (!entry) return null;
    return { statuses: entry.statuses, complete: entry.complete };
  }

  /**
   * Remove a single sheet's entry from the validation cache.
   * Called after a successful form submit for that sheet (or for its cross-sheet
   * dependents). Flips the cache's `complete` flag to `false` so the next load
   * knows to resume validation for the missing entry rather than trusting the
   * cache as fully authoritative. Delegates to the cache owner.
   */
  invalidateValidationCacheForSheet(sheetKey: string): void {
    this.validationCache.invalidateSheet(
      this.currentConversionId,
      sheetKey as keyof SheetLevelStatusMap
    );
  }

  /**
   * Remove the entire validation cache for the current conversion.
   * Called when: conversion ID changes, or Role-0 is submitted (affects all sheets).
   * Delegates to the cache owner.
   */
  clearValidationCache(): void {
    this.clearFullValidationLatch();
    this.validationCache.invalidate(this.currentConversionId);
  }

  /**
   * Cancel-path teardown for the validation cache. Called from the Cancel
   * button flow (`SubmitForValidationComponent.onCancel` accept handler) when
   * the user discards changes and navigates back to the listing.
   *
   * Does two things, both required to guarantee the cache is gone after Cancel:
   *   1. Invalidates the cache now (same as {@link clearValidationCache}).
   *   2. Records the conversion id as suppressed so the debounced persist
   *      subscription drops the sheet's trailing post-cancel re-baseline write
   *      — which lands ~1s later, AFTER the navigation-away wipe, and would
   *      otherwise re-create `mbrs-validation-cache-<id>`.
   *
   * The suppression is a one-shot: {@link setConversionId} clears it on the
   * next form entry (including the same-id revisit), so persistence resumes
   * normally the moment the user re-opens any conversion. Back/Submit do NOT
   * call this — their flows are unchanged.
   *
   * @param conversionId the conversion being cancelled. Defaults to the current
   *   conversion; passed explicitly by the component to be unambiguous.
   */
  /**
   * Record that a completed validation cycle has just published an
   * authoritative, applicability-scoped cover for the current conversion.
   * Called by the filing container from `runBackgroundValidationCycle` and from
   * the full-cache-hit branch (which applies an equally complete cover).
   */
  markFullValidationPublished(coverKeys: Iterable<string>): void {
    this.completeValidationConversionId = this.currentConversionId;
    this.completeValidationCover = new Set(coverKeys);
  }

  /** Drop the latch: the persisted map can no longer claim to be complete. */
  private clearFullValidationLatch(): void {
    this.completeValidationConversionId = null;
    this.completeValidationCover.clear();
  }

  /**
   * Called for every sheet key that reports a status. A key outside the latched
   * cover means the applicable set has grown past what the cycle validated, so
   * the map is no longer a full cover and the latch must drop.
   */
  private noteReportedForCompleteness(sheetKey: string): void {
    if (this.completeValidationConversionId === null) return;
    if (this.completeValidationCover.has(sheetKey)) return;
    this.clearFullValidationLatch();
  }

  abandonValidationCache(conversionId: string = this.currentConversionId): void {
    this.suppressedValidationCacheConversionId = conversionId;
    // The entry is gone, so nothing may re-publish it as complete. Note
    // `setConversionId` clears the cancel SUPPRESSION on form re-entry; the
    // latch must not ride back in with it.
    this.clearFullValidationLatch();
    this.validationCache.invalidate(conversionId);
  }

  /**
   * Save original state via FilingFormPersistenceService (IndexedDB) (API data)
   * Uses conversionId-scoped key
   * IMPORTANT: Applies default values before saving to ensure consistency
   */
  saveOriginalState(state: FilingFormState): void {
    if (this.isBatchMode) return;
    // Detached: this tab has left the form. A trailing write from a torn-down
    // sheet must neither resurrect the wiped blob nor reach sibling tabs.
    // See the `hasLeftForm` docblock.
    if (this.hasLeftForm) return;
    if (!environment.production && this.isPersistedStateHydrating && !this.isHydrationInternalWrite && this.currentConversionId !== 'new') {
      console.warn(
        '[FilingFormState] State write during the hydration window for conversion',
        this.currentConversionId,
        '— a caller is mutating state before whenHydrated() resolved. Await whenHydrated() before wiring valueChanges.'
      );
    }
    this.persistence.putOriginal(this.currentConversionId, state);
    this.notifyCrossTab('originalState');
  }

  /**
   * Save modified state via FilingFormPersistenceService (IndexedDB) (user changes)
   * Uses conversionId-scoped key
   * IMPORTANT: Applies default values before saving to ensure consistency
   */
  private saveModifiedState(state: FilingFormState, changedKeys?: (keyof FilingFormState)[]): void {
    if (this.isBatchMode) return;
    // Detached: this tab has left the form — see saveOriginalState above and the
    // `hasLeftForm` docblock.
    if (this.hasLeftForm) return;
    if (!environment.production && this.isPersistedStateHydrating && !this.isHydrationInternalWrite && this.currentConversionId !== 'new') {
      console.warn(
        '[FilingFormState] State write during the hydration window for conversion',
        this.currentConversionId,
        '— a caller is mutating state before whenHydrated() resolved. Await whenHydrated() before wiring valueChanges.'
      );
    }
    // `changedKeys` present → persist ONLY those slices, merged against the live
    // store at drain time, so a slice another tab wrote while this one was
    // queued is preserved. Absent → whole-document replace, which is what the
    // bulk callers (an API load, a revert, a batch flush) intend.
    //
    // Why this distinction exists: a whole-document write from one tab rewrites
    // every other sheet from that tab's own in-memory view, last-write-wins. A
    // tab editing Filing Information was therefore reverting a sibling's newer
    // save on a sheet it had never opened — reproduced as real data loss, not
    // just a mislit pencil. See `FilingFormPersistenceService.putModified`.
    //
    // `changedKeys === []` means this write moved nothing, so the store write is
    // skipped entirely — but the notify still fires: a no-op persist is not a
    // no-op broadcast.
    if (!changedKeys)
      this.persistence.putModified(this.currentConversionId, state);
    else if (changedKeys.length > 0)
      this.persistence.putModified(this.currentConversionId, state, changedKeys);

    this.notifyCrossTab('modifiedState');
  }

  /**
   * Apply default values to readonly fields in filingPrerequisites
   * Ensures these fields always have their default values regardless of loaded data
   */
  /**
   * @param previousState the state this write REPLACES, when the caller has it
   *   (the user-edit seams `updateSheetData` / `setSheetData`). It exists purely
   *   so `deriveTypeOfSubmission` can tell a genuine user transition to an empty
   *   Basis from the ordinary empty-Basis state of a brand-new filing — see the
   *   doc comment there. Omitting it means "no transition known", which is the
   *   safe, non-destructive reading and the right one for hydration/API loads.
   */
  private applyDefaultValuesToState(
    state: FilingFormState,
    previousState?: FilingFormState
  ): FilingFormState {
    // Define default values for readonly fields that must always be set
    const defaultValues: Record<string, any> = {
      'ssmt-dei:StatusOfCompany': 'Private company',
      'ssmt-dei:ApplicationOfSubmission': 'Ordinary filing',
      'ssmt-dei:StatusOfCarryingOnBusinessDuringFinancialYear': 'Carrying on business activities',
      'ssmt-dei:LevelOfRoundingUsedInFinancialStatements': 'Actuals',
      // Other readonly defaults (already in schema)
      'ssmt-dei:NameAndVersionOfSoftwareUsedToGenerateXBRLFile': 'MBRS_Preparation_Tool v2.2',
      'ssmt-dei:TaxonomyVersion': 'SSMxT_2022v1.0',
      'ssmt-dei:DescriptionOfPresentationCurrency': 'Malaysian Ringgit (MYR)',
      'ssmt-dei:DescriptionOfFunctionalCurrency': 'Malaysian Ringgit (MYR)',
      'ssmt-dei:DisclosureOfFinancialStatementsAuditStatus': 'Audited',
      // NOTE: ssmt-dei:TypeOfSubmission is intentionally NOT in this static
      // dictionary. Its value is derived from
      // `ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements`
      // (see deriveTypeOfSubmission() below) and applied as a post-processing
      // step on each sheet that carries both keys. Hardcoding 'FS-MFRS' here
      // would be wrong for filings whose Basis is MPERS — the derivation
      // captures both branches explicitly.
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister': 'No',
      'ssmt:DisclosureOnWhetherComparativePeriodValuesAreRestated': 'No',
      'ssmt:DisclosureOnWhetherCompanysSharesAreTradedOnAnyOfficialStockExchange': 'Not-listed',
      'ssmt:MethodUsedForPreparingStatementOfFinancialPosition': 'Current-Noncurrent',
      'ssmt:MethodUsedForPreparingStatementOfProfitOrLoss': 'Function of expense',
      'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome': 'After tax',
      'ssmt:MethodUsedForPreparingStatementOfCashFlows': 'Indirect',
    };

    // Apply defaults to filingPrerequisites sheet
    let updatedState = { ...state };
    if (updatedState.filingPrerequisites) {
      const updatedPrerequisites = { ...updatedState.filingPrerequisites };

      // Force set all default values
      Object.entries(defaultValues).forEach(([qname, defaultValue]) => {
        updatedPrerequisites[qname] = defaultValue;
      });

      // Derived: TypeOfSubmission follows Basis-of-accounting-standards.
      this.deriveTypeOfSubmission(
        updatedPrerequisites,
        previousState?.filingPrerequisites
      );

      updatedState = { ...updatedState, filingPrerequisites: updatedPrerequisites };
    }

    // Also apply defaults to filingInfo and scopeOfFiling sheets
    // These sheets inherit the same readonly fields, so they must be consistent
    const sheetsToApplyDefaults: Array<keyof FilingFormState> = ['filingInfo', 'scopeOfFiling'];
    for (const sheetKey of sheetsToApplyDefaults) {
      const sheetData = updatedState[sheetKey];
      if (sheetData && Object.keys(sheetData).length > 0) {
        const updatedSheet = { ...sheetData };
        Object.entries(defaultValues).forEach(([qname, defaultValue]) => {
          // Only overwrite if the key exists in this sheet (don't add keys that don't belong)
          if (qname in updatedSheet) {
            updatedSheet[qname] = defaultValue;
          }
        });

        // Derived: TypeOfSubmission follows Basis-of-accounting-standards
        // (only when both keys are present on this sheet — same "don't add
        // keys that don't belong" rule the static defaults loop above
        // honours).
        this.deriveTypeOfSubmission(updatedSheet, previousState?.[sheetKey]);

        updatedState = { ...updatedState, [sheetKey]: updatedSheet };
      }
    }

    return this.applyValueNormalizersToState(updatedState);
  }

  /**
   * Apply schema-declared value normalisers to every sheet in a state blob.
   *
   * Also applies `ui.inputTransform` (strip whitespace / uppercase) — today the audit
   * firm registration number on the Auditors Report — via INPUT_TRANSFORMS_BY_QNAME.
   *
   * Leading-zero stripping for the BRN
   * (`ssmt-dei:NewCompanyRegistrationNumber`, declared via `ui.trimLeadingZeros`
   * in role-0/role-1 JSON and surfaced by TRIM_LEADING_ZERO_QNAMES).
   *
   * Runs as the last step of `applyDefaultValuesToState`, which every original-
   * and modified-state write funnels through. That placement is deliberate:
   * because both baselines pass through the same normaliser, they stay equal,
   * so `reconcileEditedFlag` cannot raise the pencil on a sheet the user never
   * touched just because the backend sent a zero-padded BRN.
   *
   * Only rewrites keys that are already present — the same "don't add keys that
   * don't belong" rule the static defaults loop above honours.
   */
  private applyValueNormalizersToState(state: FilingFormState): FilingFormState {
    if (TRIM_LEADING_ZERO_QNAMES.size === 0 && INPUT_TRANSFORMS_BY_QNAME.size === 0) return state;

    let updatedState = state;

    (Object.keys(state) as Array<keyof FilingFormState>).forEach((sheetKey) => {
      const sheetData = updatedState[sheetKey];
      if (!sheetData || typeof sheetData !== 'object' || Array.isArray(sheetData)) return;

      const sheet = sheetData as Record<string, any>;
      let updatedSheet: Record<string, any> | undefined;

      TRIM_LEADING_ZERO_QNAMES.forEach((qname) => {
        if (!(qname in sheet)) return;

        const value = sheet[qname];
        if (typeof value !== 'string') return;

        const trimmed = trimLeadingZeros(value);
        if (trimmed !== value) {
          // Copy lazily — at most 2 of ~24 sheets ever carry a trim qname,
          // so most sheets should never pay for a spread here.
          updatedSheet ??= { ...sheet };
          updatedSheet[qname] = trimmed;
        }
      });

      // `ui.inputTransform` (strip whitespace / uppercase): read the possibly-copied
      // sheet so a qname that is both trimmed and transformed composes correctly.
      INPUT_TRANSFORMS_BY_QNAME.forEach((rule, qname) => {
        const current = (updatedSheet ?? sheet)[qname];
        if (typeof current !== 'string') return;

        const transformed = applyInputTransforms(current, rule.transforms, rule.maxLength);
        if (transformed !== current) {
          updatedSheet ??= { ...sheet };
          updatedSheet[qname] = transformed;
        }
      });

      if (updatedSheet) {
        updatedState = { ...updatedState, [sheetKey]: updatedSheet };
      }
    });

    return updatedState;
  }

  /**
   * Derive `ssmt-dei:TypeOfSubmission` from
   * `ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements`
   * for a single sheet payload, mutating it in place.
   *
   * Mapping (per product spec):
   *   "Malaysian Financial Reporting Standards"           → "FS-MFRS"
   *   "Malaysian Private Entities Reporting Standards"    → "FS-MPERS"
   *
   * A user CLEARING the Basis on a sheet that owns the pair clears a
   * previously-derived FS-MFRS / FS-MPERS with it — the pair is derived in both
   * directions, so a cleared Basis cannot latch a stale, uneditable submission
   * type. "Cleared" means a genuine TRANSITION: `previousSheet` carried a Basis
   * and this write empties it. An empty Basis with no such transition is the
   * ordinary state of a brand-new (or unmapped-extraction) filing, where an
   * existing submission type must SURVIVE — clearing it unconditionally wiped a
   * legitimately loaded value on the first write of every such filing and handed
   * sheet applicability, the MFRS/MPERS schema switch and role 3's gate an empty
   * string.
   *
   * This guard once also protected `ui.defaultValue: "FS-MFRS"` on
   * `ssmt-dei:TypeOfSubmission` in role-0/role-1.form.json. That default was
   * REMOVED (2026-07-30) — the backend now returns `null` for both Basis and
   * TypeOfSubmission, and seeding FS-MFRS presented an unearned accounting
   * standard. A brand-new filing therefore now carries an EMPTY submission type
   * until Basis is picked; the mandatory error surfaces on Basis (enabled,
   * required) while the readonly TypeOfSubmission is excluded from the pure
   * validators' required set (see `deriveDisabledKeys` in
   * `validators/filing-info-common.rules.ts`). Do NOT reinstate the default.
   *
   * Any other Basis value (e.g. "Others") leaves
   * TypeOfSubmission untouched, and a non-derived submission type (KFI variants,
   * FS-EPC) is never clobbered — those flows are owned by their own components.
   *
   * Why this lives in the state-defaults pipeline rather than in a
   * component-level subscriber: the pure validators read directly from
   * `modifiedStateSubject`, so the state slot must carry the derived value
   * the moment it carries the Basis value. Otherwise we re-introduce the
   * exact form/state divergence that tripped the dual-path parity check on
   * the first round of this fix (form had the schema default, state had
   * null). Doing the derivation here keeps both views aligned across every
   * write seam — `loadFromAPI`, `setSheetData`, `updateSheetData`,
   * `hydrateCurrentConversion` — without each call site having
   * to remember.
   *
   * Only present keys are written, mirroring the "don't add keys that don't
   * belong" rule of the static defaults loop in `applyDefaultValuesToState`.
   */
  private deriveTypeOfSubmission(
    sheet: Record<string, any>,
    previousSheet?: Record<string, any>
  ): void {
    const BASIS_KEY =
      'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements';
    const TYPE_KEY = 'ssmt-dei:TypeOfSubmission';
    if (!(TYPE_KEY in sheet)) return;

    const basis = sheet[BASIS_KEY];
    if (basis === 'Malaysian Financial Reporting Standards') {
      sheet[TYPE_KEY] = 'FS-MFRS';
    } else if (basis === 'Malaysian Private Entities Reporting Standards') {
      sheet[TYPE_KEY] = 'FS-MPERS';
    } else if (BASIS_KEY in sheet && (basis === null || basis === undefined || basis === '')) {
      // Basis was CLEARED on a sheet that OWNS the pair (role 0 / role 1 both
      // carry both keys). The derived half must go with it, or the readonly —
      // therefore uneditable — TypeOfSubmission latches a value the filing no
      // longer claims while sheet applicability and the MFRS/MPERS schema switch
      // keep reading it. This is the only derivation seam role 1 has (it runs no
      // component-level Basis subscription, unlike role 0).
      //
      // Three guards keep the blast radius at exactly what this rule derived:
      //   - `BASIS_KEY in sheet` — a downstream sheet that merely INHERITS
      //     TypeOfSubmission without owning Basis is never touched.
      //   - the FS-MFRS/FS-MPERS check — a submission type owned by another flow
      //     (KFI-*, FS-EPC) is never clobbered.
      //   - a TRANSITION: the slice this write replaces must have carried a
      //     Basis. Without it, every write of a brand-new filing (Basis not yet
      //     picked) wiped a legitimately loaded value. Callers that cannot know
      //     the previous slice (hydration, loadFromAPI) pass nothing and this
      //     branch stays inert — the non-destructive reading.
      const previousBasis = previousSheet?.[BASIS_KEY];
      const isUserClearedBasis = !!previousBasis;
      if (
        isUserClearedBasis &&
        (sheet[TYPE_KEY] === 'FS-MFRS' || sheet[TYPE_KEY] === 'FS-MPERS')
      ) {
        sheet[TYPE_KEY] = '';
      }
    }
    // Any other Basis value (e.g. "Others") leaves TypeOfSubmission untouched.
  }

  /**
   * Wipes every per-conversion form-state entry across all conversionIds:
   * `mbrs-filing-form-original-*`, `mbrs-filing-form-modified-*`, and
   * `mbrs-filing-form-metadata-*`. Mirrors the prefix-sweep pattern used by
   * `ConversionValidationCacheService.clearAll()` and
   * `CurrentSheetCacheService.clearAll()`.
   *
   * Used by the listing-return navigation boundary in
   * `CurrentSheetCacheService` so re-entering any conversion starts from a
   * fresh fetch instead of resurrecting a previous session's persisted form
   * state. Logout already wipes these via `clearCustomerDataCache()` (prefix
   * match on `mbrs-`); this method is the explicit, narrower seam for the
   * non-logout boundary.
   *
   * NOTE: Only clears persisted (IndexedDB) state. The in-memory subjects /
   * signals on this singleton are reset by the sibling method
   * {@link clearAllInMemoryFormState}.
   * The listing-return hook in {@link CurrentSheetCacheService} calls both,
   * paired, because the same-id revisit path bypasses
   * {@link setConversionId}'s reload guard. Keep the two methods siblings
   * (rather than collapsing them) so callers that legitimately want only
   * the storage wipe — e.g. logout cleanup paths that already discard the
   * service instance — can opt out of the in-memory reset.
   */
  clearAllPersistedFormState(): void {
    // The per-conversion original/modified/metadata blobs now live in
    // IndexedDB via FilingFormPersistenceService. Delegate the prefix-scoped
    // wipe there; this is fire-and-forget (callers do not await).
    void this.persistence.clearAllFormState();
  }

  /**
   * In-memory companion to {@link clearAllPersistedFormState}. Resets every
   * per-conversion subject, set, and signal owned by this singleton so the
   * next conversion entry starts from a clean slate even when the
   * conversionId is unchanged (the same-id revisit path that bypasses
   * {@link setConversionId}'s `currentConversionId !== newId` reload guard).
   *
   * Used by the listing-return navigation boundary in
   * {@link CurrentSheetCacheService} so that when the user comes back from
   * the XBRL Files listing into the SAME conversion they just left, the
   * in-memory state does not survive across the boundary the way it
   * historically did. Without this reset, a stale {@link sheetLevelStatusSubject}
   * entry could feed `hasSuggestedChanges` (or any future per-sheet flag)
   * into the dual-path parity check on the next mount and throw a phantom
   * mismatch.
   *
   * Intentionally does NOT touch:
   *   - `currentConversionId` — left as-is. The next `setConversionId(sameId)`
   *     call no-ops, but that is fine: the cache-clear hook is now the
   *     authoritative reset, not the conversionId-change branch.
   *   - `isApplyingRemote` / `isHydrating` / `isBatchMode` /
   *     `isHydratingMbrsDataSubject` — transient lifecycle flags owned by
   *     the active fetch, not per-conversion state.
   *
   * Resets `conversionPhaseSignal` to `null`. Safe because the next
   * conversion entry's `fetchAllMbrsData` always re-issues
   * `setConversionPhase(response.phase)` (see the container's `fetchAllMbrsData`).
   */
  clearAllInMemoryFormState(): void {
    // Detach BEFORE the resets: everything below empties the subjects, and any
    // trailing write that merges into an emptied subject must be dropped rather
    // than persisted/broadcast. See the `hasLeftForm` docblock.
    this.hasLeftForm = true;
    // Everything below empties the subjects while `currentConversionId` stays
    // put, so the next entry into this same conversion MUST re-read rather than
    // no-op at setConversionId's reload guard. See the `needsRehydrate`
    // docblock for what went wrong while these two facts could not meet.
    this.needsRehydrate = true;
    this.originalStateSubject.next({ ...this.initialState });
    this.modifiedStateSubject.next({ ...this.initialState });
    this.sheetLevelStatusSubject.next({ ...this.initialSheetLevelStatus });
    this.statementSheetRowStatusSubject.next({ ...this.initialStatementSheetRowStatus });
    this.pendingValidationSubject.next(new Set());
    this.statusReportedSheets.clear();
    // The status map has just been reset to the pessimistic seed, so any
    // latched cover is void. See clearFullValidationLatch.
    this.clearFullValidationLatch();
    this.initializingSheets.clear();
    this.editingSheets.clear();
    this.editedAtInitStart.clear();
    this.unlockAllSheetStatuses();
    this.clearAllEdited();
    this.clearSessionLoadedFlag();
    this.conversionPhaseSignal.set(null);
  }

  /**
   * Clear all localStorage keys for current conversionId (original, modified,
   * metadata, and the current-sheet pointer).
   */
  private clearAllStorage(): void {
    void this.persistence.remove(this.currentConversionId);
  }

  /**
   * Save metadata to localStorage (API load time, etc.)
   * Uses conversionId-scoped key
   */
  private saveMetadata(metadata: any): void {
    if (this.isBatchMode) return;
    this.persistence.putMetadata(this.currentConversionId, metadata);
  }

  /**
   * Import data from external source (e.g., API)
   * Uses loadFromAPI() for dual-state architecture
   */
  importData(data: Partial<FilingFormState>): void {
    this.loadFromAPI(data);
  }



  /**
   * Conversion phase as reported by the backend's retrieve-mbrs-data response.
   * Container components set this via setConversionPhase() after a successful
   * fetch. Consumers who need "the active filing-info source" read the active
   * state key via getActiveFilingInfoKey() / getActiveFilingInfoData() below.
   *
   * Null when the backend has not yet reported a phase (initial load /
   * pre-hydration). Consumers must tolerate null — the default active source
   * is Phase 1 (filingPrerequisites).
   */
  private conversionPhaseSignal = signal<ConversionPhaseEnum | null>(null);

  /**
   * Set the current conversion phase. Called by the filing-form container
   * once the backend reports the phase on retrieve-mbrs-data. Downstream
   * reads (getTypeOfSubmission, getActiveFilingInfoData, scenario matcher,
   * parity-check context builder) consult this to decide which sheet-state
   * slot is authoritative.
   */
  setConversionPhase(phase: ConversionPhaseEnum | null): void {
    this.conversionPhaseSignal.set(phase);
  }

  /**
   * Current conversion phase (read-only). Returns null until the backend
   * reports it.
   */
  getConversionPhase(): ConversionPhaseEnum | null {
    return this.conversionPhaseSignal();
  }

  /**
   * The state-slot key that carries the authoritative filing-info values
   * for the CURRENT phase.
   *
   *  - Phase 1 → 'filingPrerequisites' (role0 is the editable source in
   *    Phase 1; role1 is not yet revealed; hydration writes only this slot).
   *  - Phase 2 → 'filingInfo' (role1 is the editable source in Phase 2;
   *    role0 is hidden and frozen; hydration writes only this slot).
   *
   * Defaults to 'filingPrerequisites' when phase is unknown (pre-hydration
   * or backend yet to report the phase).
   */
  getActiveFilingInfoKey(): 'filingPrerequisites' | 'filingInfo' {
    return this.conversionPhaseSignal() === ConversionPhaseEnum.Phase2
      ? 'filingInfo'
      : 'filingPrerequisites';
  }

  /**
   * Read the filing-info values from the currently-authoritative sheet slot.
   * Prefer this over direct `getSheetData('filingInfo')` or
   * `getSheetData('filingPrerequisites')` anywhere the caller needs
   * "whatever the user's latest filing-info is" rather than a specific slot.
   */
  getActiveFilingInfoData(): Record<string, any> {
    return this.getSheetData(this.getActiveFilingInfoKey()) ?? {};
  }

  /**
   * Build the filing-info entry for a cross-sheet `allValues` bag passed into
   * `FieldWarningService.evaluateWarnings()`.
   *
   * Schema rules reference the filing-info source by either alias name —
   * `'filingPrerequisites'` (role0 / Phase 1) or `'filingInfo'` (role1 /
   * Phase 2). The validation service translates either alias to whichever
   * state slot is authoritative for the current phase via
   * `getActiveFilingInfoKey()`. To keep that translation lossless, this
   * helper returns the active slot's data under BOTH alias keys — so the
   * resolver hits the same data regardless of which alias the schema uses
   * or which phase is active.
   *
   * Spread the result into your bag literal:
   * ```ts
   * const allValues = {
   *   ...allFormValues,
   *   ...this.stateService.buildFilingInfoBagEntry(),
   *   otherSheet: ...,
   * };
   * ```
   */
  buildFilingInfoBagEntry(): { filingPrerequisites: Record<string, any>; filingInfo: Record<string, any> } {
    const data = this.getActiveFilingInfoData();
    return { filingPrerequisites: data, filingInfo: data };
  }

  /**
   * Get Type of Submission from whichever filing-info slot is authoritative
   * for the current phase.
   * Returns the submission type (e.g., "FS-MFRS", "FS-MPERS").
   */
  getTypeOfSubmission(): string {
    return this.getActiveFilingInfoData()['ssmt-dei:TypeOfSubmission'] || '';
  }

  /**
   * Get form title with Type of Submission.
   * Format: `"Form Title (FS-MFRS)"`, or the bare `"Form Title"` when no
   * submission type is resolvable yet.
   *
   * ## Callers MUST read this from a live getter, never an ngOnInit snapshot
   *
   * The type is read through {@link getActiveFilingInfoKey}, which falls back
   * to the Phase-1 slot (`filingPrerequisites`) until the backend reports the
   * conversion phase. In a Phase-2 conversion that slot is deliberately empty
   * (hydration writes only `filingInfo`), so this returns the BARE title for
   * as long as the phase is unknown.
   *
   * That window is real and routinely hit: the filing container opens its
   * hydration mount-gate as soon as IndexedDB resolves and immediately
   * restores the persisted current-sheet pointer — both BEFORE the listing /
   * retrieve-mbrs-data responses that call {@link setConversionPhase} land.
   * On a duplicated tab or an F5 taken while sitting on a sheet, that sheet
   * therefore mounts phase-blind. Because the phase arrives without changing
   * `currentSheet`, the component is never re-created, so a value captured
   * once in `ngOnInit` stays wrong for the life of the tab — which is exactly
   * how all 24 sheets lost their "(FS-MPERS)" suffix on a duplicated tab.
   *
   * A getter re-reads on the next change-detection pass and self-heals when
   * the phase lands. The read is two property lookups and a concat, the same
   * cost profile as the sheets' `dimensionVisibility` getters.
   */
  getFormTitleWithSubmissionType(baseTitle: string): string {
    const submissionType = this.getTypeOfSubmission();
    return submissionType ? `${baseTitle} (${submissionType})` : baseTitle;
  }

  /**
   * A sheet's card header: its canonical display name plus the submission type.
   *
   * Preferred over calling {@link getFormTitleWithSubmissionType} with a
   * literal — the name comes from `SHEET_LABELS_BY_STATE_KEY`, which is derived
   * from `SHEET_DISPLAY_NAMES`, so a sheet's header, its selector entry and any
   * field-link tooltip naming it always read the same string.
   *
   * The same cold-mount rule applies: call this from a LIVE getter, never from
   * an `ngOnInit` snapshot.
   */
  getSheetTitleWithSubmissionType(sheetKey: keyof FilingFormState): string {
    return this.getFormTitleWithSubmissionType(
      SHEET_LABELS_BY_STATE_KEY[sheetKey]
    );
  }

  /**
   * Commit current modified state to original state (after successful submission)
   * This makes the modified state the new "pristine" baseline
   * Call this after a successful submission to prevent false "modified" detection
   * IMPORTANT: Applies default values before committing
   * IMPORTANT: Clears all edited statuses after commit (reset for fresh validation)
   * IMPORTANT: Clears the session-loaded flag so the next container mount re-runs
   *            fetchAllMbrsData() against the backend. Without this, navigating back
   *            into the same conversion after submit would skip the fetch (gated by
   *            isDataLoadedInCurrentSession) and show stale pre-submit data.
   */
  commitModifiedStateAsOriginal(): void {
    const currentModified = { ...this.modifiedStateSubject.value };
    // Apply defaults before committing to original
    const currentModifiedWithDefaults = this.applyDefaultValuesToState(currentModified);
    this.originalStateSubject.next(currentModifiedWithDefaults);
    this.saveOriginalState(currentModifiedWithDefaults);

    // Clear all edited statuses after commit - sheets will re-report their status
    this.clearAllEdited();

    // Force the next container mount to re-fetch backend data for this conversion.
    this.clearSessionLoadedFlag();
  }

  /**
   * Commit specific sheet's modified state to original state
   * Use after successful sheet-level submission
   * IMPORTANT: Applies default values before committing
   */
  commitSheetAsOriginal(sheetKey: keyof FilingFormState): void {
    const modifiedSheetData = this.modifiedStateSubject.value[sheetKey];
    const currentOriginal = { ...this.originalStateSubject.value };

    currentOriginal[sheetKey] = { ...modifiedSheetData };

    // Apply defaults before committing to original
    const currentOriginalWithDefaults = this.applyDefaultValuesToState(currentOriginal);
    this.originalStateSubject.next(currentOriginalWithDefaults);
    this.saveOriginalState(currentOriginalWithDefaults);

    // Force the next container mount to re-fetch backend data, mirroring
    // commitModifiedStateAsOriginal. A committed sheet means the backend now
    // owns newer data than anything cached in this JS context, so preserving
    // the in-memory copy on navigate-back would show pre-submit values.
    //
    // Defence-in-depth rather than a behaviour change today: both callers
    // (role0 / role1 onSubmitSucceeded) navigate straight to /mbrs/xbrl-files,
    // and CurrentSheetCacheService's filing-route exit boundary already flushes
    // everything — clearAllInMemoryFormState() clears this same flag — on that
    // NavigationEnd. This closes the gap for any future caller that commits a
    // sheet WITHOUT leaving the form.
    this.clearSessionLoadedFlag();
  }
}
